<script setup lang="ts">
import GuardianTerritoryField from "@/components/guardian/GuardianTerritoryField.vue";
import { CreatePosSale } from "@/api/posApi";
import PosReturnCart from "@/components/pos/PosReturnCart.vue";
import PosCreditVoucherDialog from "@/components/pos/returns/PosCreditVoucherDialog.vue";
import { usePosReturn } from "@/composables/usePosReturn";
import { refreshPacks } from "@/offline/runtime";
import { invalidateTags } from "@/services/frappe/cache";
// Catalogue reads go through the offline layer: it answers from the device copy
// when there is one (instant, and the same path online and offline) and falls
// back to the server otherwise. Same signatures as the posApi functions they
// replace.
import {
  ensurePosMirror,
  ReadPosCatalogItems,
  ReadPosItemByBarcode,
  ReadPosItemGroups,
  readPackWarehouses,
} from "@/offline/posReads";
import { findLocalItemByCode, posMirrorVersion } from "@/offline/posDataSource";
import type { CartDraft } from "@/offline/drafts";
import {
  clearDraft as clearCartDraft,
  createDraftSaver,
  loadDraft as loadCartDraft,
} from "@/offline/drafts";
import { GetCustomerDetail } from "@/api/customerApi";
import { CreateGuardian, getGuardianProfile } from "@/api/guardianApi";
import { GetCustomerBalance } from "@/api/customerBalanceApi";
import {
  FindSettleableInvoices,
  IsPosMultiSettlementAvailable,
  IsPosSettlementAvailable,
  SettleOpenInvoiceAtPos,
  SettleOpenInvoicesAtPos,
} from "@/api/posSettlementApi";
import { ReadSalesInvoiceDetail } from "@/api/salesInvoiceApi";
import AddSalesInvoicePaymentDialog from "@/components/accounting/AddSalesInvoicePaymentDialog.vue";
import CreatePaymentEntryDialog from "@/components/accounting/CreatePaymentEntryDialog.vue";
import PosCameraScanDialog from "@/components/pos/PosCameraScanDialog.vue";
import PosInvoiceContextMenu from "@/components/pos/PosInvoiceContextMenu.vue";
import AttachInvoiceToPartnerDialog from "@/components/delivery-partner/AttachInvoiceToPartnerDialog.vue";
import PosCashierWarning from "@/components/pos/PosCashierWarning.vue";
import PosCatalogToolbar from "@/components/pos/PosCatalogToolbar.vue";
import PosCategoryBar from "@/components/pos/PosCategoryBar.vue";
import PosColumnResizer from "@/components/pos/PosColumnResizer.vue";
import PosContextCards from "@/components/pos/PosContextCards.vue";
import PosCustomerSection from "@/components/pos/PosCustomerSection.vue";
import PosDetailsColumn from "@/components/pos/PosDetailsColumn.vue";
import PosExpenseDialog from "@/components/pos/PosExpenseDialog.vue";
import PosFavouritesStrip from "@/components/pos/PosFavouritesStrip.vue";
import PosHeader from "@/components/pos/PosHeader.vue";
import PosSaleTabs from "@/components/pos/PosSaleTabs.vue";
import PosKeypad from "@/components/pos/PosKeypad.vue";
import PosDetailTabs from "@/components/pos/PosDetailTabs.vue";
import PosItemSearchOverlay from "@/components/pos/PosItemSearchOverlay.vue";
import PosCheckoutPanel from "@/components/pos/PosCheckoutPanel.vue";
import { clampDiscountValue, discountValueCeiling } from "@/utils/posDiscount";
import { onBeforeRouteLeave } from "vue-router";
import PosInvoicePeekPanel from "@/components/pos/PosInvoicePeekPanel.vue";
import PosOpenInvoiceBar from "@/components/pos/PosOpenInvoiceBar.vue";
import PosSidePanel from "@/components/pos/PosSidePanel.vue";
import DriverOrderDetailPanel from "@/components/driver/DriverOrderDetailPanel.vue";
import DriverHandoverDialog from "@/components/driver/DriverHandoverDialog.vue";
import DriverReturnUnsoldDialog from "@/components/driver/DriverReturnUnsoldDialog.vue";
import DriverSettlementPanel from "@/components/driver/DriverSettlementPanel.vue";
import { usePosDriverAccess } from "@/composables/usePosDriverAccess";
import RecordDriverSettlementDialog from "@/components/driver/settlement/RecordDriverSettlementDialog.vue";
import { IsDriverSettlementAvailable } from "@/api/driverSettlementApi";
import { matchDriverCode } from "@/constants/barcode";
import PosOrderSummary from "@/components/pos/PosOrderSummary.vue";
import PosPaymentSection from "@/components/pos/PosPaymentSection.vue";
import PosProductGrid from "@/components/pos/PosProductGrid.vue";
import PosQuickActions from "@/components/pos/PosQuickActions.vue";
import PosPetTransport from "@/components/pos/PosPetTransport.vue";
import PosPartnerOrderBar from "@/components/pos/PosPartnerOrderBar.vue";
import PosPartnerPickerPanel from "@/components/pos/PosPartnerPickerPanel.vue";
import PosGamePrizeSection from "@/components/pos/PosGamePrizeSection.vue";
import PosGamePrizeListPanel from "@/components/pos/PosGamePrizeListPanel.vue";
import { usePosGamePrize } from "@/composables/usePosGamePrize";
import { GetActiveDeliveryPartners } from "@/api/deliveryPartnerApi";
import PosTransporterBar from "@/components/pos/transporter/PosTransporterBar.vue";
import type { TransportTransporterOption } from "@/type/petTransport";
import {
  buildTransportJob, transportLines, transporterShare, transporterSplit, type TransporterCharge,
} from "@/utils/transporterSale";
import { useDisplay } from "vuetify";
import { useBarcodeWedge } from "@/composables/useBarcodeWedge";
import { usePosInvoiceMenu } from "@/composables/usePosInvoiceMenu";
import { usePagination } from "@/composables/usePagination";
import { writeClipboard } from "@/utils/clipboard";
import { pickLocalizedName } from "@/utils/localizedName";
import { usePosColumnLayout } from "@/composables/usePosColumnLayout";
import { usePosTheme, type PosThemeId } from "@/composables/usePosTheme";
import { createReusableTemplate } from "@vueuse/core";
import { usePosCounterLayout, type PosCounterWidgetId } from "@/composables/usePosCounterLayout";
import PosLayoutZone from "@/components/pos/PosLayoutZone.vue";
import PosLayoutDesignerBar from "@/components/pos/PosLayoutDesignerBar.vue";
import { canDoctype, canPage } from "@/helper/permissionHelper";
import { PAGE_KEYS } from "@/config/moduleRegistry/keys";
import { resolveFrappeFileUrl } from "@/services/frappe/files";
import { printPosReceipt } from "@/services/posReceiptPrinter";
import { useOrderLabel } from "@/composables/useOrderLabel";
import { newIdempotencyKey } from "@/services/posSaleEnvelope";
import { useCashierStore } from "@/stores/cashier.store";
import useToastify from "@/stores/toastify.store";
import { signalScan } from "@/utils/scanFeedback";
import type { PosCatalogView } from "@/components/pos/PosCatalogToolbar.vue";
import type { PosScannerState } from "@/components/pos/PosContextCards.vue";
import type { FrappeDoctypeLookupOption } from "@/api/frappeLookupApi";
import type { CustomerBalance } from "@/type/customerBalance";
import type { PaymentEntryType } from "@/type/paymentEntry";
import type { SalesInvoiceDetail, SalesInvoiceListItem } from "@/type/salesInvoice";
import type { PosCartItem, PosCatalogItem, PosDiscountType, PosPaymentStatus, PosReceiptPayload, PosSaleSlot, PosSaleSlotForm, PosSaleTabView } from "@/type/pos";
import { isPosExpectedRefusal, posSaleSlotHasWork } from "@/type/pos";
import usePosSalesStore from "@/stores/posSales.store";
import {
  billsAppCustomer,
  partnerCommissionOn,
  partnerCommissionType,
  type DeliveryPartner,
} from "@/type/deliveryPartner";

/**
 * Point of Sale — a fixed, single-screen cashier terminal.
 *
 * ⚠️ THE PAGE ITSELF MUST NEVER SCROLL. Everything the cashier needs to act on —
 * cashier/warehouse context, scanner state, totals, payment and the Process
 * Transaction button — stays on screen at 1366x768. That is why this route opts into
 * `layout-content-height-fixed` and why `pos-terminal-shell` is pinned on `<body>`:
 * see the unscoped style block at the end of this file. The scrolling regions are
 * named per layout below, and NOTHING else may scroll.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  TWO LAYOUTS, ONE PAGE. `usePosTheme` decides; `classic` is always the default.
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ ONLY THE `__body` REGION IS BRANCHED. The head, the side panel, every dialog,
 * the scan chip and the whole of this script are SHARED. Extracting two shell
 * components was considered and rejected: this page reaches into its body children
 * with template refs it uses for logic (`toolbarRef.focusSearch()`, and `bodyRef` /
 * `checkoutRef` / `orderSummaryEl` handed to `usePosColumnLayout` as raw DOM refs),
 * and that composable feeds `PosHeader` in the SHARED head, so it cannot move into a
 * shell either. The extraction would buy re-emit boilerplate and nothing else.
 *
 * ⚠️ `classic` ADDS NO CLASS AT ALL, so its rendered markup is what it always was.
 * Every Counter rule keys off `.pos-terminal--counter`.
 *
 *   CLASSIC — a three-panel resizable grid, Catalog | Details | Cart, with a drag
 *   handle on each seam. Widths are the cashier's and persist per browser; see
 *   `usePosColumnLayout`. The catalogue is the only flexible track, so neither
 *   handle can push a fixed column past its own clamp.
 *   Scrolls: the product grid, the summary line list, the details card stack, and
 *   the customer/payment forms.
 *
 *   COUNTER — built for staff who cannot read the small layout. The same five-track
 *   grid and handles as Classic, but every column is a `PosLayoutZone` of movable
 *   widgets (search trigger, favourites, quick actions, customer, detail tabs, order
 *   summary) arranged by the cashier — see `usePosCounterLayout`. By default the
 *   customer card heads the middle column. There is no product grid —
 *   `PosItemSearchOverlay` and the barcode wedge replace it. Customer (again),
 *   payment and Process live in `PosCheckoutPanel`, a sixth `sidePanel` kind opened
 *   by PAY and F9.
 *   All type is scaled by `--pos-font-scale` (1.18).
 *   Scrolls: the detail band, the summary line list, and the checkout panel.
 *
 * ⚠️ RAISING `--pos-font-scale` MOVES THE COLUMN FLOORS. `POS_COLUMN_LIMITS` is a px
 * budget justified against literal grid tracks at 1x, and it is a frozen JS constant
 * that cannot react to a CSS variable. Counter gets away with 1.18 only because it
 * renders no resizer and does not use the `clamp()`ed body class, so those floors are
 * unreachable there. Never scale Classic.
 *
 * ⚠️ SCANNING IS GLOBAL AND NEEDS NO FOCUSED FIELD, in both layouts.
 * `useBarcodeWedge` listens on the document; a resolved code goes straight into the
 * existing `addItemToCart` with no dialog and no confirmation. The header search box
 * is the MANUAL fallback only. `resolveScannedBarcode` short-circuits on the cart and
 * then on a session `Map` before it will ask the backend.
 *
 * ⚠️ MORE THAN ONE SALE CAN BE OPEN, but only ONE IS LIVE. The others are frozen
 * snapshots in `saleSlots`; switching swaps this page's own refs. That is what keeps
 * there being exactly one barcode wedge, one keydown handler and one cart — two
 * `/pos/sell` workspace tabs would give every scan to both. See the phase-4 entry in
 * `docs/AGENT.md`. Parked sales are in memory only and do not survive a reload.
 *
 * State lives here and only here. The `components/pos/*` children are
 * presentational — props in, events out.
 */

definePage({
  meta: {
    // `layout-footer-hidden` is the template's own switch (`_placeholders.scss`).
    // A counter terminal has no use for the site footer, and on a 768px-tall
    // screen its ~56px is a whole row of products.
    layoutWrapperClasses: "layout-content-height-fixed layout-footer-hidden",
  },
});

const { t, te, locale } = useI18n();
const router = useRouter();
const route = useRoute();
const display = useDisplay();
const { isMobileShell } = useMobileShell();
const toast = useToastify();
const cashierStore = useCashierStore();

const catalogLoading = ref(false);
const checkoutLoading = ref(false);
const itemGroupsLoading = ref(false);
const searchQuery = ref("");
const manualCode = ref("");
const catalogSort = ref("item_name asc");
const catalogView = ref<PosCatalogView>("grid");
const selectedItemGroup = ref<string | null>(null);
const shellRef = ref<HTMLElement | null>(null);

/**
 * The column grid itself — deliberately NOT `shellRef`.
 *
 * `shellRef` is the outer terminal and carries the 8px frame padding, so measuring it
 * would overstate the space available to the columns by 16px on every calculation.
 * It also has a second job (the `attach` target for four dialogs) that must not move.
 */
const bodyRef = ref<HTMLElement | null>(null);

/** The cart column and its order summary — the summary seam's height budget and live size. */
const checkoutRef = ref<HTMLElement | null>(null);
const orderSummaryRef = ref<InstanceType<typeof PosOrderSummary> | null>(null);
const orderSummaryEl = computed<HTMLElement | null>(() => (orderSummaryRef.value?.$el as HTMLElement | undefined) ?? null);
const customerOption = ref<FrappeDoctypeLookupOption | null>(null);
const customerDialogOpen = ref(false);
const catalog = ref<PosCatalogItem[]>([]);
const catalogTotal = ref(0);
const catalogItemGroups = ref<string[]>([]);
const cart = ref<PosCartItem[]>([]);

/**
 * The live cart, written to the device as it is typed.
 *
 * `posSales.store` keeps carts in memory on purpose, and a reload still loses them —
 * its own header says so and points at the `beforeunload` warning as the mitigation.
 * A warning does not survive a power cut, which is the case offline mode exists for,
 * so the cart is also mirrored to IndexedDB on a 400ms debounce and OFFERED back on
 * the next open. Offered, not restored: a cashier walking up to a till must not find
 * someone's half-finished sale already rung up.
 */
const cartDraftSaver = createDraftSaver<PosCartItem>("pos-sell");
const recoverableDraft = ref<CartDraft<PosCartItem> | null>(null);
const recoveringDraft = ref(false);
const lastReceipt = ref<PosReceiptPayload | null>(null);
const lastInvoiceId = ref<string | null>(null);
const isFullscreen = ref(false);
const customerCreateLoading = ref(false);
const customerPreselectLoading = ref(false);
const paymentEdited = ref(false);
const catalogPagination = usePagination({ initialPageSize: 24, pageSizeOptions: [24, 48, 96] });

// --- Customer account + open-invoice settlement --------------------------
const customerBalance = ref<CustomerBalance | null>(null);
const balanceLoading = ref(false);
const settleableInvoices = ref<SalesInvoiceListItem[]>([]);
const settleableLoading = ref(false);
const canSettleInvoices = ref(false);
/** Is the MERGED endpoint deployed? Probed apart — the two ship independently. */
const canSettleAllInvoices = ref(false);
/**
 * Every draft being settled by this sale — not one.
 *
 * ⚠️ A customer with three clinic drafts pays ONCE at the counter, so all three
 * are linked together and every downstream figure is built on the merged set. It
 * is an array rather than a nullable object precisely so no code path can quietly
 * settle "the first one" and leave the rest open, which is what happened before.
 */
const linkedInvoices = ref<SalesInvoiceDetail[]>([]);
const linkLoading = ref(false);

// Money in / out for the selected customer, with no sale attached.
const paymentDialogOpen = ref(false);
const paymentDialogType = ref<PaymentEntryType>("Receive");

/**
 * Right-click on an invoice row, and the payment it can open.
 *
 * ⚠️ SEPARATE FROM `paymentDialogOpen` ABOVE, DELIBERATELY. That one is customer-scoped
 * money on account; this one is a receipt against ONE named invoice. They differ in what
 * they post, who may use them, and what they refresh — collapsing them would reintroduce
 * the unreferenced-entry bug documented at the dialog mount below.
 */
const invoiceMenu = usePosInvoiceMenu();
const invoicePaymentOpen = ref(false);
const invoicePaymentTarget = ref<SalesInvoiceListItem | null>(null);
/** "Attach to delivery partner", from the same right-click menu. */
const attachPartnerOpen = ref(false);
const attachPartnerTarget = ref<SalesInvoiceListItem | null>(null);
const expenseDialogOpen = ref(false);
const cameraDialogOpen = ref(false);

/** Held across retries of the same sale so a repeat cannot charge twice. */
const checkoutIdempotencyKey = ref<string | null>(null);
const returnCredit = ref<{ voucher: string; amount: number } | null>(null);
const creditDialogOpen = ref(false);
const returnCartRef = ref<InstanceType<typeof PosReturnCart> | null>(null);
/**
 * RETURN MODE OF THIS CART (F8). The return being built lives here, in the page, so the
 * till knows whether returns are offered and whether a posting is unresolved even while
 * the cart is in sale mode. The sale cart underneath is never touched by it.
 *
 * ⚠️ A return posts through its own priced, durable request (`usePosReturn`), never
 * through `checkout()`. While return mode is on, ordinary checkout is blocked.
 */
const posReturn = reactive(usePosReturn({
  profile: computed(() => cashierStore.activeProfileName || ""),
  paymentModes: computed(() => paymentModeItems.value.map(item => item.value)),
  onCompleted: () => { void handleReturnCompleted(); },
  onRecoveredSale: value => handleRecoveredCreditSale(value),
}));
const returnOperationPending = computed(() => posReturn.pending);
const returnWorkspaceEditing = computed(() => posReturn.active || posReturn.busy || creditDialogOpen.value);
const returnsAvailable = computed(() => Boolean(posReturn.capabilities?.returns));
/** In v1 a voucher pays only an ordinary paid sale: no linked invoices, partners, Due or prizes. */
const returnCreditAllowed = computed(() => !isSettlingInvoices.value && !isPartnerOrder.value && !isDuePayment.value && !form.prize);
const creditOffered = computed(() => Boolean(posReturn.capabilities?.credit_sale && returnCreditAllowed.value && customerOption.value?.id && !posReturn.pending));
const petTransportOpen = ref(false);
const petTransportAvailable = ref(false);
const petTransportCount = ref(0);
const petTransportPanel = ref<InstanceType<typeof PosPetTransport> | null>(null);

/**
 * A refusal the cashier can act on — out of stock, or a till that forbids part
 * payment. Kept on screen rather than toasted, because it names what to change
 * and a toast would be gone before they had read it.
 */
const checkoutBlockReason = ref<string | null>(null);

/**
 * Monotonic token for customer-scoped lookups.
 *
 * ⚠️ A cashier picks customers faster than these requests resolve. Without this,
 * a slow reply for the PREVIOUS customer paints over the current one's balance —
 * and a stale "owes nothing" is exactly the number that gets goods handed over on
 * credit. Every response checks the token it was issued under before writing.
 */
let customerRequestToken = 0;

const headerRef = ref<InstanceType<typeof PosHeader> | null>(null);
const toolbarRef = ref<InstanceType<typeof PosCatalogToolbar> | null>(null);
const customerSectionRef = ref<InstanceType<typeof PosCustomerSection> | null>(null);
const paymentSectionRef = ref<InstanceType<typeof PosPaymentSection> | null>(null);

/** How long the scan chip stays up, matching the stocktaking surface. */
const SCAN_FEEDBACK_MS = 2600;
const SCAN_HIGHLIGHT_MS = 1400;
/** A queue longer than this means the gun is running away from the backend. */
const SCAN_QUEUE_LIMIT = 12;
const TERMINAL_BODY_CLASS = "pos-terminal-shell";

const scannerState = ref<PosScannerState>("ready");
const scanFeedback = ref<{ tone: "ok" | "duplicate" | "error"; text: string } | null>(null);
const highlightedLineId = ref<string | null>(null);

const scanQueue: string[] = [];
let scanDraining = false;
let scanFeedbackTimer: ReturnType<typeof setTimeout> | null = null;
let scanHighlightTimer: ReturnType<typeof setTimeout> | null = null;
let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

function getToday() {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

/** The till clock as `HH:MM`, stamped onto the receipt built locally at checkout. */
function getCurrentTime() {
  const date = new Date();

  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

const form = reactive<PosSaleSlotForm>({
  postingDate: getToday(),
  paymentStatus: "Paid" as PosPaymentStatus,
  paymentMode: "",
  paidAmount: 0,
  discountType: "Fixed" as PosDiscountType,
  discountValue: 0,
  remarks: "",
  /** The partner's own order number. Required while a partner is attached. */
  partnerOrderRef: "",
  /** The end customer's name as the app gave it. Optional, never a Customer record. */
  partnerCustomerName: "",
  /** A kiosk game prize on this sale — see `usePosGamePrize`. */
  prize: null,
});

// --- Delivery partner mode ---------------------------------------------------
//
// ⚠️ A PARTNER ORDER IS A `Due` SALE AGAINST THE PARTNER'S OWN CUSTOMER. The app
// already took the customer's money, so nothing is collected at this counter and
// the till must not expect any. Because the cashier's expected cash is derived
// from the till's cash-account ledger, a Due sale is invisible to it automatically
// — which is the whole reason this is modelled as Due rather than as a payment
// mode. Do not "improve" this into a Paid sale with a partner payment method: that
// puts the money back in the drawer the cashier has to hand over.

const activePartner = ref<DeliveryPartner | null>(null);
const partnerOptions = ref<DeliveryPartner[]>([]);
const partnersLoading = ref(false);
const partnersFailed = ref(false);

const isPartnerOrder = computed(() => activePartner.value !== null);

/**
 * Is the selected customer the POS Profile's walk-in default?
 *
 * ⚠️ AN INSIDE PARTNER CANNOT SELL ONTO IT. The server refuses
 * (`PARTNER_INSIDE_NEEDS_REAL_CUSTOMER`) because the partner's order has to sit on the
 * account of the person who actually ordered — and the till reseeds the walk-in on
 * every cleared sale, so it is almost always what is selected when a partner is tapped.
 */
const isWalkInCustomer = computed(() => {
  const walkIn = String(cashierStore.defaultCustomer ?? "").trim();

  return Boolean(walkIn) && customerOption.value?.id === walkIn;
});

/** The customer a pet pickup can be booked for: never the shared walk-in account. */
const petTransportCustomer = computed(() =>
  customerOption.value?.id && !isWalkInCustomer.value
    ? { id: customerOption.value.id, label: String(customerOption.value.label || customerOption.value.id) }
    : null);

/** An inside partner is attached but the sale is still on the walk-in default. */
const partnerNeedsRealCustomer = computed(() =>
  billsAppCustomer(activePartner.value) && isWalkInCustomer.value);

// --- The transporter (Transporter sheet) ------------------------------------------
//
// The clinic's own transporter is a TRANSPORTER — a record of his own, not a delivery
// partner. «Deliver this sale» puts his fare on the cart as a line marked `transport`, locks
// the sale to Due (he collects it at the door into his cash box), and the sale is sent with
// `transport_job`, so the server books his delivery job in the same transaction — on an
// ordinary cart and on a settled clinic draft alike. See `utils/transporterSale.ts`.

const saleTransporter = ref<TransportTransporterOption | null>(null);

/** Where the goods go and who to call; and the pet trip it rides with, if any. */
const transportDelivery = ref<{ address: string; phone: string; rideAlongTask: string | null; ridingWith: string | null } | null>(null);

/** The F7 picker is for the delivery apps only; the clinic's transporter has his own sheet. */
const pickerPartnerOptions = computed(() => partnerOptions.value.filter(partner => !billsAppCustomer(partner)));

/** The attached delivery-app partner, for its bar. */
const barPartner = computed(() => activePartner.value);

const saleTransportShare = computed(() => transporterShare(cart.value));
const hasTransportLine = computed(() => transportLines(cart.value).length > 0);

/** What the sheet needs to know about this sale to say which jobs are open. */
const transporterSaleContext = computed(() => ({
  realCustomer: Boolean(petTransportCustomer.value),
  hasSaleWork: cart.value.some(line => !line.transport) || linkedInvoices.value.length > 0,
  linkedInvoiceCount: linkedInvoices.value.length,
}));

/**
 * "Fast customer" creates a Guardian, not a bare Customer — the same record the
 * coordinator's "Add guardian" makes. The backend links a Customer to every Guardian
 * (`customer_id`) and the sale is invoiced to that linked customer.
 */
const quickCustomerForm = reactive({
  fullName: "",
  phone: "",
  addressLine1: "",
  territory: "",
});

/** Same phone rule as the coordinator's guardian dialog. */
const GUARDIAN_PHONE_LENGTH = 11;

function guardianPhoneRule(value: unknown): true | string {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (!digits)
    return t("alkokh.validations.phoneNumber.required");

  return digits.length === GUARDIAN_PHONE_LENGTH
    || t("alkokh.validations.phoneExactLength", { length: GUARDIAN_PHONE_LENGTH });
}

function onQuickCustomerPhoneUpdate(value: string) {
  quickCustomerForm.phone = String(value ?? "")
    .replace(/\D/g, "")
    .slice(0, GUARDIAN_PHONE_LENGTH);
}

const numberFormatter = computed(() =>
  new Intl.NumberFormat(locale.value === "ar" ? "ar-IQ-u-nu-latn" : "en-US", {
    maximumFractionDigits: 2,
  }),
);

function formatAmount(value: number | null | undefined) {
  return numberFormatter.value.format(Number(value ?? 0));
}

const orderLabel = useOrderLabel();

const activeCashierLabel = computed(() => cashierStore.activeProfileName || "");
/**
 * The warehouse the stock on screen is actually counted from.
 *
 * ⚠️ THIS USED TO SHOW `cashierStore.warehouse`, WHICH IS NOT WHERE THE NUMBERS
 * COME FROM. The pack resolves the effective warehouse — `Branch.custom_wharehouse`
 * beats `POS Profile.warehouse` — and reads `Bin` from that one. On the till where
 * the two disagree the header named a warehouse the figures had nothing to do
 * with, so "why is this item out of stock after I transferred it" could not be
 * answered by looking at the screen. Both are shown when they differ.
 */
const packWarehouse = ref<string | null>(null);
const packProfileWarehouse = ref<string | null>(null);

async function refreshWarehouseLabel() {
  const resolved = await readPackWarehouses();

  packWarehouse.value = resolved?.warehouse ?? null;
  packProfileWarehouse.value = resolved?.profileWarehouse ?? null;
}

const activeWarehouseLabel = computed(() => {
  const reading = packWarehouse.value || cashierStore.warehouse;

  if (!reading)
    return t("alkokh.global.notSet");

  const profile = packProfileWarehouse.value || cashierStore.warehouse;

  // Naming only one of them is what hid the mismatch in the first place.
  return profile && profile !== reading ? `${reading} (${profile})` : reading;
});
const isDuePayment = computed(() => form.paymentStatus === "Due");
const activeModeLabel = computed(() =>
  isDuePayment.value
    ? t("alkokh.pos.paymentStatus.due")
    : form.paymentMode || cashierStore.preferredModeOfPayment || t("alkokh.global.notSet"),
);
const hasCashierAccess = computed(() => cashierStore.canUseActiveProfile && Boolean(cashierStore.activeProfileName));

/**
 * ⚠️ Running a till and administering tills are different rights.
 *
 * POS is `SALES_ACCOUNTING_ROLES`; the Cashiers page is `ACCOUNTING_ROLES`, which
 * excludes Sales User and Sales Manager. Sending such an operator to
 * `/accounting/cashiers` lands them on a page they may not open, which reads as
 * the app being broken rather than as a permission they lack.
 */
const canOpenCashiersPage = computed(() => canPage(PAGE_KEYS.ACCOUNTING_CASHIERS));

/**
 * Profiles have loaded and none of them belongs to this operator — an
 * administrator has to assign them. Distinguished from "not chosen yet" so the
 * warning can say which of the two it is.
 */
const hasNoAssignedProfile = computed(() =>
  cashierStore.loaded && !cashierStore.viewableProfiles.length,
);
const canCreatePosSaleAction = computed(() => canDoctype("Sales Invoice", "create"));
const canCreateQuickCustomerAction = computed(() => canDoctype("Guardian", "create"));
const canPrintPosReceipt = computed(() => canDoctype("Sales Invoice", "print") || canDoctype("Sales Invoice", "read"));
/**
 * Receive money / Pay out — anyone who can create a Payment Entry.
 *
 * This used to also require `FULL_ACCESS_ROLES`; that admin-only restriction was
 * dropped on request, so create rights on Payment Entry are the whole gate.
 */
const canRecordPaymentAction = computed(() => canDoctype("Payment Entry", "create"));

/**
 * Taking a payment against ONE named invoice — the right-click action.
 *
 * `canUseActiveProfile` is required because the entry is stamped with the till's profile
 * and cash account; without one the dialog itself refuses to submit.
 */
const canAddInvoicePayment = computed(() =>
  canDoctype("Payment Entry", "create") && cashierStore.canUseActiveProfile,
);

/**
 * The two expense modes need different rights, so they are gated separately.
 *
 * ⚠️ These are the FALLBACK routes' requirements, and many cashiers will have
 * neither. Hiding a mode is the honest outcome — better an absent option than one
 * that collects an amount and then fails at submit. Once `record_cashier_expense`
 * is deployed the backend can scope this to cashiers without granting blanket
 * journal or stock rights.
 */
const canRecordCashExpense = computed(() =>
  Boolean(cashierStore.cashAccount)
  && canDoctype("Journal Entry", "create")
  && canDoctype("Journal Entry", "submit"),
);

/** Issuing stock needs no till at all — the money left when the item was bought. */
const canRecordItemExpense = computed(() =>
  canDoctype("Stock Entry", "create") && canDoctype("Stock Entry", "submit"),
);

const canRecordExpense = computed(() => canRecordCashExpense.value || canRecordItemExpense.value);

/**
 * What the dialog opens with. Receive defaults to the whole debt, Pay to the
 * whole credit — the amount a cashier settles in one go far more often than not.
 * `null` when the balance is unknown, which leaves the field empty rather than
 * suggesting a figure that was never fetched.
 */
const paymentDialogAmount = computed(() => {
  if (!customerBalance.value)
    return null;

  return paymentDialogType.value === "Receive"
    ? customerBalance.value.receivable
    : customerBalance.value.credit;
});

/**
 * The fixed terminal frame is a DESKTOP shape. Below 960px the app swaps in its
 * own phone shell, which owns the scroll; forcing a viewport-height page inside
 * it would trap the content.
 */
const isTerminalFixed = computed(() => display.mdAndUp.value);

/**
 * Which layout the till renders. Persisted per device; see `usePosTheme`.
 *
 * ⚠️ `classic` adds NO class, so its rendered markup is byte-identical to before
 * this feature existed. Every Counter-only rule keys off `.pos-terminal--counter`.
 */
const { theme: posTheme, isCounter: isCounterTheme, setTheme: setPosTheme } = usePosTheme();

/**
 * Where each Counter block sits — the cashier's own arrangement, per device. See
 * `usePosCounterLayout`; Classic never reads it.
 */
const {
  zones: counterZones,
  editing: counterLayoutEditing,
  setEditing: setCounterLayoutEditing,
} = usePosCounterLayout();

/**
 * ONE template for every Counter widget, reused by all three columns. Vue has no
 * template fragments; without this each column would carry its own copy of every
 * binding, and a widget dragged to a column whose copy had drifted would silently
 * lose a handler.
 */
const [DefineCounterWidget, ReuseCounterWidget] = createReusableTemplate<{ id: PosCounterWidgetId }>();

/** The Counter customer card, when the layout shows one on the till. */
const counterCustomerRef = ref<InstanceType<typeof PosCustomerSection> | null>(null);

function toggleCounterLayoutEditing() {
  setCounterLayoutEditing(!counterLayoutEditing.value);
}

/** Leaving Counter leaves design mode too — Classic has nothing to design. */
watch(isCounterTheme, value => {
  if (!value)
    setCounterLayoutEditing(false);
});

/**
 * Column widths for the three-panel body.
 *
 * ⚠️ DESTRUCTURED ON PURPOSE. Refs nested inside a returned object are not
 * auto-unwrapped in a template — only top-level `<script setup>` bindings are. Kept
 * as `posColumns.detailsVisible`, the `v-if` below would test a Ref object, which is
 * always truthy, and the details column would never hide.
 */
const {
  bodyStyle: posBodyStyle,
  cartInline: posCartInline,
  detailsAvailable: posDetailsAvailable,
  detailsInline: posDetailsInline,
  detailsVisible: posDetailsVisible,
  isResizing: posIsResizing,
  isRtl: posIsRtl,
  resizeEnabled: posResizeEnabled,
  beginResize: posBeginResize,
  handleRange: posHandleRange,
  jump: posJumpHandle,
  nudge: posNudgeHandle,
  resetHandle: posResetHandle,
  toggleDetails: posToggleDetails,
  summarySized: posSummarySized,
} = usePosColumnLayout(bodyRef, checkoutRef, orderSummaryEl);

/**
 * The trailing handle's neighbours change when the details column is away, and a
 * separator that announces "details and cart" while details is off screen is worse
 * than one with no label at all.
 */
const cartResizerLabel = computed(() => (posDetailsVisible.value
  ? t("alkokh.pos.layout.resizeDetailsCart")
  : t("alkokh.pos.layout.resizeCatalogCart")));

/**
 * Counter's middle column is on screen when the column layout allows it AND the
 * cashier's arrangement puts something there. An emptied column collapses exactly
 * like a hidden one — no blank track — except while designing, when it must stay
 * open as a drop target.
 */
const counterDetailsShown = computed(() => posDetailsVisible.value
  && (counterLayoutEditing.value || counterZones.value.col2.length > 0));

/**
 * ⚠️ SPILL-OVER, DISPLAY ONLY. When the middle column is away (narrow screen, or the
 * cashier hid it), its widgets other than the detail tabs — which already move to the
 * side panel — are shown at the top of the cart column instead of vanishing. Nothing
 * is written back, so the arrangement is intact when the column returns.
 */
const counterCol3Ids = computed(() => {
  if (counterDetailsShown.value || counterLayoutEditing.value)
    return counterZones.value.col3;

  return [...counterZones.value.col2.filter(id => id !== "detailTabs"), ...counterZones.value.col3];
});

const paymentStatusItems = computed(() => [
  {
    title: t("alkokh.pos.paymentStatus.paid"),
    value: "Paid",
  },
  {
    title: t("alkokh.pos.paymentStatus.due"),
    value: "Due",
  },
]);

const paymentModeItems = computed(() => {
  const values = cashierStore.allowedModeOfPayments.length
    ? cashierStore.allowedModeOfPayments
    : cashierStore.preferredModeOfPayment
      ? [cashierStore.preferredModeOfPayment]
      : [];

  return values.map(value => ({
    title: value,
    value,
  }));
});

const catalogPageSizeItems = computed(() =>
  catalogPagination.pageSizeOptions.map(value => ({
    title: String(value),
    value,
  })),
);

const selectedCustomerLabel = computed(() =>
  String(
    customerOption.value?.raw?.customer_name
    ?? customerOption.value?.raw?.customer_name_in_arabic
    ?? customerOption.value?.label
    ?? customerOption.value?.id
    ?? "",
  ).trim() || null,
);

const customerDialogAttachTarget = computed<HTMLElement | undefined>(() =>
  shellRef.value ?? undefined,
);

const cartCount = computed(() =>
  cart.value.reduce((sum, line) => sum + Number(line.qty ?? 0), 0),
);

/**
 * The cart as it goes to the backend, with each line's `label` set to the name the
 * cashier actually saw.
 *
 * ⚠️ THIS DECIDES WHAT `item_name` IS WRITTEN ONTO THE INVOICE (`posApi` sends
 * `item_name: item.label`) AND WHAT THE RECEIPT PRINTS. An Arabic session therefore
 * records the Arabic name, which is the behaviour that was asked for: the paperwork
 * matches what was on screen when the sale was rung up.
 *
 * ⚠️ THE TRADE-OFF IS REAL AND WAS ACCEPTED DELIBERATELY. The same item now reads
 * differently across invoices depending on the till's language at the time, so
 * `item_name` is NOT a reliable grouping key for reports or exports — `item_code` is.
 * Anything that needs one canonical name must join on the code, never on this string.
 */
const cartItemsForSale = computed(() => cart.value.map(line => ({
  ...line,
  label: pickLocalizedName(locale.value, line.label, line.arabicName, line.itemCode),
})));

const cartSubtotalAmount = computed(() =>
  cart.value.reduce((sum, line) => sum + (Number(line.qty ?? 0) * Number(line.unitPrice ?? 0)), 0),
);

/**
 * What the linked drafts are still owed, summed. Zero when nothing is linked.
 *
 * ⚠️ `amountDue`, NOT `total`. The gross ignores every prepayment already sitting on the
 * invoice, and this figure is what the cashier is asked to collect — that is how 175,000 was
 * taken from a customer who owed 105,000, the rest having been paid on a boarding stay weeks
 * earlier. On an invoice with no advances the two are identical, so the ordinary counter sale
 * is unchanged. See `resolveAmountDue` in `salesInvoiceApi`.
 */
const linkedInvoiceTotal = computed(() =>
  linkedInvoices.value.reduce((sum, invoice) => sum + Number(invoice.amountDue ?? 0), 0),
);

/** Prepayments across the linked drafts — what the gross overstates the bill by. */
const linkedInvoiceAdvanceTotal = computed(() =>
  linkedInvoices.value.reduce((sum, invoice) => sum + Number(invoice.totalAdvance ?? 0), 0),
);

/** The gross of the linked drafts. Still shown, because the customer's paper bill says it. */
const linkedInvoiceGrossTotal = computed(() =>
  linkedInvoices.value.reduce((sum, invoice) => sum + Number(invoice.total ?? 0), 0),
);

const isSettlingInvoices = computed(() => linkedInvoices.value.length > 0);

/**
 * Shared by the open-invoice bar and the details column. A computed rather than an
 * inline `.map()` in the template so both consumers read the same array identity —
 * a fresh array on every render would retrigger the details column's watchers.
 */
const linkedInvoiceIds = computed(() => linkedInvoices.value.map(invoice => invoice.id));

/**
 * The whole tab: the linked invoice's existing lines plus what was scanned here.
 *
 * The cashier collects for both, so every figure downstream — discount ceiling,
 * total, paid-amount default, change — has to be built on this and not on the
 * cart alone. When nothing is linked this is identical to the cart subtotal, so
 * the ordinary sale path is unchanged.
 */
const subtotalAmount = computed(() =>
  cartSubtotalAmount.value + linkedInvoiceTotal.value,
);

const discountValue = computed(() =>
  Math.max(Number(form.discountValue ?? 0), 0),
);

const discountAmount = computed(() => {
  if (discountValue.value <= 0)
    return 0;

  if (form.discountType === "Percentage")
    return Math.min(subtotalAmount.value * (Math.min(discountValue.value, 100) / 100), subtotalAmount.value);

  return Math.min(discountValue.value, subtotalAmount.value);
});

const discountLabel = computed(() => {
  const percent = form.discountType === "Percentage" && discountValue.value > 0
    ? `${formatAmount(discountValue.value)}%`
    : null;

  // A game prize names itself, so the receipt says where the discount came from.
  if (form.prize)
    return [t("alkokh.pos.prize.receiptLabel", { code: form.prize.claimCode }), percent].filter(Boolean).join(" · ");

  return percent;
});

const totalAmount = computed(() =>
  Math.max(subtotalAmount.value - discountAmount.value, 0),
);

const changeAmount = computed(() =>
  isDuePayment.value ? 0 : Math.max(Number(form.paidAmount ?? 0) - Math.max(totalAmount.value - (returnCredit.value?.amount ?? 0), 0), 0),
);

const balanceDueAmount = computed(() =>
  isDuePayment.value ? totalAmount.value : Math.max(totalAmount.value - (returnCredit.value?.amount ?? 0) - Number(form.paidAmount ?? 0), 0),
);

const hasStockBlockers = computed(() =>
  cart.value.some(line =>
    (line.isStockItem && !line.inStock)
    || (line.maxQty != null && Number(line.qty ?? 0) > Number(line.maxQty ?? 0)),
  ),
);

const hasPriceBlockers = computed(() =>
  cart.value.some(line => Number(line.unitPrice ?? 0) <= 0),
);

const quickCustomerFormValid = computed(() =>
  quickCustomerForm.fullName.trim().length >= 2
  && guardianPhoneRule(quickCustomerForm.phone) === true,
);

const canCheckout = computed(() =>
  Boolean(
    canCreatePosSaleAction.value
    && !returnOperationPending.value
    && !returnWorkspaceEditing.value
    && !petTransportOpen.value
    && hasCashierAccess.value
    && customerOption.value?.id
    && (isDuePayment.value || form.paymentMode || returnCredit.value?.amount === totalAmount.value)
    // A linked invoice is itself something to charge for, so settling one with
    // nothing scanned at the counter is a valid sale.
    && (cart.value.length > 0 || linkedInvoices.value.length > 0)
    /**
     * ⚠️ THERE IS DELIBERATELY NO `totalAmount > 0` HERE. DO NOT PUT IT BACK.
     *
     * It reads as obviously correct and it blocked every 100% discount: the total
     * goes to zero, `paidAmount` is auto-synced to the total, and both halves of
     * this guard fired at once — with a dead button and no reason on screen. A
     * comp, a giveaway and a written-off replacement are all real counter events.
     *
     * Nothing about a zero total is invalid: no backend contract states a minimum
     * amount, and the accounting side has always allowed a zero-total invoice. The
     * empty-sale case this was mistaken for is already covered by the line above —
     * an empty cart with nothing linked cannot check out.
     */
    /**
     * ⚠️ A PART PAYMENT IS A VALID SALE. This used to demand the full total,
     * leaving only two options — pay everything, or pay nothing on account — so a
     * customer handing over 10,000 against a 24,000 sale could not be served at
     * all. Any amount above zero now goes through; whatever is left becomes the
     * customer's balance, which is exactly what the account panel above shows.
     *
     * Zero is allowed only when there is genuinely nothing to collect.
     */
    && (isDuePayment.value || totalAmount.value === 0 || Number(form.paidAmount ?? 0) > 0 || Boolean(returnCredit.value))
    && (!returnCredit.value || (returnCredit.value.amount <= totalAmount.value && !isSettlingInvoices.value && !isPartnerOrder.value && !isDuePayment.value && !form.prize))
    /**
     * ⚠️ A PARTNER ORDER NEEDS THE PARTNER'S OWN ORDER NUMBER.
     *
     * It is the only thing tying this invoice to a line on the partner's monthly
     * statement. Without it a disputed order cannot be traced from either side,
     * and at settlement time the difference is unresolvable. The reason is shown
     * beside the button by `checkoutBlockReason`, not just implied by a dead
     * control.
     */
    && (!isPartnerOrder.value || Boolean(form.partnerOrderRef.trim()))
    /**
     * ⚠️ AN INSIDE PARTNER BILLS A REAL CUSTOMER — never the walk-in default. Named
     * beside the button by the `insideNeedsCustomer` watcher, refused here so it can
     * never reach the server.
     */
    && !partnerNeedsRealCustomer.value
    /**
     * ⚠️ A FLAT FEE MUST BE SMALLER THAN THE CART. The server refuses a 2,000 fee on
     * a 1,500 order (`PARTNER_COMMISSION_EXCEEDS_TOTAL`) — correctly, but only once
     * Process Transaction has been pressed. See `partnerFeeExceedsCart`.
     */
    && !partnerFeeExceedsCart.value
    /**
     * ⚠️ A TRANSPORTER'S SALE IS DUE, ON AT MOST ONE DRAFT, WITH SOMEWHERE TO GO. He
     * collects it at the door, so nothing is taken here (the server refuses a paid one), and
     * several drafts settled at once have no single invoice for him to deliver.
     */
    && (!saleTransporter.value || (isDuePayment.value && linkedInvoices.value.length <= 1 && Boolean(saleTransportJob.value)))
    && !hasStockBlockers.value
    && !hasPriceBlockers.value,
  ),
);

/**
 * Keeps the refusal on screen while a partner order is missing its reference.
 *
 * Written as a watcher rather than folded into `canCheckout` because the reason
 * has to appear as soon as the field is emptied, not only when Process Transaction
 * is pressed on a sale the cashier already believed was ready.
 */
watch([isPartnerOrder, () => form.partnerOrderRef], ([partnerOrder, orderRef]) => {
  if (partnerOrder && !String(orderRef ?? "").trim()) {
    checkoutBlockReason.value = t("alkokh.pos.partner.orderRefRequired");

    return;
  }

  if (checkoutBlockReason.value === t("alkokh.pos.partner.orderRefRequired"))
    checkoutBlockReason.value = null;
});


const catalogRangeLabel = computed(() => {
  if (!catalogTotal.value)
    return t("alkokh.pos.pagination.empty");

  const start = catalogPagination.limitStart.value + 1;
  const end = Math.min(catalogPagination.limitStart.value + catalog.value.length, catalogTotal.value);

  return t("alkokh.pos.pagination.range", {
    start,
    end,
    total: catalogTotal.value,
  });
});

function resolveItemImage(item: PosCatalogItem) {
  return resolveFrappeFileUrl(item.image) ?? "";
}

async function fetchItemGroupOptions() {
  catalogItemGroups.value = await ReadPosItemGroups(itemGroupsLoading);
}

async function fetchCatalog() {
  catalogPagination.isLoading.value = true;

  try {
    const response = await ReadPosCatalogItems(catalogLoading, {
      search: searchQuery.value.trim() || undefined,
      itemGroup: selectedItemGroup.value,
      warehouse: cashierStore.warehouse,
      ...catalogPagination.apiParams,
      order_by: catalogSort.value,
    });

    catalog.value = response?.data ?? [];
    catalogTotal.value = response?.total ?? catalog.value.length;
    catalogPagination.updateResult(catalog.value);
    catalogPagination.hasMore.value
      = catalogPagination.limitStart.value + catalog.value.length < catalogTotal.value;
  }
  finally {
    catalogPagination.isLoading.value = false;
  }
}

function scheduleCatalogRefresh(resetPage = true) {
  if (searchDebounceTimer)
    clearTimeout(searchDebounceTimer);

  searchDebounceTimer = setTimeout(() => {
    if (resetPage)
      catalogPagination.reset();

    fetchCatalog();
  }, 220);
}

async function goToNextCatalogPage() {
  if (!catalogPagination.next())
    return;

  await fetchCatalog();
}

async function goToPreviousCatalogPage() {
  if (!catalogPagination.previous())
    return;

  await fetchCatalog();
}

async function updateCatalogPageSize(value: number) {
  catalogPagination.changePageSize(Number(value ?? 24));
  await fetchCatalog();
}

function getItemQtyInCart(itemCode: string) {
  return cart.value.reduce((sum, line) =>
    line.itemCode === itemCode ? sum + Number(line.qty ?? 0) : sum,
  0);
}

function isItemSellable(item: PosCatalogItem) {
  return Boolean(item.itemCode) && (!item.isStockItem || item.inStock) && Number(item.price ?? 0) > 0;
}

/**
 * A catalogue item's name for anything the cashier READS — toasts, scan feedback,
 * refusals. The same string the product card and the cart line show, so a message about
 * an item names it the way the screen just did.
 */
function itemDisplayName(item: PosCatalogItem) {
  return pickLocalizedName(locale.value, item.itemName, item.arabicName, item.itemCode);
}

function buildCartItem(item: PosCatalogItem, qty = 1): PosCartItem {
  return {
    id: `pos-cart-${item.itemCode}-${Date.now()}`,
    itemId: item.id,
    itemCode: item.itemCode,
    label: item.itemName,
    arabicName: item.arabicName,
    itemGroup: item.itemGroup,
    barcode: item.barcode,
    image: item.image,
    unitPrice: Number(item.price ?? 0),
    priceList: item.priceList,
    priceListCurrency: item.priceListCurrency,
    qty,
    maxQty: item.isStockItem ? item.qtyAvailable : null,
    inStock: item.inStock,
    isStockItem: item.isStockItem,
  };
}

/**
 * Why this returns an outcome instead of nothing.
 *
 * The rules are UNCHANGED — same three refusals, same duplicate merge by item
 * code, same stock ceiling. What is new is that a scan needs to know which of
 * them happened (to sound the right tone) and needs a channel that is not a
 * toast: toasts dedupe identical text for 4 s and each one plays a sound, so a
 * cashier scanning the same item five times would hear and see almost nothing.
 * Clicks still toast exactly as before.
 */
type AddToCartOutcome = "added" | "incremented" | "missing-code" | "missing-price" | "out-of-stock" | "not-permitted";

function addItemToCart(item: PosCatalogItem, options: { silent?: boolean } = {}): AddToCartOutcome {
  const silent = options.silent === true;

  if (posReturn.active)
    return addToReturnCart(item, silent);

  if (!canCreatePosSaleAction.value)
    return "not-permitted";

  if (!item.itemCode) {
    if (!silent) {
      toast.error({
        text: t("alkokh.pos.messages.itemMissingCode", { item: itemDisplayName(item) }),
      });
    }

    return "missing-code";
  }

  if (Number(item.price ?? 0) <= 0) {
    if (!silent) {
      toast.warning({
        text: t("alkokh.pos.messages.productMissingPrice", { item: itemDisplayName(item) }),
      });
    }

    return "missing-price";
  }

  // Never merged into the Transportation line: that one is the transporter's fee, priced
  // on his sheet, and a scan of the same item must not double his share.
  const existingIndex = cart.value.findIndex(line => line.itemCode === item.itemCode && !line.transport);
  const nextQty = existingIndex > -1
    ? Number(cart.value[existingIndex]?.qty ?? 0) + 1
    : 1;

  if (item.isStockItem && (!item.inStock || (item.qtyAvailable != null && nextQty > Number(item.qtyAvailable)))) {
    if (!silent) {
      toast.warning({
        text: t("alkokh.pos.messages.outOfStock", { item: itemDisplayName(item) }),
      });
    }

    return "out-of-stock";
  }

  if (existingIndex > -1) {
    cart.value = cart.value.map((line, index) =>
      index === existingIndex
        ? {
            ...line,
            qty: nextQty,
          }
        : line,
    );

    return "incremented";
  }

  cart.value = [...cart.value, buildCartItem(item)];

  return "added";
}

function updateCartQty(index: number, nextQty: number) {
  if (nextQty <= 0) {
    removeCartLine(index);

    return;
  }

  // One job, one fee: the Transportation line stays at 1 and is re-priced on the sheet.
  if (cart.value[index]?.transport)
    return;

  cart.value = cart.value.map((line, currentIndex) => {
    if (currentIndex !== index)
      return line;

    const resolvedQty = line.maxQty != null
      ? Math.min(nextQty, Number(line.maxQty))
      : nextQty;

    return {
      ...line,
      qty: resolvedQty,
    };
  });
}

function removeCartLine(index: number) {
  cart.value = cart.value.filter((_, currentIndex) => currentIndex !== index);
}

/**
 * A delivery order was just created from this cart ("Deliver this cart" in the details
 * column). The cart is cleared here, and only here: a cart left full after its goods
 * became a delivery order is the same goods waiting to be rung up a second time.
 *
 * The card toasts the outcome itself (created / sent to the driver / created but not
 * sent) — only it knows whether quick send ran.
 */
function handleDeliveryOrderCreated() {
  clearSale();
  driverRefreshToken.value += 1;
}

// --- The side panel ----------------------------------------------------------
//
// ⚠️ NOTHING IN THE POS NAVIGATES AWAY. An order, a driver's settlement or an invoice
// the cashier wants to look at opens in a sheet INSIDE the terminal, and closing it
// leaves the cart, the customer and the payment exactly as they were. The sheet is a
// plain in-layout overlay (`PosSidePanel`), not a `VNavigationDrawer` — a drawer
// teleports outside the fullscreen element and is invisible there.

type PosSidePanelState =
  | { kind: "driverOrder"; order: string }
  | { kind: "driverSettlement"; driver: string }
  | { kind: "invoice"; invoiceId: string }
  | { kind: "partner" }
  /** The details cards, when the viewport has no room for them as a column. */
  | { kind: "details" }
  /** The selected customer's unused kiosk game prizes (`usePosGamePrize`). */
  | { kind: "prize" }
  /**
   * Customer, payment and Process, in the Counter layout.
   *
   * ⚠️ A SIXTH KIND RATHER THAN A NEW OVERLAY, deliberately. What comes free and
   * already debugged: fullscreen survival, RTL mirroring, the scrim, the
   * capture-phase Escape, and the `@media (max-width: 959px)` `position: fixed`
   * guard at the end of this file — which is exactly the rule nobody should have
   * to re-derive.
   */
  | { kind: "checkout" };

const sidePanel = ref<PosSidePanelState | null>(null);

const sidePanelOpen = computed({
  get: () => sidePanel.value !== null,
  set: (open: boolean) => {
    if (!open)
      sidePanel.value = null;
  },
});

const sidePanelOrder = computed(() => (sidePanel.value?.kind === "driverOrder" ? sidePanel.value.order : null));
const sidePanelDriver = computed(() => (sidePanel.value?.kind === "driverSettlement" ? sidePanel.value.driver : null));
const sidePanelInvoiceId = computed(() => (sidePanel.value?.kind === "invoice" ? sidePanel.value.invoiceId : null));
const sidePanelPartner = computed(() => sidePanel.value?.kind === "partner");
const sidePanelDetails = computed(() => sidePanel.value?.kind === "details");
const sidePanelCheckout = computed(() => sidePanel.value?.kind === "checkout");
const sidePanelPrize = computed(() => sidePanel.value?.kind === "prize");

/** The page's own copy of a linked invoice, so the peek does not read it twice. */
const sidePanelInvoicePreloaded = computed(() =>
  linkedInvoices.value.find(invoice => invoice.id === sidePanelInvoiceId.value) ?? null);

/** The order panel draws its own header; the others take one from the sheet. */
const sidePanelTitle = computed(() => {
  if (sidePanelDriver.value)
    return t("alkokh.driverOrders.posCards.driver.openSettlement");
  if (sidePanelInvoiceId.value)
    return `${t("alkokh.pos.openDrafts.peekTitle")} · ${sidePanelInvoiceId.value}`;
  if (sidePanelDetails.value)
    return t("alkokh.pos.sections.orderDetails");
  if (sidePanelCheckout.value)
    return t("alkokh.pos.checkout.title");
  if (sidePanelPrize.value)
    return t("alkokh.pos.prize.listTitle");

  return null;
});

/**
 * Below 1280px the details cards live in the side panel instead of a column.
 *
 * ⚠️ THIS IS THE ONLY ROUTE TO THEM ON A PHONE OR TABLET. `detailsAvailable` is false
 * under 1280px, so the `<aside>` is never mounted — open invoices, recent purchases and
 * the delivery cards were not merely hidden there, they did not exist and nothing could
 * ask for them. Same cards, same props, different container.
 */
const detailsInPanel = computed(() => !posDetailsAvailable.value);

/**
 * Everything `PosDetailsColumn` needs, in one object.
 *
 * ⚠️ BOUND WITH `v-bind` AT BOTH MOUNT POINTS — the column and the side panel — so the
 * two cannot drift. Twenty props copy-pasted into a second call site is twenty chances
 * for the phone to be quietly fed staler data than the desktop.
 */
const detailsColumnProps = computed(() => ({
  customerId: customerOption.value?.id ?? null,
  excludeInvoiceIds: linkedInvoiceIds.value,
  postingDate: form.postingDate,
  warehouseLabel: activeWarehouseLabel.value,
  paymentModeLabel: activeModeLabel.value,
  lineCount: cart.value.length,
  totalQty: cartCount.value,
  subtotalAmount: subtotalAmount.value,
  discountAmount: discountAmount.value,
  discountLabel: discountLabel.value,
  totalAmount: totalAmount.value,
  linkedInvoiceIds: linkedInvoiceIds.value,
  linkedInvoiceTotal: linkedInvoiceTotal.value,
  formatAmount,
  customerLabel: selectedCustomerLabel.value,
  cartLines: cart.value,
  attachTarget: customerDialogAttachTarget.value,
  driverRefreshToken: driverRefreshToken.value,
  draftsRefreshToken: draftsRefreshToken.value,
}));

/**
 * Everything `PosCheckoutPanel` needs, in one object.
 *
 * ⚠️ Same reason `detailsColumnProps` exists: the Classic checkout column and the
 * Counter panel render the same four children, and a second hand-copied binding list
 * is a second chance for one layout to be fed staler data than the other. The MARKUP
 * is still duplicated (Vue has no template fragment) — if you add a child to
 * `PosCheckoutPanel.vue`, add it to the Classic branch below too.
 */
const checkoutPanelProps = computed(() => ({
  customerOption: customerOption.value,
  selectedCustomerLabel: selectedCustomerLabel.value,
  canCreateCustomer: canCreateQuickCustomerAction.value,
  attachTarget: customerDialogAttachTarget.value,
  customerBalance: customerBalance.value,
  balanceLoading: balanceLoading.value,
  canRecordPayment: canRecordPaymentAction.value,
  activePartner: activePartner.value,
  partnerOrderRef: form.partnerOrderRef,
  partnerCustomerName: form.partnerCustomerName,
  partnerCommissionAmount: partnerCommissionAmount.value,
  partnerNetAmount: partnerNetAmount.value,
  transporterBar: transporterBar.value,
  settleableInvoices: settleableInvoices.value,
  linkedInvoiceIds: linkedInvoiceIds.value,
  canSettleInvoices: canSettleInvoices.value,
  canSettleAllInvoices: canSettleAllInvoices.value,
  invoicesLoading: linkLoading.value || settleableLoading.value,
  paymentStatus: form.paymentStatus,
  paymentMode: form.paymentMode,
  paidAmount: form.paidAmount,
  changeAmount: changeAmount.value,
  balanceDueAmount: balanceDueAmount.value,
  totalAmount: totalAmount.value,
  remarks: form.remarks,
  isDuePayment: isDuePayment.value,
  paymentStatusItems: paymentStatusItems.value,
  paymentModeItems: paymentModeItems.value,
  checkoutBlockReason: checkoutBlockReason.value,
  canCheckout: canCheckout.value,
  checkoutLoading: checkoutLoading.value,
  creditAvailable: creditOffered.value,
  credit: returnCredit.value,
  formatAmount,
}));

/**
 * Everything `PosOrderSummary` needs, in one object.
 *
 * ⚠️ BOUND WITH `v-bind` IN BOTH LAYOUT BRANCHES, for the same reason
 * `detailsColumnProps` is: twenty-odd props copy-pasted into a second call site is
 * twenty chances for one theme to show a different subtotal than the other.
 */
const orderSummaryProps = computed(() => ({
  cart: cart.value,
  itemCount: cartCount.value,
  subtotalAmount: subtotalAmount.value,
  discountType: form.discountType,
  discountValue: form.discountValue,
  discountAmount: discountAmount.value,
  discountLabel: discountLabel.value,
  paidAmount: form.paidAmount,
  changeAmount: changeAmount.value,
  balanceDueAmount: balanceDueAmount.value,
  totalAmount: totalAmount.value,
  isDuePayment: isDuePayment.value,
  hasStockBlockers: hasStockBlockers.value,
  hasPriceBlockers: hasPriceBlockers.value,
  highlightedLineId: highlightedLineId.value,
  linkedInvoices: linkedInvoices.value,
  linkedInvoiceTotal: linkedInvoiceTotal.value,
  customerCredit: customerBalance.value?.credit ?? null,
  discountLockedByPrize: Boolean(form.prize),
  returnAvailable: returnsAvailable.value,
  returnOffReason: returnsAvailable.value ? null : returnOffReason.value,
  formatAmount,
}));

const checkoutPanelRef = ref<InstanceType<typeof PosCheckoutPanel> | null>(null);

function openCheckoutPanel() {
  sidePanel.value = { kind: "checkout" };
}

/**
 * ⚠️ LOOSER THAN `canCheckout`, ON PURPOSE.
 *
 * `canCheckout` also requires a customer and a payment mode — both chosen INSIDE the
 * panel this button opens. Gating PAY on it would leave a cashier with a full cart
 * facing a dead button and no route to the fields that would revive it.
 */
const payDisabled = computed(() =>
  cart.value.length === 0 && linkedInvoices.value.length === 0);

/**
 * One button, two behaviours: toggle the column where there is room for one, open the
 * sheet where there is not. The cashier presses the same control either way.
 */
function toggleDetails() {
  if (detailsInPanel.value) {
    sidePanel.value = sidePanelDetails.value ? null : { kind: "details" };

    return;
  }

  posToggleDetails();
}

/** Bumped after a side-panel action changed an order, so the "on the road" card re-reads. */
const driverRefreshToken = ref(0);

function openDriverOrderPanel(order: string) {
  sidePanel.value = { kind: "driverOrder", order };
}

function openDriverSettlementPanel(driver: string) {
  sidePanel.value = { kind: "driverSettlement", driver };
}

/**
 * Receiving a driver's stock and cash, at the till.
 *
 * ⚠️ THE TWO THINGS A DRIVER BRINGS BACK, and both used to need `/drivers/settlement` —
 * a page the cashier cannot open mid-sale without abandoning the till. Goods a customer
 * refused stay in the driver's warehouse until they are received here, so without this
 * the till could send stock out and never take it back. Same dialogs as the settlement
 * page, attached to the terminal so they survive fullscreen.
 */
const settlementPanelRef = ref<InstanceType<typeof DriverSettlementPanel> | null>(null);
const returnUnsoldOpen = ref(false);
const handoverOpen = ref(false);

/** The side panel shows orders and figures to either delivery role; acting needs the result role. */
const { canActOnDriverOrders } = usePosDriverAccess();

/**
 * Settle the driver from the till — the same dialog as `/drivers/settlement`. Offered only
 * once the settlement endpoints answer; until then Handover stays the cash action.
 */
const settleDriverOpen = ref(false);
const settleDriverAvailable = ref(false);

function todayIsoDate() {
  const date = new Date();

  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

watch(sidePanelDriver, async (driver) => {
  // Reset first: a previous driver's "available" must not show Settle for the next one.
  settleDriverAvailable.value = false;

  if (!driver || !canActOnDriverOrders.value)
    return;

  settleDriverAvailable.value = await IsDriverSettlementAvailable(cashierStore.activeProfileName, driver);
});

/**
 * A DRIVER's personal code was scanned at the till (camera, scanner or the manual box).
 * His settlement opens in the side panel — the cashier never leaves the till — and, when this
 * cashier may settle, the Settle dialog opens straight away. Otherwise the read-only panel shows.
 */
async function openDriverFromScan(driver: string) {
  openDriverSettlementPanel(driver);
  toast.success({ text: t("alkokh.driverQr.pos.opening", { driver }) });

  if (!canActOnDriverOrders.value)
    return;

  const available = await IsDriverSettlementAvailable(cashierStore.activeProfileName, driver);

  settleDriverAvailable.value = available;

  if (available && sidePanelDriver.value === driver)
    settleDriverOpen.value = true;
}

function handleDriverSettled() {
  toast.success({ text: t("alkokh.driverSettle.pos.settled") });
  settlementPanelRef.value?.reload();
  driverRefreshToken.value += 1;
}

function handleDriverSettlementDone(successKey: string) {
  toast.success({ text: t(successKey) });
  settlementPanelRef.value?.reload();
  driverRefreshToken.value += 1;
}

function handleDriverOrderChanged() {
  driverRefreshToken.value += 1;
}

// --- Attaching a delivery partner --------------------------------------------

/**
 * Open the partner picker.
 *
 * ⚠️ REFUSED WHILE A CLINIC DRAFT IS LINKED, and the reason is put on screen. A
 * partner order and an invoice settlement are two different backend calls —
 * `create_pos_sale` carries the partner fields, `settle_open_invoice` does not —
 * so a sale cannot be both. Allowing it would silently drop the partner at
 * checkout and book an app's order as a walk-in customer's payment.
 */
async function openPartnerPicker() {
  // The clinic's own transporter is changed from his sheet, not from the apps' grid.
  if (saleTransporter.value) {
    petTransportOpen.value = true;

    return;
  }

  if (linkedInvoices.value.length > 0) {
    checkoutBlockReason.value = t("alkokh.pos.partner.blockedByInvoice");

    return;
  }

  sidePanel.value = { kind: "partner" };

  await loadPartnerOptions();
}

/**
 * The active partners, once per session (`GetActiveDeliveryPartners` caches). Read at
 * mount too: whether the Transporter button shows depends on there being an inside one.
 */
async function loadPartnerOptions() {
  if (partnerOptions.value.length > 0 || partnersLoading.value)
    return;

  partnersFailed.value = false;

  const result = await GetActiveDeliveryPartners(partnersLoading);

  // `null` is a failure, not "none configured". The panel says so rather than
  // telling a cashier no partners exist when the request simply did not answer.
  if (!result) {
    partnersFailed.value = true;

    return;
  }

  partnerOptions.value = result;
}

/**
 * Attach a partner to this sale.
 *
 * ⚠️ THE CUSTOMER IS SET BEFORE THE `await`, AND THE ORDER MATTERS. Assigning
 * `customerOption` fires the customer watcher, whose first act is
 * `resetCustomerAccountState()`. Anything written after the assignment but before
 * that flush is wiped by it — the same trap `loadDraft` documents. Awaiting
 * `nextTick` here means the partner state below lands after the reset has run.
 *
 * ⚠️ AN INSIDE PARTNER (`billsAppCustomer`) BILLS THE CUSTOMER THE CASHIER PICKED.
 * Swapping to the partner's own account is exactly what the server refuses for one
 * (`PARTNER_INSIDE_NEEDS_REAL_CUSTOMER`) — so the customer is left alone and the
 * picker stays open for the cashier to choose the person who ordered.
 */
async function attachPartner(partner: DeliveryPartner) {
  const wasInside = billsAppCustomer(activePartner.value);

  if (!billsAppCustomer(partner)) {
    const customerId = String(partner.customer ?? "").trim();

    if (!customerId) {
      checkoutBlockReason.value = t("alkokh.pos.partner.noCustomerLink");
      sidePanel.value = null;

      return;
    }

    customerOption.value = {
      id: customerId,
      label: partner.partner_name || customerId,
      subtitle: customerId,
      image: null,
      raw: {},
    };

    await nextTick();
  }
  else if (isWalkInCustomer.value || (activePartner.value && !wasInside)) {
    // The walk-in default is reseeded on every cleared sale, so it is almost always
    // what is selected when the partner is tapped — cleared rather than sold onto and
    // refused at Process Transaction. Likewise the previous ordinary partner's own
    // account when the cashier switches partners: it is not a person who ordered.
    customerOption.value = null;
    await nextTick();
  }

  // A name typed for a previous partner must not ride along: an inside partner's
  // customer is the real one on the invoice, and its bar has no name field to show it.
  if (billsAppCustomer(partner))
    form.partnerCustomerName = "";

  activePartner.value = partner;

  // The app collected the money. Nothing is taken here, and the payment section
  // is locked to match so a cashier cannot key a payment against it by habit.
  form.paymentStatus = "Due";
  form.paymentMode = "";
  form.paidAmount = 0;
  paymentEdited.value = false;

  checkoutBlockReason.value = null;
  sidePanel.value = null;
}

/**
 * Back to an ordinary counter sale, KEEPING the scanned lines.
 *
 * A cart is the cashier's typing, and a wrong partner tap must not cost it — the
 * same reasoning behind the driver card's Detach. Only the partner, the customer
 * and the forced payment status are undone.
 */
function detachPartner() {
  // ⚠️ AN INSIDE PARTNER'S CUSTOMER IS A REAL PERSON the cashier chose, and the cart may
  // well carry on as a normal sale for them. Only an ordinary partner's "customer" —
  // the partner's own account — is dropped.
  const wasInside = billsAppCustomer(activePartner.value);

  activePartner.value = null;
  if (!wasInside)
    customerOption.value = null;
  form.partnerOrderRef = "";
  form.partnerCustomerName = "";
  form.paymentStatus = "Paid";
  checkoutBlockReason.value = null;
  sidePanel.value = null;
  // ⚠️ THIS PATH DOES NOT GO THROUGH `clearSale` — it keeps the cart on purpose — so the
  // default customer has to be reseeded here too, or detaching leaves the picker empty.
  void applyDefaultCustomer();
  toast.success({ text: t("alkokh.pos.partner.detached") });
}

/** The transporter's name as the cashier reads it. */
function transporterName(person: TransportTransporterOption | null | undefined) {
  return person ? (locale.value === "ar" && person.label_ar) || person.label : "";
}

/**
 * The Transporter sheet's «Deliver this sale»: him, his fare and where the goods go.
 *
 * Any previous fare line is replaced, never added to — one job, one fare. The sale goes as
 * Due: nothing is taken at the counter, he collects at the door.
 */
function applyTransporter(job: TransporterCharge) {
  petTransportOpen.value = false;
  // A delivery app and the clinic's transporter never share a sale.
  if (activePartner.value)
    activePartner.value = null;

  const goods = cart.value.filter(line => !line.transport);
  cart.value = job.fee > 0 && job.item
    ? [
        ...goods,
        {
          ...buildCartItem({ ...job.item, price: job.fee }),
          maxQty: 1,
          inStock: true,
          isStockItem: false,
          transport: { transporter: job.transporter.value },
        },
      ]
    : goods;

  saleTransporter.value = job.transporter;
  transportDelivery.value = { ...job.delivery, rideAlongTask: job.rideAlongTask, ridingWith: job.ridingWith };
  form.paymentStatus = "Due";
  form.paymentMode = "";
  form.paidAmount = 0;
  paymentEdited.value = false;
  checkoutBlockReason.value = null;

  toast.success({ text: t("alkokh.pos.transporter.applied", { name: transporterName(job.transporter) }) });
}

/** «Remove from this sale»: the fare leaves with him, and the sale is an ordinary one again. */
function removeTransporter() {
  petTransportOpen.value = false;
  saleTransporter.value = null;
  transportDelivery.value = null;
  cart.value = cart.value.filter(line => !line.transport);
  form.paymentStatus = "Paid";
  paymentEdited.value = false;
}

/** What goes with the sale as `transport_job`, or null while something is missing. */
const saleTransportJob = computed(() => {
  if (!saleTransporter.value || !transportDelivery.value)
    return null;

  return buildTransportJob({
    transporter: saleTransporter.value.value,
    address: transportDelivery.value.address,
    phone: transportDelivery.value.phone,
    consignee: selectedCustomerLabel.value || customerOption.value?.id || "",
    items: cartItemsForSale.value.filter(line => !line.transport).map(line => `${Number(line.qty ?? 0)} × ${line.label}`).join("، ")
      || linkedInvoices.value.map(invoice => invoice.id).join("، "),
    rideAlongTask: transportDelivery.value.rideAlongTask,
  });
});

/** The transporter's bar in the checkout column. */
const transporterBar = computed(() => (saleTransporter.value && transportDelivery.value
  ? {
      transporter: saleTransporter.value,
      fare: saleTransportShare.value,
      clinicTotal: Math.max(totalAmount.value - saleTransportShare.value, 0),
      address: transportDelivery.value.address,
      phone: transportDelivery.value.phone,
      ridingWith: transportDelivery.value.ridingWith,
    }
  : null));

/**
 * The delivery belongs to the customer it was set up for. If the customer changes, he and
 * his fare go — delivering to whoever is selected now would be a job nobody asked for.
 */
watch(() => customerOption.value?.id, (id, previous) => {
  if (!saleTransporter.value || slotSwapping.value || id === previous)
    return;

  removeTransporter();
  toast.warning({ text: t("alkokh.transporters.droppedWithCustomer") });
});

/**
 * What the partner keeps on the sale as it currently stands — a share of the cart,
 * or the flat fee it takes from every order.
 *
 * ⚠️ PREVIEW ONLY. It is never subtracted from `totalAmount` — the customer is
 * charged in full and the commission is settled against the partner's account
 * later. The server computes the authoritative figure from the snapshot it stamps.
 */
const partnerCommissionAmount = computed(() => partnerCommissionOn(barPartner.value, totalAmount.value));

/** The commission figure sent with a partner sale — a snapshot of the partner's flat fee. */
const partnerCommissionSnapshot = computed(() => {
  if (!activePartner.value)
    return null;

  return Number(activePartner.value.commission_amount ?? 0);
});

// The clamp now also covers the cart the server refuses outright — one worth no
// more than a flat fee.
const partnerNetAmount = computed(() =>
  Math.max(totalAmount.value - partnerCommissionAmount.value, 0));

/**
 * A flat-fee order must leave something behind.
 *
 * The server refuses one that does not (`PARTNER_COMMISSION_EXCEEDS_TOTAL`), and that
 * refusal is correct — but it arrives after Process Transaction has been pressed.
 * Blocking here turns it into something the cashier sees while the cart is still
 * being built. A percentage partner cannot reach this: a rate of 100 or more is
 * refused at the partner form.
 */
const partnerFeeExceedsCart = computed(() =>
  Boolean(barPartner.value)
  && partnerCommissionType(barPartner.value) === "Amount"
  && totalAmount.value > 0
  && partnerCommissionAmount.value >= totalAmount.value);

/**
 * The message this watcher put on screen, so it only ever clears its OWN reason.
 *
 * Comparing against a freshly formatted string would not do: the fee is in the text
 * and the cart moves between setting the reason and clearing it.
 */
const partnerFeeBlockMessage = ref<string | null>(null);

/**
 * Keeps the refusal on screen while a flat-fee partner's cart is too small.
 *
 * Same shape as the order-reference watcher and for the same reason: the cashier has
 * to read it while items are still going in, not discover it at Process Transaction.
 *
 * ⚠️ DECLARED HERE, NOT BESIDE THAT WATCHER. `watch` reads its sources the moment it
 * is registered, so a watcher on these computeds placed above them throws before the
 * till renders at all.
 */
watch([partnerFeeExceedsCart, partnerCommissionAmount], ([exceeds, fee]) => {
  if (exceeds) {
    partnerFeeBlockMessage.value = t("alkokh.pos.partner.feeExceedsCart", { amount: formatAmount(fee) });
    checkoutBlockReason.value = partnerFeeBlockMessage.value;

    return;
  }

  if (partnerFeeBlockMessage.value && checkoutBlockReason.value === partnerFeeBlockMessage.value)
    checkoutBlockReason.value = null;

  partnerFeeBlockMessage.value = null;
  // `immediate` so a restored tab that is ALREADY in this state says why, rather than
  // rendering a dead Process Transaction with nothing beside it.
}, { immediate: true });

// --- Loading one of the branch's open drafts into the sale --------------------
//
// The customer walks up with a clinic bill. The desk finds the INVOICE in the details
// column, and loading it means: a fresh sale, this customer, this draft linked, its lines
// locked above the cart, Process Transaction settles it. Anything scanned afterwards is
// appended by the backend at settlement, exactly as linking already works.

/** Bumped after a settlement, so the draft that was just paid leaves the list. */
const draftsRefreshToken = ref(0);
const replaceDraftOpen = ref(false);
const pendingDraft = ref<SalesInvoiceListItem | null>(null);

function requestLoadDraft(invoice: SalesInvoiceListItem) {
  // Scanned lines are the only thing loading a draft destroys — ask before that, never
  // otherwise. Replacing a linked draft with another loses nothing the cashier typed.
  if (cart.value.length > 0) {
    pendingDraft.value = invoice;
    replaceDraftOpen.value = true;

    return;
  }

  void loadDraft(invoice);
}

async function confirmReplaceDraft() {
  const invoice = pendingDraft.value;

  replaceDraftOpen.value = false;
  pendingDraft.value = null;

  if (invoice)
    await loadDraft(invoice);
}

async function loadDraft(invoice: SalesInvoiceListItem) {
  const customerId = String(invoice.customer ?? "").trim();

  if (!customerId) {
    toast.warning({ text: t("alkokh.pos.openDrafts.noCustomer") });

    return;
  }

  clearSale();

  customerOption.value = {
    id: customerId,
    label: invoice.customerName || customerId,
    subtitle: customerId,
    image: null,
    raw: {},
  };

  // ⚠️ ORDER MATTERS. The customer watcher runs `loadCustomerAccount`, whose first act is
  // `resetCustomerAccountState()` — which empties `linkedInvoices`. Linking before that
  // flush would be wiped by it. `nextTick` resolves after the flush, so the reset has run.
  await nextTick();
  await linkOpenInvoice(invoice.id);
}

function clearSale() {
  returnCredit.value = null;
  cart.value = [];
  customerOption.value = null;
  form.remarks = "";
  form.paymentStatus = "Paid";
  form.discountType = "Fixed";
  form.discountValue = 0;
  form.paidAmount = 0;
  paymentEdited.value = false;
  manualCode.value = "";
  // ⚠️ THE PARTNER MUST NOT SURVIVE A SALE. Left attached, the next walk-in
  // customer's goods would be billed to Talabat and no cash would be asked for.
  activePartner.value = null;
  saleTransporter.value = null;
  transportDelivery.value = null;
  form.partnerOrderRef = "";
  form.partnerCustomerName = "";
  // The prize belonged to that sale and that customer. The cart and discount it set are
  // already reset above, so it is dropped, not "removed" (nothing to undo).
  form.prize = null;
  // A new sale is a new sale: the next checkout must mint a fresh key, or a
  // retry of THIS one would be replayed against the previous customer's invoice.
  checkoutIdempotencyKey.value = null;
  checkoutBlockReason.value = null;
  resetCustomerAccountState();
  // The next walk-in should not have to be named. Seeded AFTER the reset above, so the
  // customer watcher's own account load is the one that survives.
  void applyDefaultCustomer();
}



// --- Counter layout: keypad, search overlay, checkout panel ------------------

/**
 * What the shared keypad is currently editing.
 *
 * One pad, three targets. Each confirm routes straight into the page's EXISTING
 * mutator — the pad introduces no second way to change a quantity or a discount, it
 * is only another way to type the number.
 */
type PosKeypadTarget =
  | { kind: "discount" }
  | { kind: "qty"; index: number }
  | { kind: "paid" };

const keypadTarget = ref<PosKeypadTarget | null>(null);
const keypadValue = ref(0);

const keypadOpen = computed(() => keypadTarget.value !== null);

const keypadMode = computed<"integer" | "decimal">(() =>
  keypadTarget.value?.kind === "qty" ? "integer" : "decimal");

const keypadMax = computed(() => {
  const target = keypadTarget.value;

  if (!target)
    return null;

  if (target.kind === "discount")
    return discountValueCeiling(form.discountType, subtotalAmount.value);

  /*
    ⚠️ The SNAPSHOT stock, not a live re-read — the same `maxQty` the `+` button is
    capped by. A live figure would drift from the cap and make the pad and the stepper
    disagree about the same line. `null` means "not a stock item", which has no ceiling.
  */
  if (target.kind === "qty")
    return cart.value[target.index]?.maxQty ?? null;

  // Cash taken is deliberately uncapped: a customer hands over more than the total
  // and the till gives change. Capping it would make that impossible to enter.
  return null;
});

const keypadTitle = computed(() => {
  const target = keypadTarget.value;

  if (!target)
    return "";

  if (target.kind === "discount")
    return t("alkokh.pos.keypad.discountTitle");

  if (target.kind === "paid")
    return t("alkokh.pos.keypad.paidTitle");

  const line = cart.value[target.index];

  return t("alkokh.pos.keypad.qtyTitle", {
    item: line ? pickLocalizedName(locale.value, line.label, line.arabicName, line.itemCode) : "",
  });
});

const keypadHint = computed(() =>
  keypadMax.value == null
    ? null
    : t("alkokh.pos.keypad.maxHint", { max: formatAmount(keypadMax.value) }));

function openKeypad(target: PosKeypadTarget) {
  // A game prize owns the discount; the summary hides its pad button, and this covers
  // any other route in (the server would refuse a discount that doesn't match the prize).
  if (target.kind === "discount" && form.prize)
    return;

  keypadTarget.value = target;

  if (target.kind === "discount")
    keypadValue.value = Number(form.discountValue ?? 0);
  else if (target.kind === "paid")
    keypadValue.value = Number(form.paidAmount ?? 0);
  else
    keypadValue.value = Number(cart.value[target.index]?.qty ?? 0);
}

function confirmKeypad(value: number) {
  const target = keypadTarget.value;

  keypadTarget.value = null;

  if (!target)
    return;

  if (target.kind === "discount") {
    // Clamped again on the way in. The pad caps what can be TYPED; this is the same
    // guard the summary's own field applies, and the two must not diverge.
    form.discountValue = clampDiscountValue(form.discountType, value, subtotalAmount.value);

    return;
  }

  if (target.kind === "paid") {
    handlePaidAmountChange(value);

    return;
  }

  updateCartQty(target.index, Math.floor(value));
}

/** The typed-lookup overlay that stands in for the product grid. */
const searchOverlayOpen = ref(false);
const searchOverlaySeed = ref<string | null>(null);

function openSearchOverlay(seed: string | null = null) {
  searchOverlaySeed.value = seed;
  searchOverlayOpen.value = true;
}

function addFromSearch(item: PosCatalogItem) {
  void addItemToCart(item);
  // Left OPEN on purpose: a cashier adding three things should not have to reopen
  // the lookup between each one. Escape, the scrim and the close button all shut it.
}

// --- Parked sales (tabs) ----------------------------------------------------
//
// ══════════════════════════════════════════════════════════════════════════════
//  SNAPSHOT / RESTORE, NOT REACTIVE SLOTS.
// ══════════════════════════════════════════════════════════════════════════════
//
// Every ref above stays exactly what it was. A slot is a frozen COPY of the live
// sale; switching tabs captures the current refs into the outgoing slot and writes
// the incoming slot back over them. Nothing else in this 2400-line script changes.
//
// ⚠️ The alternative — making `cart` into `slots[active].cart` — would rewrite every
// `cart.value` reference and every computed that reads one, in a file that runs a
// live till. This costs one function pair instead.
//
// ⚠️ THERE IS STILL EXACTLY ONE LIVE CART, one barcode wedge and one keydown
// handler. That is the whole safety argument. Two `/pos/sell` workspace tabs would
// NOT be equivalent: `WorkspaceTabHost` keeps deactivated pages reactive under
// `<KeepAlive>`, so a scan would land in both carts at once.

/** More than this and the strip stops being readable at a counter. */
const POS_SALE_SLOT_LIMIT = 6;

function createSaleSlot(): PosSaleSlot {
  return {
    id: `sale-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
    label: null,
    cart: [],
    customerOption: null,
    customerBalance: null,
    settleableInvoices: [],
    linkedInvoices: [],
    activePartner: null,
    transporter: null,
    transportDelivery: null,
    checkoutIdempotencyKey: null,
    checkoutBlockReason: null,
    manualCode: "",
    paymentEdited: false,

    /*
      ⚠️ A PRISTINE FORM, NOT `{ ...form }`.

      Spreading the live form makes a new sale inherit the PREVIOUS sale's discount,
      remarks and partner reference — so opening a second tab after a 5%-off sale
      quietly discounts the next customer too. Caught by the slot harness, not by eye.

      These are the values `clearSale()` leaves behind, field for field. `paymentMode`
      is the one thing that legitimately carries over: it is the till's default, seeded
      from the cashier profile, not something belonging to a particular sale.
    */
    form: {
      postingDate: getToday(),
      paymentStatus: "Paid",
      paymentMode: form.paymentMode,
      paidAmount: 0,
      discountType: "Fixed",
      discountValue: 0,
      remarks: "",
      partnerOrderRef: "",
      partnerCustomerName: "",
      prize: null,
    },
  };
}

/**
 * ⚠️ THE SLOT LIST LIVES IN `posSales.store`, NOT HERE, so the tabs outlive this
 * page: leaving freezes the live sale into its slot (`onBeforeUnmount`), and mount
 * restores it (`onMounted`). `claim` seeds one fresh slot on first use, and
 * discards saved sales that belong to another user or cashier profile.
 */
const posSales = usePosSalesStore();
const posSalesClaim = posSales.claim(createSaleSlot);
const { slots: saleSlots, activeSlotId } = storeToRefs(posSales);
/** Guards the watchers while a swap is mid-flight; see `activateSlot`. */
const slotSwapping = ref(false);

// --- Kiosk game prizes ---------------------------------------------------------
//
// ⚠️ The till applies a prize and sends its code with the sale; the SERVER redeems it in the
// same transaction (docs/backend/pos-game-prizes.md). Hidden entirely until the backend
// answers `list_customer_prizes` — see `usePosGamePrize`.

/** One unit of the prize's item, through the same path (and the same refusals) as a scan. */
async function addPrizeItem(itemCode: string) {
  const item = await ReadPosItemByBarcode(itemCode, cashierStore.warehouse).catch(() => null);

  if (!item)
    return { ok: false, reason: t("alkokh.pos.prize.errors.itemNotFound") };

  const outcome = addItemToCart(item, { silent: true });

  if (outcome === "added" || outcome === "incremented") {
    flashCartLine(item.itemCode);
    const line = cart.value.find(row => row.itemCode === item.itemCode);

    return { ok: true, unitPrice: Number(line?.unitPrice ?? item.price ?? 0) };
  }

  const reasons: Partial<Record<AddToCartOutcome, string>> = {
    "missing-price": t("alkokh.pos.prize.errors.itemNoPrice"),
    "out-of-stock": t("alkokh.pos.prize.errors.itemOutOfStock"),
  };

  return { ok: false, reason: reasons[outcome] ?? t("alkokh.pos.prize.errors.failed") };
}

function removePrizeItemUnit(itemCode: string) {
  const index = cart.value.findIndex(line => line.itemCode === itemCode);

  if (index > -1)
    updateCartQty(index, Number(cart.value[index].qty ?? 0) - 1);
}

const gamePrize = usePosGamePrize({
  form,
  cart,
  customer: customerOption,
  isWalkInCustomer,
  hasLinkedInvoices: isSettlingInvoices,
  subtotalAmount,
  slotSwapping,
  addItem: addPrizeItem,
  removeOneUnit: removePrizeItemUnit,
  // A prize vanishing changes the total, so the cashier must notice — warning, not success.
  notify: text => toast.warning({ text }),
});

const gamePrizeSectionProps = computed(() => ({
  applied: gamePrize.applied.value,
  offeredCount: gamePrize.offeredPrizes.value.length,
  disabledReason: gamePrize.disabledReason.value,
  applying: gamePrize.applying.value,
  error: gamePrize.error.value,
  formatAmount,
}));

async function applyGamePrize(code: string) {
  if (await gamePrize.apply(code)) {
    toast.success({ text: t("alkokh.pos.prize.appliedToast", { code: form.prize?.claimCode ?? code }) });
    if (sidePanelPrize.value)
      sidePanel.value = null;
  }
}

const activeSlotIndex = computed(() =>
  Math.max(0, saleSlots.value.findIndex(slot => slot.id === activeSlotId.value)));

const saleTabs = computed<PosSaleTabView[]>(() => saleSlots.value.map((slot, index) => ({
  id: slot.id,
  label: slot.label || t("alkokh.pos.saleTabs.saleN", { n: index + 1 }),
  // The LIVE figures for the active slot: its snapshot is stale until the next
  // capture, so reading it would show a count one action behind.
  lineCount: slot.id === activeSlotId.value
    ? cart.value.length + linkedInvoices.value.length
    : slot.cart.length + slot.linkedInvoices.length,
})));

const canAddSaleSlot = computed(() => saleSlots.value.length < POS_SALE_SLOT_LIMIT);

/** Freeze the live sale into whichever slot currently owns it. */
function captureActiveSlot() {
  const slot = saleSlots.value[activeSlotIndex.value];
  if (!slot)
    return;

  // Shallow copies of the arrays only. The line objects themselves are never
  // mutated in place — `updateCartQty` replaces them — so a deep clone would cost
  // a full cart copy per tab click and buy nothing.
  slot.cart = [...cart.value];
  slot.customerOption = customerOption.value;
  slot.customerBalance = customerBalance.value;
  slot.settleableInvoices = [...settleableInvoices.value];
  slot.linkedInvoices = [...linkedInvoices.value];
  slot.activePartner = activePartner.value;
  slot.transporter = saleTransporter.value;
  slot.transportDelivery = transportDelivery.value;
  slot.checkoutIdempotencyKey = checkoutIdempotencyKey.value;
  slot.checkoutBlockReason = checkoutBlockReason.value;
  slot.manualCode = manualCode.value;
  slot.paymentEdited = paymentEdited.value;
  slot.form = { ...form };
}

/**
 * Make `slot` the live sale.
 *
 * ⚠️ THE ORDER HERE IS LOAD-BEARING, and it is the same discipline `loadDraft`
 * already follows. Writing `customerOption` fires the account watcher, which calls
 * `resetCustomerAccountState()` and EMPTIES `linkedInvoices`. Restore the drafts
 * before that flush and they are silently dropped — the cashier returns to a tab
 * that has quietly stopped settling three clinic invoices, with no error anywhere.
 *
 * ⚠️ `form.paidAmount` is restored LAST, after a second flush. The `totalAmount`
 * watcher overwrites `paidAmount` whenever `paymentEdited` is false, so restoring
 * the cart re-derives it and would discard a hand-typed amount.
 */
async function activateSlot(slot: PosSaleSlot) {
  slotSwapping.value = true;

  try {
    // Settle the watchers against an empty sale first, so nothing that fires
    // below is reacting to a half-restored mixture of two different sales.
    cart.value = [];
    linkedInvoices.value = [];
    activePartner.value = null;
    saleTransporter.value = null;
    transportDelivery.value = null;

    customerOption.value = slot.customerOption;

    await nextTick();

    // Past the customer watcher's synchronous reset, so these survive. The async
    // half of `loadCustomerAccount` only ever writes balance and settleables, and
    // it re-reads both under its own token, so a late reply cannot clobber a tab.
    customerBalance.value = slot.customerBalance;
    settleableInvoices.value = [...slot.settleableInvoices];
    linkedInvoices.value = [...slot.linkedInvoices];

    cart.value = [...slot.cart];
    activePartner.value = slot.activePartner;
    saleTransporter.value = slot.transporter ?? null;
    transportDelivery.value = slot.transportDelivery ?? null;
    Object.assign(form, slot.form);
    // A tab frozen before prizes existed has no `prize` key, and `Object.assign` would then
    // leave the PREVIOUS tab's prize on this sale. Always written.
    form.prize = slot.form.prize ?? null;
    manualCode.value = slot.manualCode;
    checkoutIdempotencyKey.value = slot.checkoutIdempotencyKey;

    await nextTick();

    form.paidAmount = slot.form.paidAmount;
    paymentEdited.value = slot.paymentEdited;

    // ⚠️ One more flush before the refusal goes back. Writing `paidAmount` trips the
    // blanket "something changed" watcher below, which blanks `checkoutBlockReason`;
    // that watcher no-ops while `slotSwapping` is true, and this tick is when it runs.
    await nextTick();

    checkoutBlockReason.value = slot.checkoutBlockReason;
  }
  finally {
    slotSwapping.value = false;
  }
}

async function selectSaleSlot(id: string) {
  if (id === activeSlotId.value)
    return;

  const next = saleSlots.value.find(slot => slot.id === id);
  if (!next)
    return;

  captureActiveSlot();
  activeSlotId.value = id;
  await activateSlot(next);
}

async function addSaleSlot() {
  if (!canAddSaleSlot.value) {
    toast.warning({ text: t("alkokh.pos.saleTabs.limitReached") });

    return false;
  }

  captureActiveSlot();

  const slot = createSaleSlot();

  saleSlots.value.push(slot);
  activeSlotId.value = slot.id;
  await activateSlot(slot);
  // A brand-new sale still gets the walk-in customer, exactly as `clearSale` does.
  void applyDefaultCustomer();

  focusForNewSale();

  return true;
}

/**
 * Ready to type straight away. Skipped on touch screens, where focusing would throw
 * the on-screen keyboard over the till.
 */
function focusForNewSale() {
  if (typeof window !== "undefined" && window.matchMedia?.("(pointer: fine)").matches)
    focusManualCode();
}

/**
 * "New Sale" (button and F2): start a fresh sale WITHOUT losing the one on screen.
 *
 * ⚠️ NEVER PILES UP EMPTY TABS. A cashier pressing New Sale out of habit on an
 * already-empty sale gets that sale reset in place, and a parked empty tab is reused
 * before a new one is opened — otherwise the 6-tab cap fills with blanks.
 * Emptying a sale that HAS lines is `clearSale`, a separate action.
 */
async function startNewSale() {
  if (posReturn.active) {
    warnReturnActive();

    return;
  }

  const liveHasWork = posSaleSlotHasWork({ cart: cart.value, linkedInvoices: linkedInvoices.value });

  if (!liveHasWork) {
    clearSale();
    focusForNewSale();

    return;
  }

  const emptyParked = saleSlots.value.find(slot => slot.id !== activeSlotId.value && !slotHasWork(slot));

  if (emptyParked) {
    await selectSaleSlot(emptyParked.id);
    focusForNewSale();

    return;
  }

  await addSaleSlot();
}

/**
 * Keyboard access to the sale tabs. F2 starts a new sale (`startNewSale`), Shift+F2
 * clears the one on screen; Alt+1…6 jumps to a tab. Returns true when it handled the key.
 *
 * ⚠️ Alt+digit, not Ctrl+digit: Ctrl+1…8 is the BROWSER's own tab switch and
 * cannot be reliably taken over. Alt+digit is unbound on Windows browsers.
 */
function handleSaleTabShortcut(event: KeyboardEvent) {
  if (event.ctrlKey || event.metaKey)
    return false;

  if (event.shiftKey && !event.altKey && event.key === "F2") {
    event.preventDefault();
    requestClearSale();

    return true;
  }

  if (event.altKey && !event.shiftKey && /^Digit[1-6]$/.test(event.code)) {
    event.preventDefault();

    const slot = saleSlots.value[Number(event.code.slice(5)) - 1];
    if (slot)
      void selectSaleSlot(slot.id);

    return true;
  }

  return false;
}

const pendingSlotClose = ref<string | null>(null);

function slotHasWork(slot: PosSaleSlot) {
  return slot.id === activeSlotId.value
    ? posSaleSlotHasWork({ cart: cart.value, linkedInvoices: linkedInvoices.value })
    : posSaleSlotHasWork(slot);
}

async function closeSaleSlot(id: string, { confirmed = false } = {}) {
  // The last slot is the sale itself. Closing it would leave the till with no
  // cart at all, so it clears instead — which is what "close this sale" means
  // when it is the only one.
  if (saleSlots.value.length <= 1) {
    clearSale();

    return;
  }

  const slot = saleSlots.value.find(item => item.id === id);
  if (!slot)
    return;

  if (!confirmed && slotHasWork(slot)) {
    pendingSlotClose.value = id;

    return;
  }

  pendingSlotClose.value = null;

  const wasActive = id === activeSlotId.value;
  const index = saleSlots.value.findIndex(item => item.id === id);

  saleSlots.value.splice(index, 1);

  if (!wasActive) {
    // Closing a background tab must not disturb the sale in progress, so the
    // live refs are left completely alone.
    return;
  }

  const next = saleSlots.value[Math.min(index, saleSlots.value.length - 1)];

  activeSlotId.value = next.id;
  await activateSlot(next);
}

/**
 * ⚠️ Open sales survive in-app navigation (`posSales.store`) but NOT a reload.
 *
 * Persisting them would mean writing carts to localStorage — backend row data,
 * which `docs/README.frontend-caching-plan.md §11` forbids — and restoring prices
 * that may have moved since. This warns instead; the browser decides the wording.
 * Every slot counts, the live one included: a reload loses all of them.
 */
function hasOpenWork() {
  return saleSlots.value.some(slotHasWork);
}

function guardUnloadWithOpenSales(event: BeforeUnloadEvent) {
  if (!hasOpenWork())
    return;

  event.preventDefault();
  event.returnValue = "";
}

/**
 * Leaving the till no longer discards anything — `onBeforeUnmount` freezes the live
 * sale into the store. The one navigation still refused is mid-checkout: the frozen
 * copy would keep a cart the server may already have invoiced, and the cashier would
 * come back to a sale that looks unpaid.
 *
 * A switched cashier profile or user is handled on the way back in, by
 * `posSales.claim` (their price list and warehouse would no longer match).
 */
onBeforeRouteLeave(() => {
  if (!checkoutLoading.value)
    return true;

  toast.warning({ text: t("alkokh.pos.saleTabs.leaveWhileCheckout") });

  return false;
});

// --- Scanning -------------------------------------------------------------

function reportScan(tone: "ok" | "duplicate" | "error", text: string) {
  signalScan(tone);
  scanFeedback.value = { tone, text };

  if (scanFeedbackTimer)
    clearTimeout(scanFeedbackTimer);

  scanFeedbackTimer = setTimeout(() => {
    scanFeedback.value = null;
  }, SCAN_FEEDBACK_MS);
}

function flashCartLine(itemCode: string) {
  highlightedLineId.value = cart.value.find(line => line.itemCode === itemCode)?.id ?? null;

  if (scanHighlightTimer)
    clearTimeout(scanHighlightTimer);

  scanHighlightTimer = setTimeout(() => {
    highlightedLineId.value = null;
  }, SCAN_HIGHLIGHT_MS);
}

function reportAddOutcome(item: PosCatalogItem, outcome: AddToCartOutcome, previousQty: number) {
  if (outcome === "added" || outcome === "incremented") {
    flashCartLine(item.itemCode);
    reportScan(
      outcome === "incremented" ? "duplicate" : "ok",
      outcome === "incremented"
        ? t("alkokh.pos.messages.scanIncremented", { item: itemDisplayName(item), qty: previousQty + 1 })
        : t("alkokh.pos.messages.scanAdded", { item: itemDisplayName(item) }),
    );

    return;
  }

  if (outcome === "out-of-stock") {
    reportScan("error", t("alkokh.pos.messages.outOfStock", { item: itemDisplayName(item) }));

    return;
  }

  if (outcome === "missing-price") {
    reportScan("error", t("alkokh.pos.messages.productMissingPrice", { item: itemDisplayName(item) }));

    return;
  }

  if (outcome === "missing-code") {
    reportScan("error", t("alkokh.pos.messages.itemMissingCode", { item: itemDisplayName(item) }));

    return;
  }

  reportScan("error", t("alkokh.cashier.profileRequiredMessage"));
}

/**
 * Codes already resolved during this session at this warehouse.
 *
 * ⚠️ IN MEMORY ONLY, NEVER localStorage. `docs/README.frontend-caching-plan.md §11`
 * bans persisting backend rows, and a catalogue row is one. A Map that dies with the
 * page is a different thing: it cannot outlive a price change by more than a shift,
 * and it is cleared outright whenever the warehouse changes, because the same barcode
 * carries different stock at a different warehouse.
 *
 * ⚠️ It caches the ITEM, and the item carries `maxQty` — a stock figure. That is the
 * same snapshot semantics the cart already has (`buildCartItem` freezes `maxQty` at
 * add time and the `+` button is capped by it), so this introduces no NEW staleness
 * class. It does mean a repeat scan reuses a price read earlier in the shift.
 */
const scannedItemCache = new Map<string, PosCatalogItem>();

watch(() => cashierStore.warehouse, () => {
  scannedItemCache.clear();
});

/**
 * Resolve one code and act on it. Never throws — a failed lookup must leave the
 * scanner ready for the next trigger pull, not stranded.
 *
 * Three routes, cheapest first. The round trip is what a cashier feels as slowness;
 * the keyboard wedge itself is already effectively instant.
 */
async function resolveScannedBarcode(barcode: string, options: { searchOnMiss?: boolean } = {}) {
  /*
    0. A DRIVER'S PERSONAL CODE. Checked first and on its own (`matchDriverCode`, never the full
    `parseScannedCode`, whose room fallback would claim ordinary item barcodes). It is not an
    item, so nothing is added to the cart.
  */
  const driverId = matchDriverCode(barcode);

  if (driverId) {
    await openDriverFromScan(driverId);

    return;
  }

  // Return mode: receipts and returned items, never the hidden sale cart.
  if (posReturn.active) {
    await resolveReturnScan(barcode);

    return;
  }

  /*
    1. ALREADY IN THIS CART. Re-scanning to add a second of something is the single
    most common action at a counter, and it needs no network at all: the line holds
    the barcode it was built from, and `addItemToCart` would only increment it anyway.

    ⚠️ Matched on `barcode`, not on `itemCode` — the scanned string is what we have.
    Lines added from the grid or the search overlay carry their catalogue barcode, so
    they match here too.
  */
  const cartLineIndex = cart.value.findIndex(line => line.barcode === barcode);

  if (cartLineIndex !== -1) {
    const line = cart.value[cartLineIndex];
    const previousQty = line.qty;

    updateCartQty(cartLineIndex, previousQty + 1);

    // `updateCartQty` clamps at `maxQty`, so a refused increment must not be
    // reported as a success — the cashier has to know the shelf is empty.
    if (cart.value[cartLineIndex]?.qty === previousQty) {
      reportScan("error", t("alkokh.pos.messages.cartQtyExceeded"));

      return;
    }

    flashCartLine(line.itemCode);
    // `itemDisplayName` takes a catalogue row; a CART line stores the English name
    // under `label`, so it is resolved directly here rather than shoehorned.
    reportScan("duplicate", t("alkokh.pos.messages.scanIncremented", {
      item: pickLocalizedName(locale.value, line.label, line.arabicName, line.itemCode),
      qty: cart.value[cartLineIndex].qty,
    }));

    return;
  }

  // 2. Seen earlier this session at this warehouse.
  let item: PosCatalogItem | null = scannedItemCache.get(barcode) ?? null;

  // 3. Ask the backend.
  if (!item) {
    try {
      item = await ReadPosItemByBarcode(barcode, cashierStore.warehouse);
    }
    catch {
      item = null;
    }

    if (item)
      scannedItemCache.set(barcode, item);
  }

  if (!item) {
    reportScan("error", t("alkokh.pos.messages.barcodeNotFound", { barcode }));

    // Only the MANUAL box falls back to searching. A hardware scan must not silently
    // rewrite the search and reload the grid under the cashier's hands mid-burst.
    if (options.searchOnMiss) {
      /*
        ⚠️ Counter opens the overlay SEEDED with the code instead of filtering a grid
        it does not render. It deliberately does not touch `searchQuery`: that ref is
        watched into `fetchCatalog()`, and writing it here would fire a paged read for
        a list nobody is looking at.
      */
      if (isCounterTheme.value) {
        openSearchOverlay(barcode);

        return;
      }

      selectedItemGroup.value = null;
      searchQuery.value = barcode;
      catalogPagination.reset();
      await fetchCatalog();
    }

    return;
  }

  const previousQty = getItemQtyInCart(item.itemCode);

  reportAddOutcome(item, addItemToCart(item, { silent: true }), previousQty);
}

async function drainScanQueue() {
  if (scanDraining)
    return;

  scanDraining = true;
  scannerState.value = "scanning";

  try {
    while (scanQueue.length) {
      const barcode = scanQueue.shift();
      if (barcode)
        await resolveScannedBarcode(barcode);
    }
  }
  finally {
    // Always, on every path: the indicator can never latch on a failed lookup.
    scanDraining = false;
    scannerState.value = "ready";
  }
}

function enqueueScan(barcode: string) {
  const normalized = barcode.trim();
  if (!normalized)
    return;

  // Scans are drained one at a time so a slow lookup cannot drop the code fired
  // behind it, and so the feedback chip stays in step with the cart.
  if (scanQueue.length >= SCAN_QUEUE_LIMIT)
    return;

  scanQueue.push(normalized);
  void drainScanQueue();
}

useBarcodeWedge({
  onScan: enqueueScan,
  enabled: () => !petTransportOpen.value,
});

/** Enter in the header box: exact code first, catalogue filter only on a miss. */
async function submitManualCode() {
  const code = manualCode.value.trim();
  if (!code)
    return;

  manualCode.value = "";
  scannerState.value = "scanning";

  try {
    await resolveScannedBarcode(code, { searchOnMiss: true });
  }
  finally {
    scannerState.value = "ready";
  }
}

// --- Customer account + open invoice --------------------------------------

function resetCustomerAccountState() {
  customerBalance.value = null;
  settleableInvoices.value = [];
  linkedInvoices.value = [];
}

/**
 * Load the selected customer's balance and any open invoice they already have.
 *
 * Both are best-effort and neither blocks the till: a cashier must be able to
 * keep scanning while these are in flight, and a failure must leave POS exactly
 * as usable as it is today.
 */
async function loadCustomerAccount(customerId: string | null) {
  customerRequestToken += 1;

  const token = customerRequestToken;

  resetCustomerAccountState();

  if (!customerId)
    return;

  balanceLoading.value = true;
  settleableLoading.value = true;

  const balancePromise = GetCustomerBalance(customerId, { company: cashierStore.company })
    .then((balance) => {
      if (token === customerRequestToken)
        customerBalance.value = balance;
    })
    .finally(() => {
      if (token === customerRequestToken)
        balanceLoading.value = false;
    });

  const invoicePromise = FindSettleableInvoices(settleableLoading, customerId)
    .then((invoices) => {
      if (token === customerRequestToken)
        settleableInvoices.value = invoices;
    })
    .catch((error: any) => {
      // ⚠️ A FAILED SEARCH IS NOT "NO OPEN INVOICE". Reported, never swallowed —
      // silently treating a failed read as "none" is how a second invoice gets
      // raised beside one that already exists.
      if (token === customerRequestToken)
        console.warn("Could not check for an open invoice", error);
    })
    .finally(() => {
      if (token === customerRequestToken)
        settleableLoading.value = false;
    });

  await Promise.allSettled([balancePromise, invoicePromise]);
}

/**
 * ⚠️ THIS DELIBERATELY REFUSES RATHER THAN DEGRADING, AND THAT IS NOT AN OVERSIGHT.
 *
 * The list row behind the bar already carries `id` and `total`, so linking from
 * it "works" — right up until the money moves. Three things are lost with the
 * detail document, and each is enough on its own:
 *
 *  1. `items`. `buildLocalReceipt` prints the clinic block from them; without
 *     them the receipt shows the counter goods against a total that silently
 *     includes charges it never lists.
 *  2. The read-back. `resolvePosSaleEnvelope` THROWS when it cannot re-read the
 *     invoice — after the backend has already appended, taken payment and
 *     submitted. Nothing prints, the cart survives, and the cashier's correct
 *     instinct is to press Pay again on a sale that already went through.
 *  3. `modified`. `mapSalesInvoice` never maps it — only `mapSalesInvoiceDetail`
 *     does — so `expectedModified` would go up as `null` and the optimistic lock
 *     that stops POS overwriting the vet's concurrent save is gone.
 *
 * So a refused read stays refused, and is reported by name. The bar and the
 * invoice stay on screen: the customer really does have an open tab, and hiding
 * it re-opens the duplicate-invoice hole the bar exists to close.
 */
function reportLinkFailure(invoiceId: string, error: any) {
  const status = Number(error?.status ?? error?.data?.status ?? 0);

  // ⚠️ The backend's own 403 text is an untranslated developer string naming
  // the doctype. It tells a cashier nothing and names internals, so this is
  // the one refusal whose message is written here rather than passed through.
  if (status === 403) {
    toast.error({
      text: t("alkokh.pos.linkedInvoice.linkNotAllowed", { invoice: invoiceId }),
    });

    return;
  }

  const reason = String(error?.message ?? "").trim();

  toast.error({
    text: reason
      ? t("alkokh.pos.linkedInvoice.linkFailedReason", { invoice: invoiceId, reason })
      : t("alkokh.pos.linkedInvoice.linkFailedNamed", { invoice: invoiceId }),
  });
}

/**
 * Refuse, before anything is linked, a draft that already carries prepayments.
 *
 * ⚠️ THIS IS THE HALF THAT MATTERS, not the `INVOICE_CARRIES_ADVANCES` handler below.
 * By the time the backend refuses, the cashier has already read a total off the screen
 * and very likely already taken the cash for it. Catching it at link time means the
 * wrong number is never shown at all.
 *
 * A prepaid invoice belongs to a boarding stay, and the remainder is collected there —
 * the till has no way to apply money to an invoice that already has advances against it.
 * Blocked rather than toasted, for the same reason as every other refusal here: it needs
 * to stay on screen while the cashier works out what to do.
 *
 * Returns true when it refused, so the callers can bail without linking.
 */
function rejectAdvanceCarryingInvoices(details: SalesInvoiceDetail[]) {
  const carrying = details.filter(invoice => Number(invoice.totalAdvance ?? 0) > 0);

  if (!carrying.length)
    return false;

  checkoutBlockReason.value = t("alkokh.pos.messages.invoiceCarriesAdvances", {
    invoice: carrying.map(invoice => invoice.id).join(", "),
  });

  return true;
}

/**
 * Link ONE draft. Reached only on the fallback path, when the merged endpoint is
 * not deployed and the cashier picks which single invoice to settle.
 */
async function linkOpenInvoice(invoiceId: string) {
  if (!invoiceId || linkLoading.value)
    return;

  // ⚠️ THE OTHER HALF OF THE MUTUAL EXCLUSION — see `openPartnerPicker`. Settling
  // a customer's clinic draft and selling on behalf of a delivery app are two
  // different backend calls, and only one of them carries the partner fields.
  if (isPartnerOrder.value) {
    checkoutBlockReason.value = t("alkokh.pos.partner.blockedByPartner");

    return;
  }

  try {
    const detail = await ReadSalesInvoiceDetail(linkLoading, invoiceId, { suppressAuthorizationBanner: true });

    if (rejectAdvanceCarryingInvoices([detail]))
      return;

    linkedInvoices.value = [detail];
    // Non-fatal, deliberately. The link SUCCEEDED; the credit strip is context beside it.
    // `refreshCustomerBalance` has no catch of its own, so without this a balance lookup
    // failing would be reported to the cashier as "the invoice could not be opened" — about
    // an invoice that is on screen and linked.
    await refreshCustomerBalance().catch(() => {});
  }
  catch (error: any) {
    reportLinkFailure(invoiceId, error);
  }
}

/**
 * Link every open draft the customer has, so one payment closes the whole tab.
 *
 * ⚠️ ALL OR NOTHING, AND THE FAILURE BRANCH IS THE IMPORTANT HALF. If one of the
 * drafts cannot be read, this links NONE of them. Quietly dropping the unreadable
 * one would put a total on screen, take the customer's money against it, and leave
 * a charge they believe they have paid still sitting open — the cashier would have
 * no way of knowing the tab shown was not the tab settled.
 *
 * Reads run in parallel; `Promise.all` rejects on the first failure, and the
 * assignment below it never happens.
 */
async function linkAllOpenInvoices() {
  const invoiceIds = settleableInvoices.value.map(invoice => invoice.id).filter(Boolean);

  if (!invoiceIds.length || linkLoading.value)
    return;

  linkLoading.value = true;

  try {
    const details = await Promise.all(
      invoiceIds.map(invoiceId =>
        ReadSalesInvoiceDetail(ref(false), invoiceId, { suppressAuthorizationBanner: true })
          .catch((error: any) => {
            // Re-thrown so `Promise.all` rejects, but tagged first — otherwise the
            // handler cannot say WHICH invoice refused, and with several in flight
            // that is the only detail worth having.
            error.invoiceId = invoiceId;
            throw error;
          }),
      ),
    );

    if (rejectAdvanceCarryingInvoices(details))
      return;

    linkedInvoices.value = details;
    // Non-fatal, deliberately. The link SUCCEEDED; the credit strip is context beside it.
    // `refreshCustomerBalance` has no catch of its own, so without this a balance lookup
    // failing would be reported to the cashier as "the invoice could not be opened" — about
    // an invoice that is on screen and linked.
    await refreshCustomerBalance().catch(() => {});
  }
  catch (error: any) {
    reportLinkFailure(String(error?.invoiceId ?? ""), error);
  }
  finally {
    linkLoading.value = false;
  }
}

function unlinkOpenInvoice() {
  linkedInvoices.value = [];
}

/**
 * "Open" an invoice from the bar — in the side panel. This used to `router.push` to the
 * accounting page, which took the cashier off the till mid-sale.
 */
function openLinkedInvoice(invoiceId: string) {
  if (invoiceId)
    sidePanel.value = { kind: "invoice", invoiceId };
}

/**
 * Reload the balance only.
 *
 * ⚠️ NOT `loadCustomerAccount` — that one calls `resetCustomerAccountState`,
 * which clears `linkedInvoices`. Calling it after a payment would silently drop
 * an invoice the cashier had already linked and is midway through settling.
 */
async function refreshCustomerBalance() {
  const customerId = customerOption.value?.id;
  if (!customerId)
    return;

  const token = customerRequestToken;

  balanceLoading.value = true;

  try {
    const balance = await GetCustomerBalance(customerId, { company: cashierStore.company });

    if (token === customerRequestToken)
      customerBalance.value = balance;
  }
  finally {
    if (token === customerRequestToken)
      balanceLoading.value = false;
  }
}

/**
 * Reload the customer's open-invoice list only.
 *
 * ⚠️ SAME REASON AS `refreshCustomerBalance` — not `loadCustomerAccount`, whose
 * `resetCustomerAccountState()` would clear `linkedInvoices` out from under a sale that
 * is midway through settling them. A fully paid invoice simply stops coming back from
 * the search, which is how it leaves the bar.
 */
async function refreshSettleableInvoices() {
  const customerId = customerOption.value?.id;
  if (!customerId)
    return;

  const token = customerRequestToken;

  try {
    const invoices = await FindSettleableInvoices(settleableLoading, customerId);

    if (token === customerRequestToken)
      settleableInvoices.value = invoices;
  }
  catch (error: any) {
    // A failed search is not "no open invoice" — report it, never swallow it.
    console.warn("Could not refresh the open invoices", error);
  }
}

function openPaymentDialog(type: PaymentEntryType) {
  if (!customerOption.value?.id || !canRecordPaymentAction.value)
    return;

  paymentDialogType.value = type;
  paymentDialogOpen.value = true;
}

/**
 * A payment changes what the customer owes, so the figures on screen are stale
 * the moment it succeeds. The cart and any linked invoice are left untouched —
 * money on account is a separate transaction from the sale in progress.
 */
async function handlePaymentCreated() {
  await refreshCustomerBalance();
}

// --- Right-click on an invoice row ---------------------------------------
//
// Three lists show invoices — the branch's open drafts, this customer's open invoices,
// and their recent purchases — and all three funnel their right-click here so the menu,
// its permissions and its actions are defined once.

function openInvoiceMenu(event: MouseEvent, invoice: SalesInvoiceListItem) {
  invoiceMenu.openAt(event, invoice);
}

function openInvoicePayment(invoice: SalesInvoiceListItem) {
  if (!canAddInvoicePayment.value)
    return;

  invoicePaymentTarget.value = invoice;
  invoicePaymentOpen.value = true;
}

/**
 * F4 — pay the invoice this sale already has linked.
 *
 * Silent when there is nothing unambiguous to act on: with no linked invoice there is no
 * target, and with several the cashier must say which, so the key does nothing rather
 * than guessing at one of them.
 */
function openInvoicePaymentForLinked() {
  if (linkedInvoices.value.length !== 1)
    return;

  const linked = linkedInvoices.value[0];

  openInvoicePayment({
    ...(linked as unknown as SalesInvoiceListItem),
    id: linked.id,
  });
}

function openAttachPartner(invoice: SalesInvoiceListItem) {
  if (!canCreatePosSaleAction.value)
    return;

  attachPartnerTarget.value = invoice;
  attachPartnerOpen.value = true;
}

/**
 * The invoice is now the partner's to collect. What the customer owes on screen is
 * unchanged (it is still their account), but the till must never take this money: if
 * it was somehow linked to the sale in progress it is dropped, and the lists re-read.
 * Same refresh rule as `handleInvoicePaymentCreated` — never `loadCustomerAccount`.
 */
async function handleInvoiceAttachedToPartner(result: { invoice: string }) {
  linkedInvoices.value = linkedInvoices.value.filter(invoice => invoice.id !== result.invoice);
  draftsRefreshToken.value += 1;
  await refreshCustomerBalance().catch(() => {});
  await refreshSettleableInvoices();
}

async function copyInvoiceId(invoice: SalesInvoiceListItem) {
  try {
    await writeClipboard(invoice.id);
    toast.success({ text: t("alkokh.copyable.copied"), timeout: 1200 });
  }
  catch {
    toast.error({ text: t("alkokh.copyable.copyFailed") });
  }
}

/**
 * A payment against an invoice changes two things on screen at once: the draft may now be
 * settled (so it should leave the open-invoices card) and the customer owes less.
 *
 * ⚠️ `refreshCustomerBalance`, NOT `loadCustomerAccount` — the latter starts with
 * `resetCustomerAccountState()`, which empties `linkedInvoices` and would silently drop an
 * invoice the cashier is midway through settling. Same rule as `handlePaymentCreated`.
 */
async function handleInvoicePaymentCreated() {
  draftsRefreshToken.value += 1;
  await refreshCustomerBalance();
  await refreshSettleableInvoices();
}

// --- Customer -------------------------------------------------------------

function openQuickCustomerDialog() {
  quickCustomerForm.fullName = "";
  quickCustomerForm.phone = "";
  quickCustomerForm.addressLine1 = "";
  quickCustomerForm.territory = "";
  customerDialogOpen.value = true;
}

/**
 * Read a customer and shape it the way the picker and the receipt both expect.
 *
 * ⚠️ THE FULL DOC, NOT A `{ id, label: id }` STUB. `selectedCustomerLabel` reads
 * `raw.customer_name` off this option, so a stub prints the bare docname under the
 * customer icon AND on the printed receipt.
 */
async function resolveCustomerOption(customerId: string): Promise<FrappeDoctypeLookupOption | null> {
  const customer = await GetCustomerDetail(customerPreselectLoading, customerId);
  if (!customer)
    return null;

  return {
    id: customer.name,
    label: customer.customerName,
    subtitle: customer.mobileNo || customer.emailId || customer.name,
    image: customer.image,
    raw: {
      name: customer.name,
      customer_name: customer.customerName,
      mobile_no: customer.mobileNo,
      email_id: customer.emailId,
      customer_group: customer.customerGroup,
      image: customer.image,
    },
  };
}

async function preselectCustomerFromRoute() {
  const customerId = typeof route.query.customer === "string"
    ? route.query.customer
    : "";

  if (!customerId || customerOption.value?.id === customerId)
    return;

  const option = await resolveCustomerOption(customerId);
  if (option)
    customerOption.value = option;
}

/**
 * `?customer=` (the "Sell" action on the customer pages) never re-assigns a sale
 * that already has lines — open sales survive navigation now, so the active tab may
 * well be someone else's order. It gets a new tab instead; an empty sale just takes
 * the customer, as before.
 */
async function openSaleForRouteCustomer() {
  const customerId = typeof route.query.customer === "string" ? route.query.customer : "";

  if (!customerId || customerOption.value?.id === customerId)
    return;

  const liveHasWork = posSaleSlotHasWork({ cart: cart.value, linkedInvoices: linkedInvoices.value });

  if (liveHasWork && !(await addSaleSlot()))
    return;

  await preselectCustomerFromRoute();
}

/**
 * Seed the till's default customer — عميل نقدي for an ordinary walk-in.
 *
 * A walk-in is the commonest sale at the counter and the one the cashier should not have
 * to name. The default comes from the POS Profile's own "Default Customer" field, so each
 * till can point at a different one and changing it never needs a deploy.
 *
 * ⚠️ NEVER OVERWRITES A CHOSEN CUSTOMER. The `customerOption.value` guard is what lets
 * this be called from `clearSale()`, `detachPartner()` and mount alike without racing the
 * route preselect or a loaded draft's own customer.
 *
 * ⚠️ A FAILED LOOKUP MUST NOT STOP THE TILL. A deleted or unreadable default customer
 * leaves the field empty — the cashier picks one by hand, exactly as before — rather than
 * throwing on the way into the terminal.
 */
async function applyDefaultCustomer() {
  const defaultCustomerId = String(cashierStore.defaultCustomer ?? "").trim();

  if (!defaultCustomerId || customerOption.value)
    return;

  try {
    const option = await resolveCustomerOption(defaultCustomerId);

    // Re-check: an await means the cashier may have picked someone meanwhile.
    if (option && !customerOption.value)
      customerOption.value = option;
  }
  catch (error: any) {
    console.warn("The till's default customer could not be loaded", error);
  }
}

async function createQuickCustomer() {
  if (!canCreateQuickCustomerAction.value || !quickCustomerFormValid.value)
    return;

  try {
    const guardian = await CreateGuardian(customerCreateLoading, {
      fullName: quickCustomerForm.fullName,
      phone: quickCustomerForm.phone || null,
      email: null,
      territory: quickCustomerForm.territory || null,
      addressLine1: quickCustomerForm.addressLine1 || null,
      city: "Baghdad",
      country: "Iraq",
      guardianImage: null,
    });

    if (!guardian?.name) {
      toast.error({ text: t("alkokh.pos.messages.customerCreateFailed") });
      return;
    }

    // ⚠️ INVOICE THE REAL LINKED CUSTOMER, NEVER A GUESSED ID. The backend may set
    // `customer_id` after insert, so re-read the guardian once when the POST lacks it.
    let customerId = guardian.customer_id || "";
    if (!customerId) {
      const profile = await getGuardianProfile(customerCreateLoading, guardian.name);
      customerId = profile?.guardian.customer_id || "";
    }

    const createdCustomer = customerId ? await resolveCustomerOption(customerId) : null;
    if (!createdCustomer) {
      // The guardian exists now — close so a second Save cannot create a duplicate.
      customerDialogOpen.value = false;
      toast.error({ text: t("alkokh.pos.messages.customerLinkMissing") });
      return;
    }

    customerOption.value = createdCustomer;
    customerDialogOpen.value = false;

    toast.success({
      text: t("alkokh.pos.messages.customerCreateSuccess", { customer: createdCustomer.label }),
    });
  }
  catch (error: any) {
    toast.error({
      text: error?.message || t("alkokh.pos.messages.customerCreateFailed"),
    });
  }
}

// --- Payment / checkout ---------------------------------------------------

function handlePaidAmountChange(value: unknown) {
  if (isDuePayment.value) {
    form.paidAmount = 0;
    paymentEdited.value = false;

    return;
  }

  paymentEdited.value = true;
  form.paidAmount = Number(value ?? 0);
}

function resetPaymentDefaults() {
  form.paymentMode = cashierStore.preferredModeOfPayment || paymentModeItems.value[0]?.value || "";
  form.paidAmount = isDuePayment.value ? 0 : Math.max(totalAmount.value - (returnCredit.value?.amount ?? 0), 0);
  paymentEdited.value = false;
}

function handleRecoveredCreditSale({ receipt, checkoutKey }: { receipt: PosReceiptPayload; checkoutKey: string | null }) {
  lastReceipt.value = receipt;
  lastInvoiceId.value = receipt.invoiceId;
  if (checkoutKey) {
    // A recovered parked sale must not be presented as an unpaid cart on reopening.
    saleSlots.value = saleSlots.value.filter(slot => slot.id === activeSlotId.value || slot.checkoutIdempotencyKey !== checkoutKey);
    if (checkoutKey === checkoutIdempotencyKey.value) clearSale();
  }
  if (!printPosReceipt(receipt)) toast.warning({ text: t("alkokh.posReturns.printFailed") });
  toast.success({ text: t("alkokh.posReturns.completed") });
}

/**
 * "Apply credit & complete sale": credit that covers the whole sale finishes it through
 * the ordinary checkout, and only when the till would allow that checkout anyway.
 */
async function handleCreditCheckout() {
  await nextTick();
  if (canCheckout.value)
    void checkout();
}

/** Why the return pill is muted: the server's own reasons once it sends them. */
const returnOffReason = computed(() => {
  if (!cashierStore.activeProfileName) return null;
  const blocked = posReturn.capabilities?.blocked ?? [];
  const reasons = blocked.map(code => (te(`alkokh.posReturnsBlocked.${code}`) ? t(`alkokh.posReturnsBlocked.${code}`) : code));
  return [t("alkokh.posReturns.returnOff"), ...reasons].join(" · ");
});

function warnReturnActive() {
  toast.warning({ text: t("alkokh.posReturns.finishReturnFirst") });
}

/** F8, the cart pill and the quick action. Leaving with lines asks first (inside the cart). */
function toggleReturnMode() {
  if (posReturn.active) {
    if (returnCartRef.value) returnCartRef.value.requestExit();
    else posReturn.exit();
    return;
  }
  enterReturnMode();
}

function enterReturnMode(invoiceId?: string) {
  if (checkoutLoading.value || petTransportOpen.value)
    return;
  // Still loading is fine with an invoice: it opens once the till's capabilities arrive.
  if (posReturn.capabilities ? !returnsAvailable.value : !invoiceId) {
    toast.warning({ text: returnOffReason.value || t("alkokh.posReturns.unavailable") });
    return;
  }
  posReturn.enter(invoiceId);
  void nextTick(() => returnCartRef.value?.focusScan());
}

function requestClearSale() {
  if (posReturn.active) {
    warnReturnActive();
    return;
  }
  clearSale();
}

function applyReturnCredit(credit: { voucher: string; amount: number }, andCheckout: boolean) {
  returnCredit.value = credit;
  if (andCheckout)
    void handleCreditCheckout();
}

/**
 * A pick from the catalogue while the cart is in return mode. With a receipt the item
 * goes out to the customer (the exchange); without one it is the item coming back.
 */
function addToReturnCart(item: PosCatalogItem, silent: boolean): AddToCartOutcome {
  if (posReturn.result || posReturn.busy || posReturn.pending)
    return "not-permitted";
  if (!item.itemCode)
    return "missing-code";
  if (!posReturn.source && !posReturn.withoutInvoice) {
    if (!silent) toast.warning({ text: t("alkokh.posReturns.scanReceiptFirst") });
    return "not-permitted";
  }
  if (!posReturn.withoutInvoice && item.isStockItem && !item.inStock) {
    if (!silent) toast.warning({ text: t("alkokh.pos.messages.outOfStock", { item: itemDisplayName(item) }) });
    return "out-of-stock";
  }
  posReturn.addItem(item);
  return "added";
}

/**
 * A scan in return mode: a receipt number (or customer) first, then an item. A
 * returnable item on the loaded receipt comes back; anything else goes out.
 */
async function resolveReturnScan(code: string) {
  if (posReturn.result || posReturn.busy || posReturn.pending)
    return;
  if (!posReturn.source && !posReturn.withoutInvoice) {
    if (await posReturn.findInvoices(code)) {
      reportScan("ok", t("alkokh.posReturns.receiptFound", { receipt: (posReturn.source as { name?: string } | null)?.name ?? code }));
      return;
    }
    posReturn.notice = "";
  }
  let item: PosCatalogItem | null = scannedItemCache.get(code) ?? null;
  if (!item) {
    try {
      item = await ReadPosItemByBarcode(code, cashierStore.warehouse);
    }
    catch {
      item = null;
    }
    if (item)
      scannedItemCache.set(code, item);
  }
  if (!item) {
    reportScan("error", t("alkokh.pos.messages.barcodeNotFound", { barcode: code }));
    return;
  }
  if (!posReturn.source && !posReturn.withoutInvoice) {
    reportScan("error", t("alkokh.posReturns.scanReceiptFirst"));
    return;
  }
  const where = posReturn.scanItem(item);
  reportScan("ok", t(where === "back" ? "alkokh.posReturns.scannedBack" : "alkokh.posReturns.scannedOut", { item: itemDisplayName(item) }));
}

async function handleReturnCompleted() {
  // fetchCatalog alone can reread an old in-memory offline mirror after a stock write.
  draftsRefreshToken.value += 1;
  await invalidateTags(["Sales Invoice", "Payment Entry", "Journal Entry", "Item", "Bin"]);
  try {
    const results = await refreshPacks({ force: true });
    const itemsFailed = results.find(pack => pack.pack === "pos")?.datasets.some(dataset => dataset.dataset === "items" && dataset.error);
    if (itemsFailed || results.mirrorReloaded === false) toast.warning({ text: t("alkokh.offline.syncPartial") });
    await fetchCatalog();
    await refreshCustomerBalance();
  }
  catch (error) {
    console.warn("POS return posted but catalog/account refresh failed", error);
    toast.warning({ text: t("alkokh.offline.syncPartial") });
  }
}

function amountsMatch(left: unknown, right: unknown) {
  return Math.abs(Number(left ?? 0) - Number(right ?? 0)) < 0.005;
}

async function handlePetTransportCompleted() {
  await invalidateTags(["Sales Invoice", "Payment Entry", "Journal Entry", "Account", "POS Profile"]);
  if (customerOption.value?.id)
    await refreshCustomerBalance().catch(error => console.warn("Transport posted; POS balance refresh failed", error));
}

/**
 * Build the receipt locally when the settle endpoint did not return a usable one.
 *
 * Everything on it is already known here — the linked invoice's lines, the cart,
 * the totals the cashier just read on screen — so this is exact, not a guess. It
 * exists because the alternative is printing blanks and `NaN` onto thermal paper
 * in front of the customer, which raises no error anywhere.
 */
function buildLocalReceipt(invoiceId: string): PosReceiptPayload {
  /**
   * ⚠️ ONE BLOCK PER INVOICE, IN ORDER — not one flat list. When several drafts
   * are settled together the customer is handed a single slip covering several
   * documents, and a run of unlabelled lines gives them no way to reconcile it
   * against the charges they already know about. The header row costs one line
   * and is only added when there is more than one document to tell apart.
   */
  const linkedLines = linkedInvoices.value.flatMap((invoice) => {
    const lines = (invoice.items ?? []).map(item => ({
      label: String(item.itemName ?? item.itemCode ?? "").trim(),
      qty: Number(item.qty ?? 0),
      unitPrice: Number(item.rate ?? 0),
      amount: Number(item.amount ?? 0),
    }));

    if (linkedInvoices.value.length < 2)
      return lines;

    return [
      { label: invoice.id, qty: 0, unitPrice: 0, amount: 0 },
      ...lines,
    ];
  });

  const cartLines = cart.value.map(line => ({
    label: line.label,
    qty: Number(line.qty ?? 0),
    unitPrice: Number(line.unitPrice ?? 0),
    amount: Number(line.qty ?? 0) * Number(line.unitPrice ?? 0),
  }));

  return {
    invoiceId,
    postingDate: form.postingDate,
    // Built at the moment of the sale, so the till clock is the sale's own time.
    postingTime: getCurrentTime(),
    customerLabel: selectedCustomerLabel.value,
    cashierProfileName: cashierStore.activeProfileName ?? "",
    paymentStatus: form.paymentStatus,
    paymentMode: isDuePayment.value ? null : form.paymentMode || null,
    paidAmount: isDuePayment.value ? 0 : Number(form.paidAmount ?? 0),
    changeAmount: changeAmount.value,
    balanceDueAmount: balanceDueAmount.value,
    subtotalAmount: subtotalAmount.value,
    discountAmount: discountAmount.value,
    discountLabel: discountLabel.value,
    totalAmount: totalAmount.value,
    // So a partner slip never prints as a plain unpaid invoice, which would invite
    // whoever collects it to ask the customer for money the app already took.
    partnerLabel: barPartner.value?.partner_name ?? null,
    partnerOrderRef: form.partnerOrderRef.trim() || null,
    // What the transporter keeps and what is the clinic's, when he is on the sale.
    ...(saleTransporter.value && hasTransportLine.value
      ? {
          transporterLabel: transporterName(saleTransporter.value),
          transporterShare: transporterSplit(totalAmount.value, cart.value).transporter,
          clinicShare: transporterSplit(totalAmount.value, cart.value).clinic,
        }
      : {}),
    // Clinic lines first, then what was scanned at the counter — the order the
    // customer saw on screen.
    items: [...linkedLines, ...cartLines],
  };
}

async function checkout() {
  // A pending return-credit write must never be converted into an ordinary/offline sale.
  if (returnCredit.value && (isDuePayment.value || isSettlingInvoices.value || isPartnerOrder.value || form.prize)) {
    checkoutBlockReason.value = t("alkokh.posReturns.invalidCredit");
    return;
  }
  if (!canCreatePosSaleAction.value)
    return;

  await cashierStore.ensureReady();

  if (!cashierStore.canUseActiveProfile || !cashierStore.activeProfileName) {
    toast.warning({
      text: t("alkokh.cashier.profileRequiredMessage"),
    });

    return;
  }

  if (!customerOption.value?.id || !canCheckout.value)
    return;

  // Settling open invoices goes through endpoints that know nothing of prize codes: the
  // discount would go through and the code would stay valid. The watcher removes a prize
  // when invoices are linked; this is the backstop.
  if (form.prize && isSettlingInvoices.value) {
    checkoutBlockReason.value = t("alkokh.pos.prize.disabled.linked");

    return;
  }

  const checkoutReturnCredit = returnCredit.value ? { ...returnCredit.value } : null;
  // Frozen now: `clearSale` empties all of it before the receipt runs.
  const checkoutTransporter = saleTransporter.value;
  const checkoutTransportJob = saleTransportJob.value;
  const checkoutSplit = hasTransportLine.value ? transporterSplit(totalAmount.value, cart.value) : null;
  // A Due sale for a pet on a transport trip can be handed to the transporter to collect —
  // not one he is already delivering (it is his job already).
  const checkoutDueCustomer = isDuePayment.value && !checkoutTransporter ? petTransportCustomer.value?.id ?? "" : "";
  try {
    const checkoutPostingDate = getToday();

    form.postingDate = checkoutPostingDate;

    /**
     * ⚠️ MINTED ONCE PER SALE, NOT ONCE PER ATTEMPT.
     *
     * A checkout that times out has often already been committed server-side.
     * Pressing Process Transaction again with a FRESH key would create a second
     * invoice and move the stock a second time — the customer charged twice with
     * nothing on screen to show it. Reusing the key lets the backend recognise
     * the retry and hand back the original sale. Cleared only on success and in
     * `clearSale`, so every genuine retry of this sale carries the same key.
     */
    if (!checkoutIdempotencyKey.value)
      checkoutIdempotencyKey.value = newIdempotencyKey();

    /**
     * ⚠️ SETTLING A LINKED INVOICE IS A BACKEND TRANSACTION, NOT A LOCAL MERGE.
     * The client sends intent and nothing else — it never stamps `is_pos`, never
     * touches `update_stock`, never rewrites the existing rows. Each of those
     * would be its own save that can succeed while the submit behind it fails,
     * and a draft left at `docstatus 0, is_pos 1` is invisible to the clinic's
     * own draft search forever. See docs/backend/pos-settle-open-invoice.md.
     */
    const settleCommon = {
      cashierProfileName: cashierStore.activeProfileName,
      company: cashierStore.company,
      warehouse: cashierStore.warehouse,
      paymentStatus: form.paymentStatus,
      paymentMode: form.paymentMode,
      paidAmount: isDuePayment.value ? 0 : Number(form.paidAmount ?? 0),
      cashAccount: cashierStore.cashAccount,
      discountType: form.discountType,
      discountValue: discountValue.value,
      remarks: form.remarks || null,
      transportJob: checkoutTransportJob,
      items: cartItemsForSale.value,
    };

    /**
     * ⚠️ THE SINGULAR ENDPOINT IS NOT LOOPED WHEN THERE ARE SEVERAL. It is used
     * only for exactly one invoice. Calling it three times is not a merged
     * settlement: the second call can fail after the first has submitted and taken
     * the money, leaving the customer paid-up on part of a tab that is still open,
     * and there is no idempotency key on that path to make the retry safe.
     */
    const result = isSettlingInvoices.value
      ? linkedInvoices.value.length > 1
        ? await SettleOpenInvoicesAtPos(checkoutLoading, {
            ...settleCommon,
            invoices: linkedInvoices.value.map(invoice => ({
              invoiceId: invoice.id,
              expectedModified: invoice.modified ?? null,
            })),
            idempotencyKey: checkoutIdempotencyKey.value,
          })
        : await SettleOpenInvoiceAtPos(checkoutLoading, {
            ...settleCommon,
            invoiceId: linkedInvoices.value[0].id,
            expectedModified: linkedInvoices.value[0].modified ?? null,
          })
      : await CreatePosSale(checkoutLoading, {
          returnCredit: checkoutReturnCredit,
          cashierProfileName: cashierStore.activeProfileName,
          company: cashierStore.company,
          cashAccount: cashierStore.cashAccount,
          customerId: customerOption.value.id,
          customerLabel: selectedCustomerLabel.value,
          postingDate: checkoutPostingDate,
          paymentStatus: form.paymentStatus,
          paymentMode: form.paymentMode,
          paidAmount: isDuePayment.value ? 0 : Number(form.paidAmount ?? 0),
          warehouse: cashierStore.warehouse,
          discountType: form.discountType,
          discountValue: discountValue.value,
          discountAmount: discountAmount.value,
          remarks: form.remarks || null,
          idempotencyKey: checkoutIdempotencyKey.value,
          // Omitted entirely on an ordinary sale — `buildPartnerFields` drops the
          // whole group when there is no partner, so a counter sale's request is
          // unchanged from before delivery partners existed.
          deliveryPartner: activePartner.value?.name ?? null,
          partnerOrderRef: form.partnerOrderRef.trim() || null,
          partnerCustomerName: form.partnerCustomerName.trim() || null,
          partnerCommissionRate: activePartner.value
            ? Number(activePartner.value.commission_rate ?? 0)
            : null,
          partnerCommissionAmount: partnerCommissionSnapshot.value,
          // Omitted entirely without a prize (see `CreatePosSale`).
          prizeClaimCode: form.prize?.claimCode ?? null,
          // His delivery job, booked by the server with the invoice; absent on an ordinary sale.
          transportJob: checkoutTransportJob,
          items: cartItemsForSale.value,
        });

    if (!result) {
      // Only the settle path can resolve null for a reason worth explaining: the
      // endpoint is not deployed. Say so, drop the affordance for this session,
      // and leave the cart intact so the cashier can unlink and sell normally.
      if (isSettlingInvoices.value) {
        // Which affordance is missing depends on which call resolved null, so
        // only that one is withdrawn. A site with the singular endpoint but not
        // the merged one keeps single-invoice settlement working.
        if (linkedInvoices.value.length > 1) {
          canSettleAllInvoices.value = false;
          toast.warning({ text: t("alkokh.pos.linkedInvoice.multiSettleUnavailable") });
        }
        else {
          canSettleInvoices.value = false;
          toast.warning({ text: t("alkokh.pos.linkedInvoice.unavailable") });
        }
      }

      return;
    }

    // The backend's receipt often carries the date without the time. It is printed at
    // the moment of the sale, so the till clock is the sale's own time — same as
    // `buildLocalReceipt`. Stored on `lastReceipt`, so a reprint keeps the sale's time.
    const baseReceipt = result.receipt
      ? { ...result.receipt, postingTime: result.receipt.postingTime || getCurrentTime() }
      : buildLocalReceipt(result.invoice.id);
    // The backend's receipt knows nothing of the split — that is the till's to print.
    const receipt: PosReceiptPayload = checkoutTransporter
      ? {
          ...baseReceipt,
          transporterLabel: transporterName(checkoutTransporter),
          transporterShare: checkoutSplit?.transporter ?? null,
          clinicShare: checkoutSplit?.clinic ?? null,
        }
      : baseReceipt;

    lastReceipt.value = receipt;
    lastInvoiceId.value = result.invoice.id;
    if (checkoutReturnCredit) {
      posReturn.checkPending();
      void handleReturnCompleted();
    }
    if (checkoutDueCustomer)
      void petTransportPanel.value?.offerInvoice(checkoutDueCustomer, result.invoice.id);
    // His delivery job was booked with the sale, in the same transaction: it is on his phone.
    if (checkoutTransporter) {
      toast.success({ text: t("alkokh.pos.transporter.deliver.booked", { name: transporterName(checkoutTransporter) }) });
      petTransportPanel.value?.refreshCount();
    }

    // A settled draft is no longer open: the branch list re-reads and drops it.
    draftsRefreshToken.value += 1;

    const printed = printPosReceipt(receipt);
    if (!printed) {
      toast.warning({
        text: t("alkokh.pos.messages.printPopupBlocked"),
      });
    }

    // A replay is not a new sale. Saying "completed" would invite the cashier to
    // wonder whether the customer was charged twice; naming it removes the doubt.
    //
    // ⚠️ A merged settlement names the COUNT, not the first invoice. "Completed —
    // ACC-SINV-01661" after clearing three documents reads as though the other two
    // are still outstanding.
    const settledCount = result.settledInvoiceIds?.length ?? 0;

    toast.success({
      text: result.replayed
        ? t("alkokh.pos.messages.checkoutReplayed", { invoice: result.invoice.id })
        : settledCount > 1
          ? t("alkokh.pos.linkedInvoice.settledAll", { count: settledCount })
          : t("alkokh.pos.messages.checkoutSuccess", { invoice: result.invoice.id }),
    });

    clearSale();
    // The panel is about a sale that no longer exists. Left open it would show the
    // next customer's blank form floating over the till for no reason.
    if (sidePanelCheckout.value)
      sidePanel.value = null;

    await fetchCatalog();
  }
  catch (error: any) {
    /**
     * ⚠️ THE CART SURVIVES A FAILURE, AND THE IDEMPOTENCY KEY WITH IT.
     *
     * The backend writes these messages for a cashier, so they are shown as they
     * came. `INSUFFICIENT_STOCK` in particular is an ordinary part of a shift —
     * the sale is not lost, the quantity just needs changing — so nothing is
     * cleared here and the same key is reused if they press Pay again.
     */
    const code = String(error?.data?.code ?? "");
    if (checkoutReturnCredit) {
      const message = String(error?.message || "");
      checkoutBlockReason.value = message.startsWith("alkokh.") ? t(message) : message || t("alkokh.posReturns.pending");
      posReturn.checkPending();
      return;
    }

    /**
     * ⚠️ THE SERVER NEVER ANSWERED — THE SALE IS NOT LOST, IT IS QUEUED.
     *
     * This is the offline path. The goods have left the shop and the customer is
     * standing there, so the one unacceptable outcome is telling the cashier the
     * sale failed and throwing away the cart. The sale is written to the device
     * (durably, before anything on screen says "done"), printed with its own
     * receipt number, and sent by the sync engine when the connection returns —
     * carrying THIS SAME idempotency key, so a sale that did land after all comes
     * back as a replay instead of a second invoice.
     *
     * Only the plain sale path is queued. Settling a clinic draft is NOT: it has
     * no idempotency key on the singular endpoint and depends on the invoice's
     * `expected_modified`, so replaying it later could settle against a document
     * that has since changed.
     */
    /**
     * ⚠️ A SALE WITH A GAME PRIZE IS NEVER QUEUED. The code is only checked and used up on
     * the server; queued, two tills could each spend the same code offline and one replay
     * would land in "needs attention" with the discount already handed over. The cart and
     * the idempotency key stay, so Pay again (once back online) is a safe retry — a sale
     * that DID land comes back as a replay.
     */
    if ((code === "NETWORK" || code === "TIMEOUT") && form.prize) {
      checkoutBlockReason.value = t("alkokh.pos.prize.needsConnection");

      return;
    }

    if ((code === "NETWORK" || code === "TIMEOUT") && !isSettlingInvoices.value) {
      const queued = await queueSaleOffline(checkoutIdempotencyKey.value);

      if (queued)
        return;

      // Queueing failed (no storage). Fall through and report honestly rather
      // than claim a sale nobody wrote down.
    }

    /**
     * ⚠️ THE ONE REFUSAL WHOSE WORDING IS OURS.
     *
     * Every other code here is shown in the backend's own words, because the backend
     * knows what is wrong AND what to do about it. This one it cannot: it knows the
     * invoice carries prepayments, but not that the remainder is collected on the
     * boarding record, in a different part of the app entirely. A cashier told only
     * "this invoice carries advances" is stuck. The backend's sentence is appended
     * underneath when it sent one, so nothing is hidden.
     *
     * Reached only if the link-time guard was bypassed — see `rejectAdvanceCarryingInvoices`.
     */
    if (code === "INVOICE_CARRIES_ADVANCES") {
      const backendReason = String(error?.message ?? "").trim();

      checkoutBlockReason.value = [
        t("alkokh.pos.messages.invoiceCarriesAdvancesHint"),
        backendReason,
      ].filter(Boolean).join(" — ");

      return;
    }

    /**
     * The rest are shown as they came. Each one names something on this screen the
     * cashier can change — a quantity, a partner switched off, a missing order number.
     * Held beside the button rather than toasted away.
     */
    if (isPosExpectedRefusal(code)) {
      checkoutBlockReason.value = error?.message || t("alkokh.pos.messages.checkoutFailed");

      return;
    }

    toast.error({
      text: error?.message || t("alkokh.pos.messages.checkoutFailed"),
    });
  }
}

/**
 * Save the sale on this device and finish the transaction as if the server had
 * answered. Returns false when the device itself could not store it, which is
 * the one case where the cashier must NOT be told the sale is done.
 */
async function queueSaleOffline(idempotencyKey: string | null): Promise<boolean> {
  if (!idempotencyKey || !customerOption.value?.id)
    return false;

  try {
    const { enqueue, nextReceiptNumber } = await import("@/offline/outbox");
    const { drainOutbox } = await import("@/offline/syncEngine");
    const { reserveLocalStock } = await import("@/offline/posDataSource");

    const receiptNo = await nextReceiptNumber(cashierStore.activeProfileName);

    await enqueue({
      kind: "pos_sale",
      idempotencyKey,
      posProfile: cashierStore.activeProfileName,
      receiptNo,
      payload: {
        cashierProfileName: cashierStore.activeProfileName,
        company: cashierStore.company,
        cashAccount: cashierStore.cashAccount,
        customerId: customerOption.value.id,
        customerLabel: selectedCustomerLabel.value,
        postingDate: form.postingDate,
        paymentStatus: form.paymentStatus,
        paymentMode: form.paymentMode,
        paidAmount: isDuePayment.value ? 0 : Number(form.paidAmount ?? 0),
        warehouse: cashierStore.warehouse,
        discountType: form.discountType,
        discountValue: discountValue.value,
        discountAmount: discountAmount.value,
        remarks: form.remarks || null,
        deliveryPartner: activePartner.value?.name ?? null,
        partnerOrderRef: form.partnerOrderRef.trim() || null,
        partnerCustomerName: form.partnerCustomerName.trim() || null,
        partnerCommissionRate: activePartner.value ? Number(activePartner.value.commission_rate ?? 0) : null,
        partnerCommissionAmount: partnerCommissionSnapshot.value,
        items: cartItemsForSale.value,
      },
    });

    // The shelf has fewer of these now, even though the server does not know yet.
    // Without this the next customer is sold stock that is already gone.
    for (const line of cart.value)
      reserveLocalStock(line.itemCode, Number(line.qty ?? 0));

    const receipt = buildLocalReceipt(receiptNo);

    lastReceipt.value = receipt;
    lastInvoiceId.value = receiptNo;

    if (!printPosReceipt(receipt))
      toast.warning({ text: t("alkokh.pos.messages.printPopupBlocked") });

    toast.success({ text: t("alkokh.offline.saleQueued", { receipt: receiptNo }) });

    clearSale();

    if (sidePanelCheckout.value)
      sidePanel.value = null;

    // Try immediately: the connection may already be back.
    void drainOutbox();

    return true;
  }
  catch (error) {
    console.error("Could not queue the sale on this device:", error);

    toast.error({ text: t("alkokh.offline.saleNotSaved") });

    return false;
  }
}

function printLastReceipt() {
  if (!canPrintPosReceipt.value)
    return;

  if (!lastReceipt.value)
    return;

  const printed = printPosReceipt(lastReceipt.value);
  if (!printed) {
    toast.warning({
      text: t("alkokh.pos.messages.printPopupBlocked"),
    });
  }
}

/**
 * The square order sticker for the sale just completed.
 *
 * ⚠️ A COUNTER SALE HAS NO DELIVERY ADDRESS, and that is not an error. `PosReceiptPayload`
 * carries no address and none is invented here — the label's printer omits an empty section
 * rather than printing an empty "Deliver to" caption, which would read as missing data. The
 * phone comes off the selected customer when there is one; a walk-in simply has neither.
 *
 * For a sale that IS going out with a driver, the label with the address on it is the one on
 * the delivery-order panel (`DriverOrderDetailPanel`), which this terminal already mounts.
 */
function printSaleLabel() {
  const receipt = lastReceipt.value;
  if (!receipt)
    return;

  orderLabel.print({
    orderNo: receipt.invoiceId,
    customerName: receipt.customerLabel,
    phone: String(customerOption.value?.raw?.mobile_no ?? "").trim(),
    priceText: `${formatAmount(receipt.totalAmount)} ${cashierStore.currency ?? ""}`.trim(),
  });
}

function openLastInvoice() {
  if (!lastInvoiceId.value)
    return;

  router.push(`/accounting/sales-invoices/${lastInvoiceId.value}`);
}

function openCashiers() {
  router.push("/accounting/cashiers");
}

function syncFullscreenState() {
  isFullscreen.value = document.fullscreenElement === shellRef.value;
}

async function toggleFullscreen() {
  if (!document.fullscreenEnabled) {
    toast.warning({
      text: t("alkokh.pos.messages.fullscreenUnavailable"),
    });

    return;
  }

  const shell = shellRef.value;
  if (!shell)
    return;

  try {
    if (document.fullscreenElement === shell)
      await document.exitFullscreen();
    else
      await shell.requestFullscreen();
  }
  catch (error: any) {
    toast.warning({
      text: error?.message || t("alkokh.pos.messages.fullscreenUnavailable"),
    });
  }
}

// --- Focus helpers / shortcuts -------------------------------------------

function focusManualCode() {
  headerRef.value?.focusSearch();
}

function focusCatalogSearch() {
  // Counter has no catalogue toolbar — the typed lookup IS the catalogue there.
  if (isCounterTheme.value) {
    openSearchOverlay();

    return;
  }

  toolbarRef.value?.focusSearch();
}

/**
 * ⚠️ In Counter the customer field lives inside the checkout panel, so the shortcut
 * has to OPEN the panel and then focus — a plain `focusSearch()` would silently do
 * nothing, which is the kind of regression nobody reports and everybody works around.
 */
async function focusCustomerSearch() {
  // The till's own customer card, when the Counter layout shows one — no panel needed.
  if (isCounterTheme.value && counterCustomerRef.value && !counterLayoutEditing.value) {
    counterCustomerRef.value.focusSearch();

    return;
  }

  if (isCounterTheme.value) {
    openCheckoutPanel();
    await nextTick();
    checkoutPanelRef.value?.focusCustomerSearch();

    return;
  }

  customerSectionRef.value?.focusSearch();
}

async function focusNotes() {
  if (isCounterTheme.value) {
    openCheckoutPanel();
    await nextTick();
    checkoutPanelRef.value?.focusNotes();

    return;
  }

  paymentSectionRef.value?.focusNotes();
}

/**
 * ⚠️ F5 IS DELIBERATELY TAKEN OVER while POS is open. On a dedicated counter
 * terminal a reload mid-sale loses the cart, so the key is worth more as the
 * customer jump. The scanner needs no shortcut at all — it is always live.
 */
function handleShortcut(event: KeyboardEvent) {
  if (petTransportOpen.value) return;
  if (handleSaleTabShortcut(event))
    return;

  if (event.ctrlKey || event.metaKey || event.altKey)
    return;

  if (event.key === "F8") {
    event.preventDefault();
    toggleReturnMode();

    return;
  }

  // In return mode F9 is the return's own Process: recap first, then the posting.
  if (posReturn.active && event.key === "F9") {
    event.preventDefault();
    if (!posReturn.result)
      void posReturn.process();

    return;
  }

  switch (event.key) {
    case "F2":
      event.preventDefault();
      void startNewSale();
      break;
    case "F3":
      event.preventDefault();
      focusManualCode();
      break;
    case "F4":
      event.preventDefault();
      openInvoicePaymentForLinked();
      break;
    case "F5":
      event.preventDefault();
      void focusCustomerSearch();
      break;
    case "F7":
      event.preventDefault();
      void openPartnerPicker();
      break;
    case "F9":
      event.preventDefault();
      /*
        ⚠️ TWO-STEP IN COUNTER, one-step in Classic — byte-identical behaviour there.
        Counter's F9 mirrors its PAY button: the first press opens the checkout panel
        (where the customer and payment mode `canCheckout` demands are chosen), the
        second commits. A single-step F9 would be dead on a sale whose customer has
        not been picked yet, with the picker behind a panel F9 refused to open.
      */
      if (isCounterTheme.value && !sidePanelCheckout.value) {
        openCheckoutPanel();
        break;
      }

      if (canCheckout.value && !checkoutLoading.value)
        void checkout();
      break;
    default:
      break;
  }
}

function applyTerminalBodyClass(active: boolean) {
  if (typeof document === "undefined")
    return;

  document.body.classList.toggle(TERMINAL_BODY_CLASS, active);
}

// --- Watchers -------------------------------------------------------------

watch(
  () => [searchQuery.value, selectedItemGroup.value, catalogSort.value, cashierStore.warehouse],
  () => scheduleCatalogRefresh(),
);

watch(
  () => totalAmount.value,
  (value, previousValue) => {
    if (isDuePayment.value) {
      form.paidAmount = 0;
      paymentEdited.value = false;

      return;
    }

    if (!paymentEdited.value || previousValue == null || amountsMatch(form.paidAmount, previousValue)) {
      form.paidAmount = Math.max(value - (returnCredit.value?.amount ?? 0), 0);
      paymentEdited.value = false;
    }
  },
  { immediate: true },
);

/**
 * Clear the refusal as soon as the cashier changes anything it could have been
 * about. A stale "not enough stock" sitting above the button after the quantity
 * was already corrected is worse than none — it implies the fix did not take.
 */
watch(
  () => [cart.value, form.paidAmount, form.paymentStatus],
  () => {
    // ⚠️ Silent during a tab swap. Restoring a parked sale writes the cart and the
    // paid amount, which looks exactly like a cashier edit — without this, switching
    // back to a sale that was blocked on stock would clear the reason it was blocked.
    if (slotSwapping.value)
      return;

    // ⚠️ Except a refusal that still holds. The fee watcher sets its reason on the same
    // cart change and this one ran after it, blanking it: a dead Process with nothing
    // beside it (a sale holding only the transporter's fare, seen live).
    checkoutBlockReason.value = partnerFeeExceedsCart.value ? partnerFeeBlockMessage.value : null;
  },
  { deep: true },
);

/**
 * Says WHY Process Transaction is dead when no customer is chosen.
 *
 * ⚠️ THIS CASE USED TO BE SILENT. `canCheckout` requires `customerOption`, but nothing
 * ever put a reason on screen for it — so a cashier with a full cart and an empty customer
 * field saw a dead button and no explanation at all. Every other blocker names itself;
 * this one now does too.
 *
 * ⚠️ DECLARED AFTER THE BLANKET CLEAR ABOVE, DELIBERATELY. That watcher wipes the reason
 * on any cart edit; registered later, this one re-asserts the customer refusal in the same
 * flush, so adding a line to a cart with no customer does not silently blank the message.
 *
 * Only speaks once there is something to sell — an empty till is not nagged about a
 * customer it does not need yet.
 */
watch(
  [customerOption, cart, linkedInvoices],
  ([customer, lines, linked]) => {
    if (slotSwapping.value)
      return;

    const hasSomethingToSell = lines.length > 0 || linked.length > 0;

    if (!(customer as FrappeDoctypeLookupOption | null)?.id && hasSomethingToSell) {
      checkoutBlockReason.value = t("alkokh.pos.messages.customerBlockedCheckout");

      return;
    }

    if (checkoutBlockReason.value === t("alkokh.pos.messages.customerBlockedCheckout"))
      checkoutBlockReason.value = null;
  },
  { deep: true },
);

/**
 * Says WHY an inside partner's sale cannot go on the walk-in default — while the cart
 * is being built, not as a refusal after Process Transaction.
 *
 * ⚠️ DECLARED AFTER THE BLANKET CLEAR, for the reason the customer watcher above gives.
 */
watch(
  [partnerNeedsRealCustomer, cart],
  ([blocked]) => {
    if (slotSwapping.value)
      return;

    const reason = t("alkokh.pos.partner.insideNeedsCustomer");

    if (blocked)
      checkoutBlockReason.value = reason;
    else if (checkoutBlockReason.value === reason)
      checkoutBlockReason.value = null;
  },
  { deep: true },
);

watch([() => customerOption.value?.id, () => cashierStore.activeProfileName, activeSlotId], () => {
  returnCredit.value = null;
});
watch(returnCreditAllowed, (allowed) => {
  if (!allowed)
    returnCredit.value = null;
});
watch(returnCredit, (credit) => {
  // Switching tabs restores that tab's own manually entered tender amount.
  if (slotSwapping.value) return;
  form.paidAmount = isDuePayment.value ? 0 : Math.max(totalAmount.value - (credit?.amount ?? 0), 0);
});

watch(
  () => form.paymentStatus,
  () => {
    resetPaymentDefaults();
  },
);

watch(
  () => cashierStore.preferredModeOfPayment,
  () => {
    if (!paymentEdited.value)
      resetPaymentDefaults();
  },
  { immediate: true },
);

/**
 * ⚠️ Waits for the mount-time restore. Fired immediately at setup, this lookup could
 * resolve AFTER `activateSlot` and quietly re-assign a restored sale that already has
 * lines. Mount runs the same preselect itself, once the restore is done.
 */
const mountRestoreDone = ref(false);

watch(
  () => route.query.customer,
  () => {
    if (mountRestoreDone.value)
      void openSaleForRouteCustomer();
  },
);

// Keyed on the id, not the option object: re-selecting the same customer must
// not refire two requests, and clearing the picker must drop the figures.
watch(
  () => customerOption.value?.id ?? null,
  customerId => void loadCustomerAccount(customerId),
);

watch(isTerminalFixed, value => applyTerminalBodyClass(value));

/**
 * Set once the saved sale is back on screen. Until then the live refs are empty, and
 * freezing them on a quick leave would overwrite the saved sale with nothing.
 */
let liveSaleRestored = false;
/** False once the page unmounts, so a slow mount stops touching the shared tabs. */
let pageAlive = true;

onMounted(async () => {
  applyTerminalBodyClass(isTerminalFixed.value);
  document.addEventListener("fullscreenchange", syncFullscreenState);
  window.addEventListener("keydown", handleShortcut);
  window.addEventListener("beforeunload", guardUnloadWithOpenSales);
  // The partner bar's terms are read once; the Transporter button comes from the transport service.
  void loadPartnerOptions();

  /*
    ⚠️ RESTORE FIRST, before any network wait. The barcode wedge is already live, so a
    scan during the catalogue fetch lands in the live cart — and a restore after it
    would wipe that line. `resetPaymentDefaults()` below is skipped for a restored
    sale: it would overwrite a hand-typed paid amount and the chosen payment mode.
  */
  if (posSalesClaim === "restored") {
    const slot = saleSlots.value.find(item => item.id === activeSlotId.value);
    if (slot)
      await activateSlot(slot);
  }
  else if (posSalesClaim === "discarded") {
    toast.warning({ text: t("alkokh.pos.saleTabs.discardedProfileChanged") });
  }

  liveSaleRestored = true;

  /*
    Offer back anything a crash interrupted — but only when this till is empty.
    Dropping a recovered cart on top of a restored slot, or on top of lines a
    cashier has already scanned in the last second, would be worse than losing it.
  */
  try {
    const draft = await loadCartDraft<PosCartItem>("pos-sell");

    if (draft?.lines?.length && !cart.value.length)
      recoverableDraft.value = draft;
    else if (draft)
      await clearCartDraft("pos-sell");
  }
  catch {
    // A missing draft must never stop the till from opening.
  }

  await cashierStore.ensureReady();

  // Bring the device copy of the catalogue into memory BEFORE the first read, so
  // the till opens from local data instead of paying a round trip it does not
  // need. Awaited, but it cannot reject: a device with no copy yet simply falls
  // through to the server as before.
  //
  // ⚠️ SCOPED TO THIS TILL. Two branches, four profiles, four warehouses: data
  // downloaded for another till would show its stock as if it were ours.
  await ensurePosMirror(cashierStore.activeProfileName);

  // Which warehouse these figures are counted from — read once here, and again
  // whenever the device copy changes.
  void refreshWarehouseLabel();

  await Promise.all([
    fetchItemGroupOptions(),
    fetchCatalog(),
  ]);
  if (posSalesClaim !== "restored")
    resetPaymentDefaults();
  syncFullscreenState();
  // Asked once, so the Link affordance is either offered or absent — never
  // offered and then refused mid-sale with a customer waiting.
  // Probed together, cached apart: a site may have the singular endpoint and not
  // the merged one, and that site must keep single-invoice settlement working.
  const [hasSingular, hasMerged] = await Promise.all([
    IsPosSettlementAvailable(),
    IsPosMultiSettlementAvailable(),
  ]);

  canSettleInvoices.value = hasSingular || hasMerged;
  canSettleAllInvoices.value = hasMerged;

  // Left already: opening a tab now would edit the shared tabs behind the cashier's back.
  if (!pageAlive)
    return;

  // Sees the restored sale, so a customer link opens a new tab instead of
  // re-assigning one that already has lines.
  await openSaleForRouteCustomer();
  mountRestoreDone.value = true;
  // ⚠️ AFTER the route preselect, never before. `?customer=` is an explicit instruction
  // and must win; `applyDefaultCustomer` no-ops once anything is already selected.
  await applyDefaultCustomer();
});

/**
 * ⚠️ `deep` ON PURPOSE: changing a line's quantity mutates the object in place, and a
 * shallow watch would miss exactly the edit a cashier is most likely to be mid-way
 * through when something goes wrong.
 */
/**
 * Changing till changes warehouse, so everything the device holds about stock is
 * about somewhere else. Reload the mirror and refresh the pack for the new till.
 */
watch(
  () => cashierStore.activeProfileName,
  async (profile, previous) => {
    if (!profile || profile === previous)
      return;

    await ensurePosMirror(profile);
    await fetchCatalog();
  },
);

/**
 * ⚠️ THE DEVICE COPY CHANGING IS NOT THE SAME AS THE SCREEN KNOWING.
 *
 * The till reads its local mirror even when online, so stock on screen is only as
 * fresh as the last mirror load. A pack refresh — the 3-minute timer, a reconnect,
 * or the Sync button — replaced that copy and nothing re-read it, so a quantity
 * that had just arrived stayed invisible until the cashier searched or paged.
 * That is the whole of "sync does not bring the new quantities".
 *
 * ⚠️ DEFERRED, NOT DROPPED, WHEN A FETCH IS RUNNING. This used to return early, on
 * the theory that the next bump would bring its own re-read — but a read already in
 * flight may have read the OLD copy, and there may be no next bump for three minutes.
 * The re-read now runs once that fetch finishes, so there is still never a second,
 * competing read.
 *
 * And the grid is not the only thing that read the old copy: the category chips,
 * the favourites strip, the scan cache and the stock caps on cart lines all did.
 */
const catalogRefreshToken = ref(0);
let catalogRereadPending = false;

async function rereadCatalogAfterMirrorChange() {
  if (catalogPagination.isLoading.value) {
    catalogRereadPending = true;

    return;
  }

  do {
    catalogRereadPending = false;
    await fetchCatalog();
  } while (catalogRereadPending);
}

/**
 * Re-cap the lines already in the cart against the new figures.
 *
 * Only `maxQty` / `inStock` move — never `qty`. A cart is open POS work and is not
 * the sync's to change; a line now above its stock is flagged by `hasStockBlockers`
 * and left for the cashier to decide.
 */
function refreshCartStockCaps() {
  let changed = false;

  const next = cart.value.map((line) => {
    const fresh = findLocalItemByCode(line.itemCode);

    if (!fresh)
      return line;

    const maxQty = fresh.isStockItem ? fresh.qtyAvailable : null;

    if (maxQty === line.maxQty && fresh.inStock === line.inStock)
      return line;

    changed = true;

    return { ...line, maxQty, inStock: fresh.inStock };
  });

  if (changed)
    cart.value = next;
}

watch(posMirrorVersion, async () => {
  void refreshWarehouseLabel();

  // Built from the old copy: a re-scan would otherwise reuse its stock and price.
  scannedItemCache.clear();
  refreshCartStockCaps();
  catalogRefreshToken.value += 1;
  void fetchItemGroupOptions();

  await rereadCatalogAfterMirrorChange();
});

// The pending flag is only worth anything if somebody looks at it when a fetch ends.
watch(() => catalogPagination.isLoading.value, (loading) => {
  if (!loading && catalogRereadPending)
    void rereadCatalogAfterMirrorChange();
});

watch(
  [cart, customerOption],
  () => {
    if (!liveSaleRestored)
      return;

    cartDraftSaver.save({
      screen: "pos-sell",
      lines: cart.value,
      customerId: customerOption.value?.id ?? null,
      customerLabel: selectedCustomerLabel.value,
      posProfile: cashierStore.activeProfileName,
      pricedAt: Date.now(),
    });
  },
  { deep: true },
);

/** Put a recovered cart back, re-checking each line against today's catalogue. */
async function restoreRecoverableDraft() {
  const draft = recoverableDraft.value;

  if (!draft)
    return;

  recoveringDraft.value = true;

  try {
    /*
      ⚠️ RE-PRICED, NOT REPLAYED. The stored prices are as old as the draft, and
      "a restored cart would carry prices that may have moved" is the stated reason
      carts were never persisted. Each line is looked up again and takes today's
      price and stock; a line whose item has since gone is dropped rather than sold
      at a number nobody can justify.
    */
    let dropped = 0;

    for (const line of draft.lines) {
      const found = await ReadPosItemByBarcode(line.itemCode, cashierStore.warehouse);

      if (found)
        addItemToCart({ ...found, price: found.price }, { silent: true });
      else
        dropped += 1;
    }

    // Quantities are restored after the lines exist, so each one is clamped by the
    // stock check rather than trusting a number from before the outage.
    for (const line of draft.lines) {
      const index = cart.value.findIndex(row => row.itemCode === line.itemCode);

      if (index >= 0)
        updateCartQty(index, Number(line.qty ?? 1));
    }

    if (dropped)
      toast.warning({ text: t("alkokh.offline.draftLinesDropped", { count: dropped }, dropped) });

    await clearCartDraft("pos-sell");
    recoverableDraft.value = null;
  }
  finally {
    recoveringDraft.value = false;
  }
}

async function discardRecoverableDraft() {
  await clearCartDraft("pos-sell");
  recoverableDraft.value = null;
}

onBeforeUnmount(() => {
  pageAlive = false;

  // 400ms is 400ms too long when the page is going away.
  void cartDraftSaver.flush();

  // Freeze the live sale into its slot; `posSales.store` keeps every tab until the
  // cashier comes back. A leave mid-checkout is refused by the route guard above.
  if (liveSaleRestored)
    captureActiveSlot();

  if (searchDebounceTimer)
    clearTimeout(searchDebounceTimer);

  if (scanFeedbackTimer)
    clearTimeout(scanFeedbackTimer);

  if (scanHighlightTimer)
    clearTimeout(scanHighlightTimer);

  applyTerminalBodyClass(false);
  document.removeEventListener("fullscreenchange", syncFullscreenState);
  window.removeEventListener("keydown", handleShortcut);
  window.removeEventListener("beforeunload", guardUnloadWithOpenSales);
});
</script>

<template>
  <div
    ref="shellRef"
    class="pos-terminal"
    :class="{
      'pos-terminal--fixed': isTerminalFixed,
      'pos-terminal--resizing': posIsResizing,
      'pos-terminal--counter': isCounterTheme,
    }"
  >
    <div class="pos-terminal__head">
      <!--
        A sale this device was part-way through when it stopped. Offered, never
        restored on its own: the cashier standing here may not be the one who
        started it, and silently rebuilding someone else's cart is worse than
        losing it. Renders nothing unless there is something to recover.
      -->
      <VAlert
        v-if="recoverableDraft"
        type="info"
        variant="tonal"
        density="compact"
        class="mb-2"
      >
        <div class="d-flex align-center justify-space-between flex-wrap gap-2">
          <span>{{ t("alkokh.offline.draftFound", { count: recoverableDraft.lines.length }, recoverableDraft.lines.length) }}</span>
          <div class="d-flex gap-2">
            <VBtn
              size="small"
              :loading="recoveringDraft"
              @click="restoreRecoverableDraft"
            >
              {{ t("alkokh.offline.draftRestore") }}
            </VBtn>
            <VBtn
              size="small"
              variant="text"
              :disabled="recoveringDraft"
              @click="discardRecoverableDraft"
            >
              {{ t("alkokh.offline.draftDiscard") }}
            </VBtn>
          </div>
        </div>
      </VAlert>

      <!--
        Parked sales. In the SHARED head, so both layouts get it — this is not a
        theme feature. Renders nothing at all until a second sale exists.
      -->
      <PosSaleTabs
        :tabs="saleTabs"
        :active-id="activeSlotId"
        :can-add="canAddSaleSlot"
        @select="id => void selectSaleSlot(id)"
        @add="void addSaleSlot()"
        @close="id => void closeSaleSlot(id)"
      />

      <PosPetTransport
        ref="petTransportPanel"
        v-model="petTransportOpen"
        :blocked="checkoutLoading || counterLayoutEditing"
        :customer="petTransportCustomer"
        :active-transporter="saleTransporter?.value ?? null"
        :current-fee="saleTransportShare"
        :clinic-total="Math.max(totalAmount - saleTransportShare, 0)"
        :sale="transporterSaleContext"
        :contact-phone="String(customerOption?.raw?.mobile_no || '')"
        :format-amount="formatAmount"
        @available="petTransportAvailable = $event"
        @count="petTransportCount = $event"
        @completed="handlePetTransportCompleted"
        @charge="applyTransporter"
        @remove="removeTransporter"
      />
      <PosCreditVoucherDialog
        v-model="creditDialogOpen"
        :profile="cashierStore.activeProfileName || ''"
        :customer="customerOption?.id || null"
        :sale-total="totalAmount"
        :credit-allowed="returnCreditAllowed"
        :checkout-busy="checkoutLoading"
        :attach="customerDialogAttachTarget"
        @apply="applyReturnCredit"
      />
      <!-- An unresolved return or voucher sale is recoverable from sale mode too. -->
      <VAlert
        v-if="posReturn.pending && !posReturn.active"
        type="warning"
        variant="tonal"
        density="compact"
      >
        {{ t('alkokh.posReturns.pending') }}
        <VBtn
          size="small"
          class="ms-2"
          :loading="posReturn.busy"
          @click="posReturn.recover()"
        >
          {{ t('alkokh.posReturns.recover') }}
        </VBtn>
      </VAlert>
      <PosHeader
        ref="headerRef"
        v-model="manualCode"
        :can-print-receipt="canPrintPosReceipt"
        :has-last-receipt="Boolean(lastReceipt)"
        :has-last-invoice="Boolean(lastInvoiceId)"
        :is-fullscreen="isFullscreen"
        :details-available="posDetailsAvailable"
        :details-visible="posDetailsVisible || sidePanelDetails"
        :details-in-panel="detailsInPanel"
        :active-theme="posTheme"
        :layout-editing="counterLayoutEditing"
        @submit="submitManualCode"
        @new-sale="startNewSale"
        @clear-sale="requestClearSale"
        @print-last="printLastReceipt"
        @print-label="printSaleLabel"
        @open-invoice="openLastInvoice"
        @open-cashiers="openCashiers"
        @toggle-fullscreen="toggleFullscreen"
        @toggle-details="toggleDetails"
        @open-camera="cameraDialogOpen = true"
        @set-theme="setPosTheme"
        @toggle-layout-editing="toggleCounterLayoutEditing"
      />

      <PosContextCards
        :warehouse="activeWarehouseLabel"
        :payment-mode="activeModeLabel"
        :cashier="hasCashierAccess ? activeCashierLabel : null"
        :scanner-state="scannerState"
      />

      <PosCashierWarning
        v-if="!hasCashierAccess"
        :can-open-cashiers="canOpenCashiersPage"
        :has-no-assigned-profile="hasNoAssignedProfile"
        @open-cashiers="openCashiers"
      />

    </div>

    <!--
      👉 TWO LAYOUTS, ONE PAGE.

      ⚠️ The branch is on the BODY ELEMENT, not inside it. Counter is not a five-track
      grid and must not receive `posBodyStyle` or the `clamp()`ed body class — those
      are what make the Classic column floors real, and applying them to a layout with
      no resizers would freeze its sidebar at a width nothing can change.

      ⚠️ Everything OUTSIDE this branch is shared: the head, the side panel, every
      dialog, the scan chip, and the whole of `<script setup>`. Extracting two shell
      components was considered and rejected — the page reaches into these children
      with template refs it uses for logic (`toolbarRef.focusSearch()`, and `bodyRef` /
      `checkoutRef` / `orderSummaryEl` are handed to `usePosColumnLayout` as raw DOM
      refs), and that composable feeds `PosHeader` in the SHARED head, so it cannot
      move into a shell either. The extraction would buy re-emit boilerplate and
      nothing else.

      ⚠️ Both branches keep `ref="bodyRef"`, so the layout composable keeps resolving.
    -->
    <div
      v-if="!isCounterTheme"
      ref="bodyRef"
      class="pos-terminal__body"
      :class="{ 'pos-terminal__body--details-hidden': !posDetailsVisible }"
      :style="posBodyStyle"
    >
      <section class="pos-terminal__catalog">
        <PosCategoryBar
          v-model="selectedItemGroup"
          :groups="catalogItemGroups"
          :loading="itemGroupsLoading"
        />

        <PosCatalogToolbar
          ref="toolbarRef"
          :search="searchQuery"
          :sort="catalogSort"
          :view="catalogView"
          :result-label="t('alkokh.pos.catalogCount', { count: catalogTotal })"
          @update:search="searchQuery = $event"
          @update:sort="catalogSort = $event"
          @update:view="catalogView = $event"
        />

        <!-- Items tagged "fav" in the desk, in front of everything, whatever page or category is on. -->
        <PosFavouritesStrip
          :warehouse="cashierStore.warehouse"
          :qty-in-cart="getItemQtyInCart"
          :is-sellable="isItemSellable"
          :add-disabled="!canCreatePosSaleAction || !hasCashierAccess"
          :image-url="resolveItemImage"
          :format-amount="formatAmount"
          :refresh-token="draftsRefreshToken + catalogRefreshToken"
          @add="addItemToCart($event)"
        />

        <PosProductGrid
          :items="catalog"
          :view="catalogView"
          :loading="catalogLoading"
          :qty-in-cart="getItemQtyInCart"
          :is-sellable="isItemSellable"
          :add-disabled="!canCreatePosSaleAction || !hasCashierAccess"
          :image-url="resolveItemImage"
          :format-amount="formatAmount"
          @add="addItemToCart($event)"
        />

        <footer class="pos-terminal__catalog-foot">
          <PosQuickActions
            :can-pet-transport="petTransportAvailable && !checkoutLoading && !counterLayoutEditing"
            :transport-count="petTransportCount"
            :can-return="returnsAvailable"
            :return-active="posReturn.active"
            @return-mode="toggleReturnMode"
            @pet-transport="petTransportOpen = true"
            @focus-search="focusCatalogSearch"
            @focus-customer="focusCustomerSearch"
            @focus-notes="focusNotes"
            @new-sale="startNewSale"
            @clear-sale="requestClearSale"
            @record-expense="expenseDialogOpen = true"
            @pick-partner="openPartnerPicker"
          />

          <div class="pos-terminal__pager">
            <span class="pos-terminal__pager-range">{{ catalogRangeLabel }}</span>

            <AppSelect
              class="pos-terminal__page-size"
              :model-value="catalogPagination.limitPageLength.value"
              :items="catalogPageSizeItems"
              item-title="title"
              item-value="value"
              density="compact"
              hide-details
              @update:model-value="updateCatalogPageSize"
            />

            <VBtn
              variant="outlined"
              color="secondary"
              size="x-small"
              icon
              :disabled="catalogPagination.limitStart.value <= 0 || catalogLoading"
              @click="goToPreviousCatalogPage"
            >
              <VIcon
                icon="tabler-chevron-left"
                size="16"
              />
            </VBtn>

            <span class="pos-terminal__page-number">{{ catalogPagination.currentPage }}</span>

            <VBtn
              variant="outlined"
              color="secondary"
              size="x-small"
              icon
              :disabled="!catalogPagination.hasMore.value || catalogLoading"
              @click="goToNextCatalogPage"
            >
              <VIcon
                icon="tabler-chevron-right"
                size="16"
              />
            </VBtn>
          </div>
        </footer>
      </section>

      <!--
        Seam 1, Catalog ↔ Details. Rendered only while the details column is on
        screen; when it is away the single remaining handle below sits between the
        catalogue and the cart instead.
      -->
      <PosColumnResizer
        v-if="posResizeEnabled && posDetailsVisible"
        :label="t('alkokh.pos.layout.resizeCatalogDetails')"
        :value="posDetailsInline"
        :min="posHandleRange('details').min"
        :max="posHandleRange('details').max"
        :rtl="posIsRtl"
        :active="posIsResizing"
        collapsible
        @resize-start="posBeginResize('details', $event)"
        @nudge="(step, coarse) => posNudgeHandle('details', step, coarse)"
        @jump="posJumpHandle('details', $event)"
        @reset="posResetHandle('details')"
        @toggle="posToggleDetails"
      />

      <aside
        v-if="posDetailsVisible"
        class="pos-terminal__details"
        :aria-label="t('alkokh.pos.sections.orderDetails')"
      >
        <PosDetailsColumn
          v-bind="detailsColumnProps"
          @delivery-order-created="handleDeliveryOrderCreated"
          @open-driver-order="openDriverOrderPanel"
          @open-driver-settlement="openDriverSettlementPanel"
          @pick-draft="requestLoadDraft"
          @detach-draft="unlinkOpenInvoice"
          @invoice-context-menu="openInvoiceMenu"
        />
      </aside>

      <!-- Seam 2, Details ↔ Cart — or Catalog ↔ Cart when details is away. -->
      <PosColumnResizer
        v-if="posResizeEnabled"
        :label="cartResizerLabel"
        :value="posCartInline"
        :min="posHandleRange('cart').min"
        :max="posHandleRange('cart').max"
        :rtl="posIsRtl"
        :active="posIsResizing"
        @resize-start="posBeginResize('cart', $event)"
        @nudge="(step, coarse) => posNudgeHandle('cart', step, coarse)"
        @jump="posJumpHandle('cart', $event)"
        @reset="posResetHandle('cart')"
      />

      <!--
        ⚠️ TWO PANES AND A SEAM, NOT ONE STACK. The summary used to be the only flexible
        child and shrank to nothing when the sections below it did not fit — its totals
        then painted straight through its box onto the customer card. Now the summary
        keeps a real floor and clips, the customer/payment sections sit in their own
        scroller with a floor of their own, the Process button is pinned outside both,
        and the seam between them lets the cashier choose the split. Auto until dragged.
      -->
      <aside
        ref="checkoutRef"
        class="pos-terminal__checkout"
        :class="{ 'pos-terminal__checkout--summary-sized': posSummarySized }"
      >
        <!-- Return mode turns this column into the return cart; the sale waits underneath. -->
        <PosReturnCart
          v-if="posReturn.active"
          ref="returnCartRef"
          :ret="posReturn"
          :payment-modes="paymentModeItems.map(item => item.value)"
          @add-out="focusCatalogSearch"
        />
        <template v-else>
        <PosOrderSummary
          ref="orderSummaryRef"
          v-bind="orderSummaryProps"
          @return="toggleReturnMode"
          @update-qty="updateCartQty"
          @remove="removeCartLine"
          @update:discount-type="form.discountType = $event"
          @update:discount-value="form.discountValue = $event"
          @open-discount-pad="openKeypad({ kind: 'discount' })"
          @open-qty-pad="index => openKeypad({ kind: 'qty', index })"
        >
          <template
            v-if="gamePrize.available.value"
            #prize
          >
            <PosGamePrizeSection
              v-bind="gamePrizeSectionProps"
              @apply="applyGamePrize"
              @remove="gamePrize.remove()"
              @open-list="sidePanel = { kind: 'prize' }"
            />
          </template>
        </PosOrderSummary>

        <!-- Seam 3, Summary ↕ Customer & payment. Down grows the summary; double-click returns to auto. -->
        <PosColumnResizer
          v-if="posResizeEnabled"
          orientation="horizontal"
          :label="t('alkokh.pos.layout.resizeSummary')"
          :value="posHandleRange('summary').value"
          :min="posHandleRange('summary').min"
          :max="posHandleRange('summary').max"
          :active="posIsResizing"
          @resize-start="posBeginResize('summary', $event)"
          @nudge="(step, coarse) => posNudgeHandle('summary', step, coarse)"
          @jump="posJumpHandle('summary', $event)"
          @reset="posResetHandle('summary')"
        />

        <div class="pos-terminal__checkout-forms">
        <PosCustomerSection
          ref="customerSectionRef"
          v-model="customerOption"
          :selected-label="selectedCustomerLabel"
          :can-create-customer="canCreateQuickCustomerAction"
          :attach-target="customerDialogAttachTarget"
          :balance="customerBalance"
          :balance-loading="balanceLoading"
          :can-record-payment="canRecordPaymentAction"
          :locked="isPartnerOrder && !billsAppCustomer(activePartner)"
          :format-amount="formatAmount"
          @create-customer="openQuickCustomerDialog"
          @receive-money="openPaymentDialog('Receive')"
          @pay-money="openPaymentDialog('Pay')"
        />

        <!--
          Sits above the open-invoice bar because the two are mutually exclusive
          and this is the one that changes who is billed for the whole sale.
        -->
        <PosTransporterBar
          v-if="transporterBar"
          v-bind="transporterBar"
          :disabled="checkoutLoading"
          :format-amount="formatAmount"
          @change="petTransportOpen = true"
          @remove="removeTransporter"
        />

        <PosPartnerOrderBar
          v-if="barPartner"
          :partner="barPartner"
          :order-ref="form.partnerOrderRef"
          :end-customer-name="form.partnerCustomerName"
          :total-amount="totalAmount"
          :commission-amount="partnerCommissionAmount"
          :net-amount="partnerNetAmount"
          :disabled="checkoutLoading"
          :format-amount="formatAmount"
          @update:order-ref="form.partnerOrderRef = $event"
          @update:end-customer-name="form.partnerCustomerName = $event"
          @change="openPartnerPicker"
          @detach="detachPartner"
        />

        <PosOpenInvoiceBar
          v-if="!activePartner"
          :invoices="settleableInvoices"
          :linked-invoice-ids="linkedInvoiceIds"
          :can-settle="canSettleInvoices"
          :can-settle-all="canSettleAllInvoices"
          :loading="linkLoading || settleableLoading"
          :format-amount="formatAmount"
          @link="linkOpenInvoice"
          @link-all="linkAllOpenInvoices"
          @unlink="unlinkOpenInvoice"
          @open="openLinkedInvoice"
          @context-menu="openInvoiceMenu"
        />

        <PosPaymentSection
          ref="paymentSectionRef"
          :payment-status="form.paymentStatus"
          :payment-mode="form.paymentMode"
          :paid-amount="form.paidAmount"
          :change-amount="changeAmount"
          :balance-due-amount="balanceDueAmount"
          :remarks="form.remarks"
          :is-due-payment="isDuePayment"
          :payment-status-items="paymentStatusItems"
          :payment-mode-items="paymentModeItems"
          :locked="Boolean(barPartner || transporterBar)"
          :locked-note="transporterBar ? t('alkokh.transporters.door.paymentLocked', { name: transporterName(transporterBar.transporter) })
            : barPartner ? t('alkokh.pos.partner.settledByPartner') : null"
          :format-amount="formatAmount"
          :credit-available="creditOffered"
          :credit="returnCredit"
          @open-credit="creditDialogOpen = true"
          @remove-credit="returnCredit = null"
          @update:payment-status="form.paymentStatus = $event"
          @update:payment-mode="form.paymentMode = $event"
          @update:paid-amount="handlePaidAmountChange"
          @update:remarks="form.remarks = $event"
          @open-paid-pad="openKeypad({ kind: 'paid' })"
        />

        <!--
          A refusal the cashier can act on — out of stock, or a till that forbids
          part payment. Held on screen beside the button rather than toasted: it
          names what to change, and a toast would be gone before it was read.
        -->
        <div
          v-if="checkoutBlockReason"
          class="pos-terminal__block"
        >
          <VIcon
            icon="tabler-alert-triangle"
            size="16"
          />
          <span>{{ checkoutBlockReason }}</span>
        </div>
        </div>

        <VBtn
          class="pos-terminal__process"
          color="primary"
          size="large"
          block
          prepend-icon="tabler-credit-card-pay"
          :loading="checkoutLoading"
          :disabled="!canCheckout"
          @click="checkout"
        >
          {{ t("alkokh.pos.actions.processTransaction") }}
          <span class="pos-terminal__process-hint">F9</span>
        </VBtn>
        </template>
      </aside>
    </div>

    <!--
      👉 THE COUNTER BODY.

      ⚠️ THE SAME FIVE-TRACK GRID AS CLASSIC, deliberately — col1 | handle | col2 |
      handle | col3. Only the CONTENT differs: a favourites grid and a lookup overlay
      instead of the product catalogue.

      That reuse is the whole point. The drag handles, the persisted widths, the
      clamps and `POS_COLUMN_LIMITS` all work here unchanged, and a cashier who
      resizes in one layout finds the same widths in the other. An earlier version
      invented its own two-track grid with no handles; it had to re-derive every one
      of those rules and got the first one wrong.

      👉 WHAT SITS IN EACH COLUMN IS THE CASHIER'S CHOICE. Every block is a named
      widget and every column is a `PosLayoutZone`; `usePosCounterLayout` holds the
      arrangement (design mode: the layout menu in the header). All widget bindings
      live ONCE, in `DefineCounterWidget` below — the columns only choose which ids
      to render.
    -->
    <div
      v-else
      ref="bodyRef"
      class="pos-terminal__body"
      :class="{ 'pos-terminal__body--details-hidden': !counterDetailsShown }"
      :style="posBodyStyle"
    >
      <!-- Renders nothing itself; `ReuseCounterWidget` stamps it into the columns. -->
      <DefineCounterWidget v-slot="{ id }">
        <!--
          The lookup TRIGGER, not the lookup. Scanning is still the fast path and
          needs no focus at all; this is for the item with no barcode to hand.
        -->
        <VBtn
          v-if="id === 'searchTrigger'"
          class="pos-terminal__search-trigger"
          variant="outlined"
          color="secondary"
          size="large"
          block
          prepend-icon="tabler-search"
          @click="openSearchOverlay()"
        >
          {{ t("alkokh.pos.search.open") }}
          <span class="pos-terminal__process-hint">F3</span>
        </VBtn>

        <!--
          ⚠️ `fill`, not the Classic rail. With no product grid in this layout the
          favourites ARE the item surface — as a one-line rail they sat above half a
          screen of empty grey.
        -->
        <PosFavouritesStrip
          v-else-if="id === 'favourites'"
          variant="fill"
          :warehouse="cashierStore.warehouse"
          :qty-in-cart="getItemQtyInCart"
          :is-sellable="isItemSellable"
          :add-disabled="!canCreatePosSaleAction || !hasCashierAccess"
          :image-url="resolveItemImage"
          :format-amount="formatAmount"
          :refresh-token="draftsRefreshToken + catalogRefreshToken"
          @add="addItemToCart($event)"
        />

        <footer
          v-else-if="id === 'quickActions'"
          class="pos-terminal__quick-foot"
        >
          <PosQuickActions
            :can-pet-transport="petTransportAvailable && !checkoutLoading && !counterLayoutEditing"
            :transport-count="petTransportCount"
            :can-return="returnsAvailable"
            :return-active="posReturn.active"
            @return-mode="toggleReturnMode"
            @pet-transport="petTransportOpen = true"
            @focus-search="focusCatalogSearch"
            @focus-customer="focusCustomerSearch"
            @focus-notes="focusNotes"
            @new-sale="startNewSale"
            @clear-sale="requestClearSale"
            @record-expense="expenseDialogOpen = true"
            @pick-partner="openPartnerPicker"
          />
        </footer>

        <!--
          The customer ON the till, not only behind PAY. Same bindings as the Classic
          card; both it and the checkout panel edit `customerOption`, so the panel
          opened by PAY shows whoever was picked here.

          The partner bar travels WITH the customer card: an attached partner decides
          who is billed (and whether the picker is locked at all), so seeing one
          without the other on the till reads as a contradiction.
        -->
        <div
          v-else-if="id === 'customer'"
          class="pos-terminal__counter-customer"
        >
          <PosCustomerSection
            ref="counterCustomerRef"
            v-model="customerOption"
            :selected-label="selectedCustomerLabel"
            :can-create-customer="canCreateQuickCustomerAction"
            :attach-target="customerDialogAttachTarget"
            :balance="customerBalance"
            :balance-loading="balanceLoading"
            :can-record-payment="canRecordPaymentAction"
            :locked="isPartnerOrder && !billsAppCustomer(activePartner)"
            :format-amount="formatAmount"
            @create-customer="openQuickCustomerDialog"
            @receive-money="openPaymentDialog('Receive')"
            @pay-money="openPaymentDialog('Pay')"
          />

          <PosTransporterBar
            v-if="transporterBar"
            v-bind="transporterBar"
            :disabled="checkoutLoading"
            :format-amount="formatAmount"
            @change="petTransportOpen = true"
            @remove="removeTransporter"
          />

          <PosPartnerOrderBar
            v-if="barPartner"
            :partner="barPartner"
            :order-ref="form.partnerOrderRef"
            :end-customer-name="form.partnerCustomerName"
            :total-amount="totalAmount"
            :commission-amount="partnerCommissionAmount"
            :net-amount="partnerNetAmount"
            :disabled="checkoutLoading"
            :format-amount="formatAmount"
            @update:order-ref="form.partnerOrderRef = $event"
            @update:end-customer-name="form.partnerCustomerName = $event"
            @change="openPartnerPicker"
            @detach="detachPartner"
          />
        </div>

        <!--
          ⚠️ TABS, NOT THE STACK. `PosDetailsColumn` renders all six cards one under
          another, which is a lot of column for context that sits BESIDE the sale.
          Here the four tab buttons stack vertically and one card shows at a time.
        -->
        <PosDetailTabs
          v-else-if="id === 'detailTabs'"
          v-bind="detailsColumnProps"
          orientation="vertical"
          @delivery-order-created="handleDeliveryOrderCreated"
          @open-driver-order="openDriverOrderPanel"
          @open-driver-settlement="openDriverSettlementPanel"
          @pick-draft="requestLoadDraft"
          @detach-draft="unlinkOpenInvoice"
          @invoice-context-menu="openInvoiceMenu"
        />

        <PosReturnCart
          v-else-if="id === 'orderSummary' && posReturn.active"
          ref="returnCartRef"
          :ret="posReturn"
          :payment-modes="paymentModeItems.map(item => item.value)"
          @add-out="focusCatalogSearch"
        />

        <PosOrderSummary
          v-else-if="id === 'orderSummary'"
          v-bind="orderSummaryProps"
          variant="counter"
          @return="toggleReturnMode"
          :pay-disabled="payDisabled"
          :pay-loading="checkoutLoading"
          :pay-block-reason="payDisabled ? null : checkoutBlockReason"
          @update-qty="updateCartQty"
          @remove="removeCartLine"
          @update:discount-type="form.discountType = $event"
          @update:discount-value="form.discountValue = $event"
          @open-discount-pad="openKeypad({ kind: 'discount' })"
          @open-qty-pad="index => openKeypad({ kind: 'qty', index })"
          @pay="openCheckoutPanel"
        >
          <template
            v-if="gamePrize.available.value"
            #prize
          >
            <PosGamePrizeSection
              v-bind="gamePrizeSectionProps"
              @apply="applyGamePrize"
              @remove="gamePrize.remove()"
              @open-list="sidePanel = { kind: 'prize' }"
            />
          </template>
        </PosOrderSummary>
      </DefineCounterWidget>

      <section class="pos-terminal__quick">
        <PosLayoutZone
          zone="col1"
          :ids="counterZones.col1"
          :rtl="posIsRtl"
        >
          <template #widget="{ id }">
            <ReuseCounterWidget :id="id" />
          </template>
        </PosLayoutZone>
      </section>

      <!-- Seam 1, col1 ↔ col2. The same handle Classic uses. -->
      <PosColumnResizer
        v-if="posResizeEnabled && counterDetailsShown"
        :label="t('alkokh.pos.layout.resizeCatalogDetails')"
        :value="posDetailsInline"
        :min="posHandleRange('details').min"
        :max="posHandleRange('details').max"
        :rtl="posIsRtl"
        :active="posIsResizing"
        collapsible
        @resize-start="posBeginResize('details', $event)"
        @nudge="(step, coarse) => posNudgeHandle('details', step, coarse)"
        @jump="posJumpHandle('details', $event)"
        @reset="posResetHandle('details')"
        @toggle="posToggleDetails"
      />

      <aside
        v-if="counterDetailsShown"
        class="pos-terminal__details"
        :aria-label="t('alkokh.pos.sections.orderDetails')"
      >
        <PosLayoutZone
          zone="col2"
          :ids="counterZones.col2"
          :rtl="posIsRtl"
        >
          <template #widget="{ id }">
            <ReuseCounterWidget :id="id" />
          </template>
        </PosLayoutZone>
      </aside>

      <!-- Seam 2, col2 ↔ col3. -->
      <PosColumnResizer
        v-if="posResizeEnabled"
        :label="counterDetailsShown
          ? t('alkokh.pos.layout.resizeDetailsCart')
          : t('alkokh.pos.layout.resizeCatalogCart')"
        :value="posCartInline"
        :min="posHandleRange('cart').min"
        :max="posHandleRange('cart').max"
        :rtl="posIsRtl"
        :active="posIsResizing"
        @resize-start="posBeginResize('cart', $event)"
        @nudge="(step, coarse) => posNudgeHandle('cart', step, coarse)"
        @jump="posJumpHandle('cart', $event)"
        @reset="posResetHandle('cart')"
      />

      <aside
        ref="checkoutRef"
        class="pos-terminal__counter-cart"
        :aria-label="t('alkokh.pos.sections.orderSummary')"
      >
        <PosLayoutZone
          zone="col3"
          :ids="counterCol3Ids"
          :rtl="posIsRtl"
        >
          <template #widget="{ id }">
            <ReuseCounterWidget :id="id" />
          </template>
        </PosLayoutZone>
      </aside>
    </div>

    <!--
      Design mode's toolbar: presets, hidden widgets, reset, done. Inside
      `.pos-terminal` like the side panel, so it survives fullscreen.
    -->
    <PosLayoutDesignerBar
      v-if="isCounterTheme && counterLayoutEditing"
      floating
      @done="setCounterLayoutEditing(false)"
    />

    <!--
      The one place a cashier "opens" something from the till. Inside `.pos-terminal`, so
      it survives fullscreen; the dialogs launched from within it are attached to the
      same element for the same reason. Closing it changes nothing about the sale.
    -->
    <PosSidePanel
      v-model="sidePanelOpen"
      :title="sidePanelTitle"
      :width="sidePanelOrder ? 520 : 460"
    >
      <DriverOrderDetailPanel
        v-if="sidePanelOrder"
        :order="sidePanelOrder"
        :pos-profile="cashierStore.activeProfileName ?? null"
        :attach="customerDialogAttachTarget"
        :allow-navigation="false"
        :can-record-results="canActOnDriverOrders"
        toast-results
        @close="sidePanel = null"
        @changed="handleDriverOrderChanged"
      />

      <div
        v-else-if="sidePanelDriver"
        class="pos-terminal__panel-body"
      >
        <DriverSettlementPanel
          ref="settlementPanelRef"
          :driver="sidePanelDriver"
          :pos-profile="cashierStore.activeProfileName ?? null"
        />

        <!-- Receiving stock and cash is a settlement action: the result role only. -->
        <div
          v-if="canActOnDriverOrders"
          class="pos-terminal__panel-actions"
        >
          <VBtn
            color="warning"
            variant="tonal"
            size="small"
            prepend-icon="tabler-package"
            @click="returnUnsoldOpen = true"
          >
            {{ t("alkokh.driverOrders.returns.unsold.action") }}
          </VBtn>
          <VBtn
            :color="settleDriverAvailable ? 'secondary' : 'success'"
            :variant="settleDriverAvailable ? 'tonal' : 'flat'"
            size="small"
            prepend-icon="tabler-cash"
            @click="handoverOpen = true"
          >
            {{ t("alkokh.driverOrders.money.handover.action") }}
          </VBtn>
          <VBtn
            v-if="settleDriverAvailable"
            color="primary"
            variant="flat"
            size="small"
            prepend-icon="tabler-scale"
            @click="settleDriverOpen = true"
          >
            {{ t("alkokh.driverSettle.actions.settle") }}
          </VBtn>
        </div>

        <RecordDriverSettlementDialog
          v-if="canActOnDriverOrders"
          v-model="settleDriverOpen"
          :driver="sidePanelDriver"
          :driver-label="sidePanelDriver"
          :pos-profile="cashierStore.activeProfileName ?? null"
          from-date="2000-01-01"
          :to-date="todayIsoDate()"
          :format-amount="formatAmount"
          :attach="customerDialogAttachTarget"
          @recorded="handleDriverSettled"
        />

        <DriverReturnUnsoldDialog
          v-model="returnUnsoldOpen"
          :driver="sidePanelDriver"
          :pos-profile="cashierStore.activeProfileName ?? null"
          :attach="customerDialogAttachTarget"
          @done="handleDriverSettlementDone('alkokh.driverOrders.returns.unsold.success')"
        />

        <DriverHandoverDialog
          v-model="handoverOpen"
          :driver="sidePanelDriver"
          :pos-profile="cashierStore.activeProfileName ?? null"
          :attach="customerDialogAttachTarget"
          @done="handleDriverSettlementDone('alkokh.driverOrders.money.handover.success')"
        />
      </div>

      <PosInvoicePeekPanel
        v-else-if="sidePanelInvoiceId"
        :invoice-id="sidePanelInvoiceId"
        :preloaded="sidePanelInvoicePreloaded"
        :format-amount="formatAmount"
      />

      <PosPartnerPickerPanel
        v-else-if="sidePanelPartner"
        :partners="pickerPartnerOptions"
        :loading="partnersLoading"
        :failed="partnersFailed"
        :active-partner-id="activePartner?.name ?? null"
        :format-amount="formatAmount"
        @pick="attachPartner"
        @close="sidePanel = null"
      />

      <PosGamePrizeListPanel
        v-else-if="sidePanelPrize"
        :claims="gamePrize.offeredPrizes.value"
        :loading="gamePrize.listLoading.value"
        :applying="gamePrize.applying.value"
        :disabled-reason="gamePrize.disabledReason.value"
        :error="gamePrize.error.value"
        :format-amount="formatAmount"
        @apply="applyGamePrize"
      />

      <!--
        The same details cards the wide layout shows as a column — this is their ONLY
        route below 1280px. Picking a draft or opening an invoice from here closes the
        sheet first, because both of those change what the cart behind it is showing.
      -->
      <div
        v-else-if="sidePanelDetails"
        class="pos-terminal__panel-details"
      >
        <PosDetailsColumn
          v-bind="detailsColumnProps"
          @delivery-order-created="handleDeliveryOrderCreated"
          @open-driver-order="openDriverOrderPanel"
          @open-driver-settlement="openDriverSettlementPanel"
          @pick-draft="(invoice) => { sidePanel = null; requestLoadDraft(invoice); }"
          @detach-draft="unlinkOpenInvoice"
          @invoice-context-menu="openInvoiceMenu"
        />
      </div>

      <!--
        ⚠️ LAST IN THE CHAIN, and a `v-else-if` like every other kind — not a `v-if`
        spliced into the middle, which would cut the chain in two.
      -->
      <PosCheckoutPanel
        v-else-if="sidePanelCheckout"
        ref="checkoutPanelRef"
        v-bind="checkoutPanelProps"
        @update:customer-option="customerOption = $event"
        @update:partner-order-ref="form.partnerOrderRef = $event"
        @update:partner-customer-name="form.partnerCustomerName = $event"
        @update:payment-status="form.paymentStatus = $event"
        @update:payment-mode="form.paymentMode = $event"
        @update:paid-amount="handlePaidAmountChange"
        @update:remarks="form.remarks = $event"
        @create-customer="openQuickCustomerDialog"
        @receive-money="openPaymentDialog('Receive')"
        @pay-money="openPaymentDialog('Pay')"
        @change-partner="openPartnerPicker"
        @detach-partner="detachPartner"
        @change-transporter="petTransportOpen = true"
        @remove-transporter="removeTransporter"
        @link="linkOpenInvoice"
        @link-all="linkAllOpenInvoices"
        @unlink="unlinkOpenInvoice"
        @open-invoice="openLinkedInvoice"
        @invoice-context-menu="openInvoiceMenu"
        @open-paid-pad="openKeypad({ kind: 'paid' })"
        @open-credit="creditDialogOpen = true"
        @remove-credit="returnCredit = null"
        @checkout="checkout"
      />
    </PosSidePanel>

    <!-- Loading a draft clears scanned lines; that is the one thing worth a confirm. -->
    <VDialog
      v-model="replaceDraftOpen"
      max-width="440"
      :attach="customerDialogAttachTarget"
    >
      <VCard rounded="xl">
        <VCardItem :title="t('alkokh.pos.openDrafts.replaceTitle')" />
        <VCardText>
          {{ t("alkokh.pos.openDrafts.replaceText", {
            count: cart.length,
            invoice: pendingDraft?.id ?? "",
            customer: pendingDraft?.customerName || pendingDraft?.customer || "",
          }) }}
        </VCardText>
        <VCardActions class="px-6 pb-6">
          <VSpacer />
          <VBtn
            variant="text"
            color="secondary"
            @click="replaceDraftOpen = false"
          >
            {{ t("alkokh.global.cancel") }}
          </VBtn>
          <VBtn
            color="primary"
            prepend-icon="tabler-file-invoice"
            @click="confirmReplaceDraft"
          >
            {{ t("alkokh.pos.openDrafts.replaceConfirm") }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!--
      Closing a parked sale that still has lines. Same treatment the replace-draft
      confirm above gets, and for the same reason: scanned lines are the only thing
      in this page that cannot be got back.
    -->
    <VDialog
      :model-value="pendingSlotClose !== null"
      max-width="440"
      :attach="customerDialogAttachTarget"
      @update:model-value="value => { if (!value) pendingSlotClose = null; }"
    >
      <VCard rounded="xl">
        <VCardItem :title="t('alkokh.pos.saleTabs.closeConfirm')" />
        <VCardText>
          {{ t("alkokh.pos.saleTabs.closeConfirmText", {
            sale: saleTabs.find(tab => tab.id === pendingSlotClose)?.label ?? "",
            count: saleTabs.find(tab => tab.id === pendingSlotClose)?.lineCount ?? 0,
          }) }}
        </VCardText>
        <VCardActions class="px-6 pb-6">
          <VSpacer />
          <VBtn
            variant="text"
            color="secondary"
            @click="pendingSlotClose = null"
          >
            {{ t("alkokh.global.cancel") }}
          </VBtn>
          <VBtn
            color="error"
            prepend-icon="tabler-trash"
            @click="void closeSaleSlot(pendingSlotClose!, { confirmed: true })"
          >
            {{ t("alkokh.pos.saleTabs.closeConfirmAction") }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!--
      ⚠️ BOTH ARE PLAIN CHILDREN OF `.pos-terminal`, not dialogs.

      Vuetify overlays default to `#alkokh-overlay-target`, which the LAYOUT renders —
      outside this element and therefore invisible in fullscreen. Every dialog below
      overrides that with `:attach="customerDialogAttachTarget"` for the same reason;
      these two sidestep the question by being children in the first place.

      They are also stacked deliberately: search (7) under the side panel (8) under the
      keypad (9), because the keypad edits the cash amount that lives inside the panel.
    -->
    <PosItemSearchOverlay
      :open="searchOverlayOpen"
      :warehouse="cashierStore.warehouse"
      :groups="catalogItemGroups"
      :qty-in-cart="getItemQtyInCart"
      :is-sellable="isItemSellable"
      :add-disabled="!canCreatePosSaleAction || !hasCashierAccess"
      :image-url="resolveItemImage"
      :format-amount="formatAmount"
      :seed="searchOverlaySeed"
      @add="addFromSearch"
      @close="searchOverlayOpen = false"
    />

    <PosKeypad
      :open="keypadOpen"
      :model-value="keypadValue"
      :mode="keypadMode"
      :max="keypadMax"
      :title="keypadTitle"
      :hint="keypadHint"
      :format-amount="formatAmount"
      @confirm="confirmKeypad"
      @cancel="keypadTarget = null"
    />

    <Transition name="pos-scan-fade">
      <div
        v-if="scanFeedback"
        class="pos-scan-chip"
        :class="`pos-scan-chip--${scanFeedback.tone}`"
      >
        <VIcon
          :icon="scanFeedback.tone === 'error' ? 'tabler-alert-circle' : 'tabler-circle-check'"
          size="16"
        />
        {{ scanFeedback.text }}
      </div>
    </Transition>

    <!--
      Reuses the accounting dialog wholesale: it already stamps the active POS
      profile on the entry and restricts modes of payment to the cashier's own
      allowed list, so there is no POS-specific payment logic to write.
      `attach` keeps it visible when the terminal is in fullscreen.

      ⚠️ `startAsAdvancePayment` IS DELIBERATELY NOT PASSED ANY MORE. It opened the
      dialog on the on-account path, which creates a Payment Entry with no
      reference rows — the money was received but never applied to the customer's
      invoices, so الديون did not move and the cashier was left staring at a debt
      the customer had just paid. Without it the dialog opens on the reference
      path, where the cashier picks which open invoice this settles, ERPNext
      allocates it, and the debt actually drops. The advance toggle is still there
      for a genuine on-account payment.
    -->
    <CreatePaymentEntryDialog
      v-model="paymentDialogOpen"
      :fixed-payment-type="paymentDialogType"
      initial-party-type="Customer"
      :initial-party="customerOption?.id ?? null"
      :initial-party-label="selectedCustomerLabel"
      :initial-amount="paymentDialogAmount"
      :initial-cash-account="cashierStore.cashAccount"
      :auto-resolve-accounts="true"
      :attach="customerDialogAttachTarget"
      @created="handlePaymentCreated"
    />

    <!--
      Right-click on any invoice row, from any of the three lists that show one.
      `isLinked` decides whether "load into sale" is still meaningful for this row.
    -->
    <PosInvoiceContextMenu
      v-model="invoiceMenu.open.value"
      :invoice="invoiceMenu.invoice.value"
      :position="invoiceMenu.position.value"
      :can-add-payment="canAddInvoicePayment"
      :is-linked="Boolean(invoiceMenu.invoice.value && linkedInvoiceIds.includes(invoiceMenu.invoice.value.id))"
      :can-attach-partner="canCreatePosSaleAction"
      :can-return="returnsAvailable"
      @add-payment="openInvoicePayment"
      @view="openLinkedInvoice($event.id)"
      @load="requestLoadDraft"
      @copy-id="copyInvoiceId"
      @attach-partner="openAttachPartner"
      @return-items="enterReturnMode($event.id)"
    />

    <!--
      A Due sale that turned out to be an app order. Same right as ringing up a sale;
      `attach` keeps it visible when the terminal is fullscreen.
    -->
    <AttachInvoiceToPartnerDialog
      v-model="attachPartnerOpen"
      :invoice="attachPartnerTarget"
      :attach="customerDialogAttachTarget"
      @attached="handleInvoiceAttachedToPartner"
      @stale="handleInvoiceAttachedToPartner({ invoice: '' })"
    />

    <!--
      Payment against ONE invoice, reusing the accounting dialog wholesale — it already
      stamps the till's POS profile and cash account and narrows the modes of payment to
      the cashier's allowed list, and it defaults the amount to `outstandingAmount`.

      ⚠️ THIS IS THE REFERENCED PATH, which is the whole point. It allocates the money to
      the named invoice, so the debt actually drops — unlike an on-account entry, which
      takes the cash and leaves الديون untouched (see the warning above).
    -->
    <AddSalesInvoicePaymentDialog
      v-model="invoicePaymentOpen"
      :invoice="invoicePaymentTarget"
      :attach="customerDialogAttachTarget"
      @created="handleInvoicePaymentCreated"
    />

    <!--
      The camera is just another producer for the scan queue the wedge already
      feeds — same lookup, same duplicate merge, same stock rules, same feedback.
      Nothing about adding an item is reimplemented for it.
    -->
    <PosCameraScanDialog
      v-model="cameraDialogOpen"
      :feedback="scanFeedback"
      :cart="cart"
      :cart-count="cartCount"
      :format-amount="formatAmount"
      @decoded="enqueueScan"
    />

    <PosExpenseDialog
      v-model="expenseDialogOpen"
      :can-record-cash="canRecordCashExpense"
      :can-record-item="canRecordItemExpense"
      :attach="customerDialogAttachTarget"
    />

    <VDialog
      v-model="customerDialogOpen"
      max-width="520"
      :fullscreen="isMobileShell"
      scrollable
      :attach="customerDialogAttachTarget"
    >
      <VCard rounded="xl">
        <VCardItem :title="t('alkokh.pos.quickCustomer.title')" />
        <VDivider />
        <VCardText>
          <VRow>
            <VCol cols="12">
              <AppTextField
                v-model="quickCustomerForm.fullName"
                autofocus
                prepend-inner-icon="tabler-id"
                :label="t('alkokh.pos.quickCustomer.customerName')"
                :rules="[(value: unknown) => !!String(value ?? '').trim() || t('alkokh.validations.required')]"
              />
            </VCol>

            <VCol cols="12">
              <AppTextField
                :model-value="quickCustomerForm.phone"
                prepend-inner-icon="tabler-phone"
                inputmode="numeric"
                :maxlength="GUARDIAN_PHONE_LENGTH"
                :counter="GUARDIAN_PHONE_LENGTH"
                :rules="[guardianPhoneRule]"
                :label="t('alkokh.pos.quickCustomer.mobileNo')"
                @update:model-value="onQuickCustomerPhoneUpdate"
              />
            </VCol>

            <VCol cols="12">
              <GuardianTerritoryField
                v-model:territory="quickCustomerForm.territory"
                v-model:address="quickCustomerForm.addressLine1"
              />
            </VCol>
            <VCol cols="12">
              <AppTextField
                v-model="quickCustomerForm.addressLine1"
                prepend-inner-icon="tabler-map-pin"
                :label="t('alkokh.pos.quickCustomer.address')"
              />
            </VCol>
          </VRow>
        </VCardText>
        <VDivider />
        <VCardActions class="px-6 pb-6">
          <VSpacer />
          <VBtn
            variant="text"
            color="secondary"
            @click="customerDialogOpen = false"
          >
            {{ t("alkokh.global.cancel") }}
          </VBtn>
          <VBtn
            color="primary"
            prepend-icon="tabler-device-floppy"
            :loading="customerCreateLoading"
            :disabled="customerCreateLoading || !canCreateQuickCustomerAction || !quickCustomerFormValid"
            @click="createQuickCustomer"
          >
            {{ t("alkokh.pos.quickCustomer.create") }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>

<style scoped>
.pos-terminal {
  --pos-border: rgba(var(--v-theme-on-surface), 0.09);
  --pos-border-strong: rgba(var(--v-theme-on-surface), 0.16);
  --pos-muted: rgba(var(--v-theme-on-surface), 0.6);
  --pos-soft: rgba(var(--v-theme-on-surface), 0.035);
  --pos-primary-soft: rgba(var(--v-theme-primary), 0.09);
  --pos-surface: rgb(var(--v-theme-surface));
  --pos-ink: rgb(var(--v-theme-on-surface));
  --pos-radius: 12px;

  /*
    👉 THE ONE TYPE KNOB FOR THE WHOLE TERMINAL.

    Every `font-size` under `.pos-terminal` is written as
    `calc(var(--pos-font-scale, 1) * Xrem)`, so this single number resizes the till.
    Counter raises it because the staff reading this screen cannot see the small one.

    ⚠️ MULTIPLY, DO NOT SNAP TO STEPS. There are ~18 distinct sizes in here (0.6 →
    1.4rem). Rounding them onto a tidy step scale would change how Classic renders,
    and Classic must stay pixel-identical — at scale 1 the used value is provably the
    same number it was before the token existed.

    ⚠️ Raising this moves the CART COLUMN FLOORS. `POS_COLUMN_LIMITS` in
    `usePosColumnLayout.ts` is a px budget justified against literal grid tracks at
    1×, and it is a frozen JS constant that cannot react to a CSS variable. That is
    safe only because Counter renders no resizer and does not use the `clamp()`d body
    class, so those floors are unreachable there. Do NOT scale this in Classic.
  */
  --pos-font-scale: 1;

  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  background: rgb(var(--v-theme-background));
}

/*
  👉 The Counter layout.

  Type only, at this stage — the layout branch lands separately. Flipping the theme
  here should render THE SAME SCREEN, BIGGER. Anything that stays small is a
  component whose `font-size` was missed by the token sweep.
*/
.pos-terminal--counter {
  --pos-font-scale: 1.18;
}


/*
  ⚠️ `min-block-size: 0` on both columns. A grid item defaults to `min-height: auto`,
  which refuses to shrink below its content — without it a long favourites list
  pushes the frame taller than the viewport and the overflow escapes to the document,
  which is the one thing this page must never do.
*/
.pos-terminal__quick,
.pos-terminal__counter-cart {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-block-size: 0;
  min-inline-size: 0;
  /*
    ⚠️ A backstop, not decoration. An earlier version of this layout declared its own
    grid template with the same specificity as the Classic one, lost on source order,
    and dropped this column into the 10px DRAG-HANDLE track — where its prices
    rendered across the middle of the screen UNCLIPPED, so the breakage read as a bad
    design rather than a bug. Anything that overflows now gets cut, not scattered.
  */
  overflow: hidden;
}

.pos-terminal__quick-head {
  flex: none;
}

/* The till's customer widget: the customer card, then the partner bar when attached. */
.pos-terminal__counter-customer {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-inline-size: 0;
}

/* Deliberately large: this is the replacement for a whole product grid. */
.pos-terminal__search-trigger {
  justify-content: flex-start;
}

.pos-terminal__quick-foot {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}

/*
  The sidebar IS the summary. `PosOrderSummary` is `flex: 1 1 auto` with its own
  internal scroller for the line list and a `flex: none` foot, so the Total and the
  PAY button sit outside that scroller and can never be scrolled away from.

  ⚠️ That structure REPLACES `POS_COLUMN_LIMITS.summary.min`, which was a 270px floor
  guarding the same thing by arithmetic. A structural guarantee beats a pixel budget,
  and unlike the budget it does not need re-deriving when the type scale changes.
*/
.pos-terminal__counter-cart {
  border: 1px solid var(--pos-border);
  border-radius: var(--pos-radius);
  background: var(--pos-surface);
  padding: 8px;
}



/*
  👉 Desktop terminal frame.

  ⚠️ `min-block-size: 0` on every link in this chain is load-bearing. A flex item
  defaults to `min-height: auto`, which refuses to shrink below its content — so
  without it the product grid pushes the frame taller than the viewport and the
  overflow escapes to the document, which is exactly the behaviour being removed.
*/
.pos-terminal--fixed {
  /*
    ⚠️ No `block-size: 100%` here either. Height arrives from the unscoped block
    at the end of this file, which makes `.page-zoom-surface` a flex column and
    this element a `flex: 1 1 auto` item in it. A percentage would not resolve,
    and specifying one would suppress the flex sizing that does work.
  */
  overflow: hidden;
}

.pos-terminal__head {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: 8px;
}

/*
  👉 THE ONE PLACE COLUMN GEOMETRY LIVES.

  Three panels, two drag handles, five tracks. The catalogue is the only flexible
  track — both handles steal from it and give back to it, which is why neither handle
  can push a fixed column past its own clamp.

  ⚠️ `--pos-details-user` / `--pos-cart-user` are written as INLINE STYLES by
  `usePosColumnLayout`. The declarations here are the pre-hydration defaults only: an
  inline custom property outranks any stylesheet rule, so the media query at the end
  of this file supplies the first-paint value and JS supplies the cashier's. Neither
  fights the other.

  ⚠️ The `clamp()`s are a second, independent floor. JS already clamps on read, but a
  corrupt localStorage entry must not be able to produce a 40px cart even for a frame.

  ⚠️ `minmax(0, 1fr)` on the catalogue, NOT `minmax(360px, 1fr)`. A track minimum does
  not shrink — it OVERFLOWS, and `.pos-terminal--fixed .pos-terminal__body` is
  `overflow: hidden`, so the cart would be silently clipped off the edge. The 360px
  catalogue floor is enforced in JS, where running out of room hides the details
  column instead of amputating the checkout panel.

  ⚠️ `gap: 8px 0` — row gap only, where this used to be `gap: 8px` on both axes. The
  horizontal gutter is now the handle's own 10px track, so the seam is a real element
  in reading order, which is what `role="separator"` and tab order want. The 8px row
  gap keeps the ≤959px stacked layout's spacing byte-identical to before.
*/
.pos-terminal__body {
  --pos-handle-track: 10px;
  --pos-details-inline: clamp(260px, var(--pos-details-user, 320px), 720px);
  --pos-cart-inline: clamp(358px, var(--pos-cart-user, 400px), 900px);

  display: grid;
  gap: 8px 0;
  grid-template-columns:
    minmax(0, 1fr)
    var(--pos-handle-track)
    var(--pos-details-inline)
    var(--pos-handle-track)
    var(--pos-cart-inline);
}

/*
  Details away — collapsed by the cashier, below 1280px, or simply out of room.

  A second explicit template rather than `--pos-details-inline: 0px`, which would
  leave the column in the DOM and in the accessibility tree with its content
  overflowing a zero-width track.
*/
.pos-terminal__body--details-hidden {
  grid-template-columns:
    minmax(0, 1fr)
    var(--pos-handle-track)
    var(--pos-cart-inline);
}

.pos-terminal--fixed .pos-terminal__body {
  flex: 1 1 auto;
  min-block-size: 0;
  overflow: hidden;
}


/*
  👉 THE CATALOG COLUMN IS A QUERY CONTAINER, and that is load-bearing since the
  three-column refactor.

  ⚠️ Everything inside this column used to go compact on VIEWPORT media queries. That
  stopped being meaningful the moment the column became independently resizable: on a
  1920px screen the catalogue can sit at 360px while every `@media (max-width: 1279px)`
  still thinks it has 1920px of room, so the toolbar and the shortcut row rendered at
  full width and were silently clipped by `.pos-terminal__body { overflow: hidden }`.
  The children now ask about THIS element instead, via `@container pos-catalog`.

  ⚠️ `inline-size`, NOT `size`. `container-type: size` contains BOTH axes, which makes
  the block size compute as if the element had no contents — that would destroy its
  participation in the grid row and take the whole height chain with it. `inline-size`
  contains the inline axis only, and this column's width already comes from the
  `minmax(0, 1fr)` track rather than from its contents, so the containment is a no-op
  for the element itself and `min-block-size: 0` below still does its job.

  ⚠️ The container is NAMED so a future nested container cannot capture these queries.

  Safe because: no `position: fixed` descendant exists in this subtree (layout
  containment would become its containing block), and the category menu and pager
  dropdown are Vuetify overlays that teleport to `<body>`, so containment cannot clip
  them.
*/
.pos-terminal__catalog {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  border: 1px solid var(--pos-border);
  border-radius: var(--pos-radius);
  background: var(--pos-surface);
  container-name: pos-catalog;
  container-type: inline-size;
  min-inline-size: 0;
}

.pos-terminal--fixed .pos-terminal__catalog {
  min-block-size: 0;
}

/*
  👉 The footer WRAPS, at every width. It used to wrap only below 959px, so in a narrow
  catalogue column the rigid pager (`flex: none`, ~262px) stayed on the one line and the
  whole deficit was charged to the shortcut strip beside it — squeezed to ~66px of five
  buttons. With wrap, the strip takes the first line whole and the pager drops
  underneath, pushed to the inline-end by its own `margin-inline-start: auto`. On a wide
  column nothing wraps and the row looks exactly as before. The product grid above is the
  column's flex scroller, so the extra line costs it one row, not the frame any height.
*/
.pos-terminal__catalog-foot {
  display: flex;
  flex: none;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 10px;
  padding-block-start: 8px;
  border-block-start: 1px solid var(--pos-border);
}

.pos-terminal__pager {
  display: flex;
  flex: none;
  align-items: center;
  gap: 6px;
  margin-inline-start: auto;
}

.pos-terminal__pager-range {
  color: var(--pos-muted);
  font-size: calc(var(--pos-font-scale, 1) * 0.7rem);
  white-space: nowrap;
}

/*
  The "1–24 of 96" range is the pager's one informational part; the page number and
  the arrows carry the same fact. Dropping it under 780px of catalogue content width
  shortens the pager from ~262px to ~170px, which is what keeps the strip and the pager
  on one line for most of the column's resizable range.
*/
@container pos-catalog (max-inline-size: 780px) {
  .pos-terminal__pager-range {
    display: none;
  }
}

.pos-terminal__page-size {
  inline-size: 78px;
}

.pos-terminal__page-number {
  min-inline-size: 22px;
  font-size: calc(var(--pos-font-scale, 1) * 0.78rem);
  font-weight: 700;
  text-align: center;
}

.pos-terminal__details {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-inline-size: 0;
}

/*
  ⚠️ Both of these are load-bearing, and for the same reasons as their twins on
  `.pos-terminal__catalog` and `.pos-terminal__checkout`. `min-inline-size: 0` stops
  long, unwrappable invoice ids inflating the track past its own `clamp()`.
  `min-block-size: 0` restores the shrink a grid item's `auto` minimum refuses —
  without it the card stack pushes the frame taller than the viewport and the overflow
  escapes to the document, which is the exact behaviour this frame exists to prevent.
*/
.pos-terminal--fixed .pos-terminal__details {
  min-block-size: 0;
}

.pos-terminal__checkout {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-inline-size: 0;
}

.pos-terminal--fixed .pos-terminal__checkout {
  min-block-size: 0;
}

/*
  👉 THE CART COLUMN'S TWO PANES.

  ⚠️ `.pos-summary` is the order summary's ROOT (a child component's root takes the
  parent's scope, so this rule reaches it). It used to be `flex: 1 1 auto` with
  `min-block-size: 0`, and a flex item that may shrink to zero WILL, the moment the
  fixed sections below it outgrow the column — and since it clipped nothing, its totals
  painted through onto the customer card. Two changes, both load-bearing:

  - a floor equal to its own fixed parts (header, column labels, the line-list floor,
    the totals, the discount control) so the Total row can never be cut off, and
    `overflow: hidden` so that if the column is ever shorter than every floor together,
    what gives is clipped inside its own box rather than drawn over the next card;
  - the sections below it live in `.pos-terminal__checkout-forms`, a scroller with its
    own floor, so THEY give way — by scrolling — and the Process button stays pinned.

  In auto the summary still absorbs the slack (`flex: 1 1 0`, basis zero so it never
  competes with the forms' content for shrink). Once the cashier has dragged the seam,
  `--pos-summary-user` is inline on the body and the summary becomes a fixed-basis pane
  that can only shrink when the window does; the forms then take the slack instead.
*/
.pos-terminal--fixed .pos-terminal__checkout > .pos-summary {
  overflow: hidden;
  flex: 1 1 0;
  min-block-size: 270px;
}

.pos-terminal--fixed .pos-terminal__checkout--summary-sized > .pos-summary {
  flex: 0 1 var(--pos-summary-user, 320px);
}

.pos-terminal__checkout-forms {
  display: flex;
  overflow: hidden auto;
  flex: 0 1 auto;
  flex-direction: column;
  gap: 8px;
  min-block-size: 0;
  overscroll-behavior: contain;
}

.pos-terminal--fixed .pos-terminal__checkout-forms {
  min-block-size: 150px;
}

.pos-terminal--fixed .pos-terminal__checkout--summary-sized > .pos-terminal__checkout-forms {
  flex: 1 1 auto;
}

/*
  Drag feedback belongs on the terminal, not the handle: pointer capture keeps the
  gesture alive while the cursor is far away over the catalogue or the cart, and the
  cursor must not flicker back to a text caret on the way.
*/
.pos-terminal--resizing {
  cursor: col-resize;
  user-select: none;
}

.pos-terminal__block {
  display: flex;
  flex: none;
  align-items: flex-start;
  gap: 6px;
  padding: 7px 10px;
  border: 1px solid rgba(var(--v-theme-warning), 0.4);
  border-radius: 10px;
  background: rgba(var(--v-theme-warning), 0.09);
  color: rgb(var(--v-theme-warning));
  font-size: calc(var(--pos-font-scale, 1) * 0.74rem);
  line-height: 1.35;
}

.pos-terminal__process {
  flex: none;
  block-size: 46px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.pos-terminal__process-hint {
  padding: 0 5px;
  border: 1px solid rgba(var(--v-theme-on-primary), 0.4);
  border-radius: 4px;
  font-size: calc(var(--pos-font-scale, 1) * 0.62rem);
  font-weight: 600;
  margin-inline-start: 8px;
}

/* The settlement panel brings no gutters of its own; the sheet gives it some. */
.pos-terminal__panel-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-block: 12px;
  padding-inline: 14px;
}

/* Receive stock / receive cash, under the figures they act on. */
.pos-terminal__panel-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.pos-terminal__panel-actions .v-btn {
  flex: 1 1 140px;
  text-transform: none;
}

/*
  👉 The details cards inside the side sheet (narrow screens only).

  ⚠️ `min-block-size: 0` IS LOAD-BEARING. `PosDetailsColumn`'s own scroller is
  `flex: 1 1 auto` inside a column flex parent, and a flex item defaults to
  `min-height: auto` — it refuses to shrink below its content. Without this the card
  stack grows past the sheet and the last cards are laid out below the fold, clipped by
  the sheet's `overflow: hidden`. Same trap as `.layout-page-content`, documented below.
*/
.pos-terminal__panel-details {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-block-size: 0;
  padding-block: 10px;
  padding-inline: 12px;
}

/* 👉 Scan feedback: deliberately not a toast. See `reportScan` for why. */
.pos-scan-chip {
  position: absolute;
  z-index: 6;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 999px;
  box-shadow: 0 6px 20px rgba(var(--v-theme-on-surface), 0.16);
  font-size: calc(var(--pos-font-scale, 1) * 0.78rem);
  font-weight: 600;
  inset-block-end: 16px;
  inset-inline-start: 50%;
  pointer-events: none;
  transform: translateX(-50%);
}

.pos-scan-chip--ok {
  background: rgb(var(--v-theme-success));
  color: rgb(var(--v-theme-on-success));
}

.pos-scan-chip--duplicate {
  background: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-on-primary));
}

.pos-scan-chip--error {
  background: rgb(var(--v-theme-error));
  color: rgb(var(--v-theme-on-error));
}

.pos-scan-fade-enter-active,
.pos-scan-fade-leave-active {
  transition: opacity 0.18s ease;
}

.pos-scan-fade-enter-from,
.pos-scan-fade-leave-to {
  opacity: 0;
}

/*
  👉 Compact desktop. The details column is gated off above this width by
  `detailsAvailable`, so all this has to do is supply the cart's first-paint width
  before the composable's inline style lands. An inline custom property outranks this
  rule, so the cashier's own value always wins once JS has run.

  ⚠️ NOT a `grid-template-columns` override any more. A fixed template here would beat
  the variable-driven one above and freeze the cart, resizer or not.

  ⚠️ Tracks `POS_COLUMN_LIMITS.cart.min`. It was 320px against a 66px qty track; the
  stepper now carries a typed field and a keypad button, so both moved to 358.
*/
@media (max-width: 1279px) {
  .pos-terminal__body {
    --pos-cart-user: 358px;
  }
}

/*
  👉 Phone shell (below the app's 960px mobile-shell gate). The fixed frame is
  dropped entirely here and the page returns to normal document flow, so the
  existing mobile behaviour is untouched.
*/
@media (max-width: 959px) {
  .pos-terminal__body,
  .pos-terminal__body--details-hidden {
    grid-template-columns: minmax(0, 1fr);
  }

  /*
    👉 THE CART GOES FIRST WHEN THE COLUMNS STACK, IN BOTH LAYOUTS.

    ⚠️ `.pos-terminal__counter-cart` is load-bearing here, not symmetry. Counter's cart
    is a DIFFERENT element from Classic's `.pos-terminal__checkout`, so it was never
    reordered — on a phone it stacked LAST, below a full-height favourites grid and the
    details tabs, which is exactly the "PAY is at the end of the screen" report. Both
    layouts now put the totals and the pay control at the top of the stack, where the
    cashier lands without scrolling.
  */
  .pos-terminal__checkout,
  .pos-terminal__counter-cart {
    order: -1;
  }

  /* Belt to `resizeEnabled`'s braces: the handles are not rendered below `md` at all. */
  .pos-resizer {
    display: none;
  }
}
</style>

<style>
/*
  ⚠️ UNSCOPED ON PURPOSE, AND INERT WITHOUT THE BODY CLASS.

  These rules cannot live in the scoped block: every selector below belongs to
  `@layouts` or `layouts/default.vue`, above this component. They are keyed on
  `body.pos-terminal-shell`, which `applyTerminalBodyClass` adds on mount and
  removes on unmount, so no other page can ever see them.

  ⚠️ `layout-content-height-fixed` ALONE DOES NOT WORK, AND THE REASON IS SUBTLE.
  It pins `.layout-content-wrapper` to 100dvh and hides overflow on
  `.layout-page-content` — but `.layout-page-content` is a `flex-grow: 1` item in
  that column and the template never gives it `min-block-size: 0`. A flex item
  defaults to `min-height: auto`, which REFUSES to shrink below its content, so
  the tall product grid pushed it past the viewport; the totals, customer,
  payment and Process Transaction were laid out below the fold and silently
  clipped by that `overflow: hidden`. Restoring the shrink is the whole fix.

  ⚠️ `WorkspaceTabHost` renders the routed page with NO wrapper element, so
  `.pos-terminal` really is a direct child of `.page-zoom-surface` — a sibling of
  the authorization banner and the tab strip, which keep their natural height and
  simply leave the terminal less room.
*/

/* The navbar must not absorb the shrink that `.layout-page-content` now allows. */
body.pos-terminal-shell .layout-navbar {
  flex: 0 0 auto;
}

body.pos-terminal-shell .layout-page-content {
  flex: 1 1 auto;
  min-block-size: 0;

  /* The template's 1.5rem is a page gutter; a terminal spends it on products. */
  padding-block: 0.5rem;
}

/*
  👉 Full width on POS only.

  The app-wide "boxed" content width caps at 1440px (`$layout-boxed-content-width`),
  which on a 1920 screen leaves the catalogue squeezed between two empty gutters.
  A cashier terminal wants every pixel; this does NOT change the setting for any
  other page.
*/
body.pos-terminal-shell .layout-content-width-boxed .layout-page-content,
body.pos-terminal-shell .layout-content-width-boxed .layout-navbar,
body.pos-terminal-shell .layout-content-width-boxed .navbar-content-container {
  max-inline-size: none;
}

/*
  ⚠️ NOT ONE PERCENTAGE HEIGHT BELOW THIS POINT — THEY DO NOT RESOLVE HERE.

  `block-size: 100%` on these two nodes was measured resolving to `auto`, so both
  grew to their content (3373px inside a 676px parent) and the checkout panel's
  lower half was laid out below the fold. `.page-content-container` is a flex item
  of `.layout-page-content`, whose own height comes from the flex algorithm, and a
  percentage against that is not reliably definite. Worse, specifying one gives
  the item a definite cross size, which SUPPRESSES the `align-items: stretch` that
  would have sized it correctly for free.

  So: no percentages, no `max-block-size: 100%` (the template's own rule on
  `.page-zoom-surface` fails for the same reason and is overridden below). Height
  is handed down by `stretch` once, then by `flex: 1 1 auto` + `min-block-size: 0`
  at every step.
*/
body.pos-terminal-shell .layout-page-content .page-content-container {
  display: flex;
  flex-direction: column;
  align-self: stretch;
  block-size: auto;
  min-block-size: 0;
  overflow: hidden;
}

body.pos-terminal-shell .page-zoom-surface {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  block-size: auto;
  max-block-size: none;
  min-block-size: 0;
  overflow: hidden;
}

body.pos-terminal-shell .page-zoom-surface > .pos-terminal {
  flex: 1 1 auto;
  min-block-size: 0;
}

/*
  Phone / tablet: the terminal's three overlays pin to the VIEWPORT, not to the terminal.

  WARNING: THE TERMINAL IS ONLY VIEWPORT-HEIGHT ABOVE 960px (`.pos-terminal--fixed`).
  Below that it returns to document flow and grows to its content - a catalogue's worth
  of height - so `PosSidePanel`'s `position: absolute; inset: 0` anchors to that whole
  scroll length: the scrim covers pixels far below the fold and the sheet opens with its
  top off screen. Against the viewport it is always where the cashier is looking.

  WARNING: `PosItemSearchOverlay` (z 7) AND `PosKeypad` (z 9) NEED THE SAME TREATMENT, and
  for a worse reason than the sheet did. Both are `position: absolute; inset: 0` children
  of `.pos-terminal` too, and the pad centres its sheet with `align-items: center` - i.e.
  the middle of a document-flow column that is a favourites grid tall. On a phone that is
  hundreds of pixels below the fold, so the qty pad opened "nowhere". In Counter the search
  overlay IS the catalogue, so the same bug costs the till its only way to add an item.
  Each keeps its own z-index; only the containing block changes.

  WARNING: THE PHONE TAB BAR PAINTS OVER ALL THREE, AND THAT IS WHY `inset-block-end` IS
  HERE. `.mobile-bottom-nav` is the last row of the shell frame at z-index 1006 (see
  `MobileBottomNav`), and these overlays sit at 7-9 inside `.pos-terminal`, which establishes no stacking
  context of its own - so the bar wins and covers the sheet's last ~3.5rem. That is
  precisely where `PosCheckoutPanel` PINS PROCESS TRANSACTION: `__foot` is `flex: none`
  outside the forms scroller, by design, so no amount of scrolling can lift the button
  clear of the bar. Ending the overlays above the bar keeps both reachable; raising their
  z-index instead would just bury the navigation under a POS sheet.

  WARNING: THE INSET IS A `max()` OF TWO MUTUALLY EXCLUSIVE CASES, not a sum, because the
  bar and the software keyboard are never both in the way:

  - keyboard closed - `--app-keyboard-open` is 0, the bar is on screen, and the overlay
    ends above it. The bar's own height INCLUDES `env(safe-area-inset-bottom)`, so this
    reserve has to as well or the home indicator eats the difference.
  - keyboard open on iOS - `useVisualViewport` reports a real `--app-keyboard-inset` and
    sets the flag, the tab bar leaves the frame (`html.app-keyboard-open`), and the overlay reclaims the
    bar's strip and ends above the KEYBOARD instead, so a typed paid amount and the button
    below it stay visible. Multiplying by `(1 - flag)` is how the bar's term drops out;
    CSS cannot branch on a variable.
  - keyboard open on Android - Chrome shrinks the layout viewport, so the inset measures
    ~0, the flag stays 0 and the bar stays visible. The first case is already correct.

  Both vars default to 0, so a browser without `visualViewport` gets today's geometry.

  WARNING: `:not(:fullscreen)` IS NOT OPTIONAL. The zoom control is NOT gated by width, so
  a phone can put `.pos-terminal` into fullscreen too - and a `position: fixed` descendant
  of a fullscreen element is clipped to that element instead of the viewport. Inside
  fullscreen the terminal is viewport-height anyway, so the component's own `absolute` is
  already right and this rule must stand down. Dropping the guard is how the sheet
  vanishes in fullscreen, the exact failure `PosSidePanel`'s header warns about.

  WARNING: UNSCOPED, AND IT HAS TO BE. `.pos-side-panel` and friends belong to the child components
  while `.pos-terminal` belongs to this one; scoped CSS stamps its data attribute on the
  LAST selector only, so this ancestor pairing cannot be expressed in either scoped block.
  Keyed on `.pos-terminal`, which no other page renders. NOT on `body.pos-terminal-shell`
  - that class is only applied when `isTerminalFixed`, i.e. exactly the widths this rule
  does not apply to.
*/
@media (max-width: 959px) {
  .pos-terminal:not(:fullscreen) .pos-side-panel,
  .pos-terminal:not(:fullscreen) .pos-item-search,
  .pos-terminal:not(:fullscreen) .pos-keypad {
    position: fixed;

    /* Above the phone tab bar, or above the keyboard while it is up. See the block above. */
    inset-block-end: max(
      var(--app-keyboard-inset, 0px),
      calc(
        (1 - var(--app-keyboard-open, 0)) *
        (var(--mobile-tabbar-height, 3.5rem) + env(safe-area-inset-bottom, 0px))
      )
    );
  }

  /*
    ⚠️ THE ONE CONSEQUENCE OF PINNING THE SEARCH OVERLAY, AND IT HAS TO BE PAID HERE.

    `PosItemSearchOverlay` caps itself at `RESULT_LIMIT = 8` rows and carries NO internal
    scroll on purpose - the desktop frame allows the terminal exactly three block-axis
    scrollers and all three are spoken for. Against the whole document that was harmless:
    an eighth row past the fold came back with a page scroll. Against the viewport it is
    clipped instead, so eight rows plus the field and the chips must fit a ~600px phone.

    The cap is confined to this media query, where `.pos-terminal--fixed` does not exist,
    the overlay is already its own fixed layer and the page-scroll bug that budget guards
    cannot be reintroduced. `__results` takes the shrink because a flex item refuses to go
    below its content without `min-block-size: 0`; the field and the chips stay put.
  */
  .pos-terminal:not(:fullscreen) .pos-item-search__sheet {
    max-block-size: calc(100% - 24px);
  }

  .pos-terminal:not(:fullscreen) .pos-item-search__results {
    overflow: hidden auto;
    min-block-size: 0;
    overscroll-behavior: contain;
  }
}
</style>
